// src/index.ts
import { initContract as initContract12 } from "@ts-rest/core";

// src/auth.ts
import { initContract } from "@ts-rest/core";
import { z as z2 } from "zod";

// src/schemas.ts
import { z } from "zod";
var UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string().nullable(),
  /**
   * Whether this account may turn on the debug view (`architecture/0029`). Set
   * by the platform's admin in the backoffice, never by the user. A client reads
   * it to decide whether to draw the switch; the debug routes read the row
   * again on every request, so a stale copy of this opens nothing.
   */
  canDebug: z.boolean()
});
var AdminUserStatusSchema = z.enum(["active", "disabled"]);
var AuthTokensSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  tokenType: z.literal("Bearer"),
  /** Seconds until the access token expires. */
  expiresIn: z.number().int().positive()
});
var RegisterInputSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(128),
  name: z.string().min(1).max(120).optional()
});
var LoginInputSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1)
});
var RefreshInputSchema = z.object({
  refreshToken: z.string().min(1)
});
var AuthResponseSchema = z.object({
  user: UserSchema,
  tokens: AuthTokensSchema
});
var TokensResponseSchema = z.object({ tokens: AuthTokensSchema });
var MeResponseSchema = z.object({ user: UserSchema });
var IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected a YYYY-MM-DD date").refine((value) => {
  const parsed = /* @__PURE__ */ new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}, "Not a real calendar date");
var ErrorSchema = z.object({
  statusCode: z.number(),
  message: z.union([z.string(), z.array(z.string())]),
  error: z.string().optional()
});
function issueText(issue) {
  const message = issue.message?.trim() || "is not valid";
  const path = (issue.path ?? []).reduce((acc, segment) => {
    if (typeof segment === "number") return `${acc}[${segment}]`;
    return acc ? `${acc}.${segment}` : String(segment);
  }, "");
  return path ? `${path}: ${message}` : message;
}
function errorMessage(res) {
  const body = res.body;
  if (!body || typeof body !== "object") return `Request failed (${res.status})`;
  if ("message" in body && body.message) {
    return Array.isArray(body.message) ? body.message.join(", ") : String(body.message);
  }
  const issues = [body.bodyResult, body.paramsResult, body.queryResult, body.headersResult].flatMap((bucket) => bucket?.issues ?? []).map(issueText).filter(Boolean);
  if (issues.length > 0) return issues.join("; ");
  return `Request failed (${res.status})`;
}
function isAuthCall(args) {
  return args.path.includes("/auth/");
}

// src/auth.ts
var c = initContract();
var authContract = c.router({
  register: {
    method: "POST",
    path: "/auth/register",
    body: RegisterInputSchema,
    responses: {
      201: AuthResponseSchema,
      400: ErrorSchema,
      // The email domain maps to no workspace, or that workspace has
      // self-registration disabled. Both return the *same* message, so this
      // cannot be used to discover which companies are onboarded.
      403: ErrorSchema,
      409: ErrorSchema
    },
    summary: "Create an account in the workspace owning the email's domain"
  },
  login: {
    method: "POST",
    path: "/auth/login",
    body: LoginInputSchema,
    responses: {
      200: AuthResponseSchema,
      // An unknown email domain, a suspended workspace and a wrong password all
      // return the same 401, so this cannot be used to enumerate workspaces.
      401: ErrorSchema
    },
    summary: "Authenticate with email + password against the email's workspace"
  },
  refresh: {
    method: "POST",
    path: "/auth/refresh",
    body: RefreshInputSchema,
    responses: {
      200: TokensResponseSchema,
      401: ErrorSchema
    },
    summary: "Exchange a refresh token for a new token pair"
  },
  logout: {
    method: "POST",
    path: "/auth/logout",
    body: RefreshInputSchema,
    responses: {
      200: z2.object({ success: z2.literal(true) }),
      401: ErrorSchema
    },
    summary: "Revoke a refresh token"
  },
  me: {
    method: "GET",
    path: "/auth/me",
    headers: z2.object({ authorization: z2.string() }),
    responses: {
      200: MeResponseSchema,
      401: ErrorSchema
    },
    summary: "Return the currently authenticated user"
  }
});

// src/health.ts
import { initContract as initContract2 } from "@ts-rest/core";
import { z as z3 } from "zod";
var c2 = initContract2();
var healthContract = c2.router({
  health: {
    method: "GET",
    path: "/health",
    responses: {
      200: z3.object({
        status: z3.literal("ok"),
        uptime: z3.number()
      })
    },
    summary: "Liveness probe"
  }
});

// src/brain.ts
import { initContract as initContract4 } from "@ts-rest/core";
import { z as z7 } from "zod";

// src/document-categories.ts
import { z as z4 } from "zod";
var DocumentCategoryKeySchema = z4.string().regex(/^[a-z][a-z0-9-]{0,39}$/, "must be lower-case letters, digits and hyphens");
var DOCUMENT_CATEGORY_SHAPE = {
  key: DocumentCategoryKeySchema,
  /** What a person sees on the shelf. */
  name: z4.string().trim().min(1).max(80),
  /** What belongs in it, in words a model and a person can both use. */
  description: z4.string().trim().min(1).max(400)
};
var DocumentCategorySchema = z4.object(DOCUMENT_CATEGORY_SHAPE);
var MAX_DOCUMENT_CATEGORIES = 40;
function duplicateCategoryKeys(list) {
  const seen = /* @__PURE__ */ new Set();
  const twice = /* @__PURE__ */ new Set();
  for (const { key } of list) (seen.has(key) ? twice : seen).add(key);
  return [...twice];
}
var DEFAULT_DOCUMENT_CATEGORIES = [
  {
    key: "contract",
    name: "Contract with the client",
    description: "The client's contract or order to this company, its annexes and variations, and the notices and letters about them."
  },
  {
    key: "budget",
    name: "Budget and invoicing",
    description: "Budgets, offers, payment applications and invoices."
  },
  {
    key: "subcontractors",
    name: "Subcontractors and suppliers",
    description: "The subcontracts and purchase orders this company places, supplier quotes, delivery notes and correspondence with them."
  },
  {
    key: "permits",
    name: "Permits and approvals",
    description: "The building permit, the grid operator's approval and other consents from authorities."
  },
  {
    key: "site-survey",
    name: "Site survey and ground",
    description: "Topographical surveys, geotechnical reports, pull-out tests and ground conditions."
  },
  {
    key: "design",
    name: "Design and site layout",
    description: "The site layout, general arrangement and design drawings."
  },
  {
    key: "civils",
    name: "Civil works and site set-up",
    description: "Site clearance, earthworks, access roads, drainage, fencing and gates, cable trenches, and the site compound and its welfare facilities."
  },
  {
    key: "ramming",
    name: "Ramming and setting out",
    description: "Where each pile goes, its type and length, the surveyor's staking, and the ramming records."
  },
  {
    key: "structure",
    name: "Mounting structure",
    description: "The racking or tracker structure: its drawings, assembly and torque values."
  },
  {
    key: "electrical",
    name: "Electrical, earthing and cabling",
    description: "DC and AC design, strings, combiner boxes, inverters, cable schedules, cabling and earthing."
  },
  {
    key: "grid",
    name: "Grid connection",
    description: "The grid operator, the substation and the connection works."
  },
  {
    key: "equipment",
    name: "Equipment and products",
    description: "Modules, inverters, trackers and other equipment: datasheets, manuals and warranties."
  },
  {
    key: "quality",
    name: "Quality control",
    description: "Inspection and test plans, checklists, non-conformance reports and quality records."
  },
  {
    key: "health-safety",
    name: "Health and safety",
    description: "The health and safety plan, RAMS, method statements and risk assessments."
  },
  {
    key: "crew",
    name: "Crew and qualifications",
    description: "Who works on the site: rosters and site lists, each person's qualifications and certificates, the certification register, and training and induction records."
  },
  {
    key: "programme",
    name: "Programme and progress",
    description: "The programme, look-aheads, the site diary and progress reports."
  },
  {
    key: "commissioning",
    name: "Commissioning and handover",
    description: "Test reports, commissioning records, as-built documents and the handover."
  }
];

// src/projects.ts
import { initContract as initContract3 } from "@ts-rest/core";
import { z as z6 } from "zod";

// src/phase-outline.ts
function asOutline(rows) {
  let previous = -1;
  return rows.map((row) => {
    const level = Math.max(0, Math.min(Math.trunc(row.level), previous + 1));
    previous = level;
    return level === row.level ? row : { ...row, level };
  });
}
function outlineRefusal(rows) {
  let previous = -1;
  for (const row of rows) {
    if (row.level > previous + 1) {
      return previous < 0 ? `\u201C${row.name}\u201D is the first phase, so it cannot be a sub-phase.` : `\u201C${row.name}\u201D is more than one level below the phase above it.`;
    }
    previous = row.level;
  }
  return null;
}
function subtreeEnd(rows, index) {
  const level = rows[index].level;
  let end = index + 1;
  while (end < rows.length && rows[end].level > level) end += 1;
  return end;
}
function hasChildren(rows, index) {
  return subtreeEnd(rows, index) > index + 1;
}
function siblingBlock(rows, index, direction) {
  const level = rows[index].level;
  if (direction === 1) {
    const start = subtreeEnd(rows, index);
    if (start >= rows.length || rows[start].level !== level) return null;
    return { start, end: subtreeEnd(rows, start) };
  }
  for (let start = index - 1; start >= 0; start -= 1) {
    const other = rows[start].level;
    if (other < level) return null;
    if (other === level) return { start, end: index };
  }
  return null;
}
function canMove(rows, index, direction) {
  return siblingBlock(rows, index, direction) !== null;
}
function moveRow(rows, index, direction) {
  const sibling = siblingBlock(rows, index, direction);
  if (!sibling) return [...rows];
  const own = { start: index, end: subtreeEnd(rows, index) };
  const [first, second] = direction === -1 ? [sibling, own] : [own, sibling];
  return [
    ...rows.slice(0, first.start),
    ...rows.slice(second.start, second.end),
    ...rows.slice(first.start, first.end),
    ...rows.slice(second.end)
  ];
}
function canIndent(rows, index) {
  return index > 0 && rows[index].level <= rows[index - 1].level;
}
function canOutdent(rows, index) {
  return rows[index].level > 0;
}
function shiftRow(rows, index, by) {
  if (by === 1 ? !canIndent(rows, index) : !canOutdent(rows, index)) return [...rows];
  const end = subtreeEnd(rows, index);
  return rows.map((row, i) => i >= index && i < end ? { ...row, level: row.level + by } : row);
}
function removeRow(rows, index) {
  const end = subtreeEnd(rows, index);
  return rows.flatMap((row, i) => {
    if (i === index) return [];
    if (i > index && i < end) return [{ ...row, level: row.level - 1 }];
    return [row];
  });
}
function outlineNumbers(rows) {
  const counters = [];
  return rows.map(({ level }) => {
    counters.length = level + 1;
    counters[level] = (counters[level] ?? 0) + 1;
    for (let i = 0; i < level; i += 1) counters[i] ??= 1;
    return counters.join(".");
  });
}
function subPhaseSpan(rows, index) {
  let start = null;
  let end = null;
  for (let i = index + 1; i < subtreeEnd(rows, index); i += 1) {
    const row = rows[i];
    const first = row.startDate || row.endDate || null;
    const last = row.endDate || row.startDate || null;
    if (first && (!start || first < start)) start = first;
    if (last && (!end || last > end)) end = last;
  }
  return start || end ? { start, end } : null;
}

// src/uploads.ts
import { z as z5 } from "zod";
var MAX_UPLOAD_BYTES = 100 * 1024 * 1024;
var MAX_FILENAME_LENGTH = 255;
var MAX_DRIVE_PICK = 50;
var MAX_DICTATION_BYTES = 14 * 1024 * 1024;
var MAX_DICTATION_SECONDS = 10 * 60;
var ALLOWED_UPLOAD_TYPES = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  // Excel on Windows types a `.csv` as its own.
  "application/vnd.ms-excel": [".xls", ".csv"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "image/vnd.dwg": [".dwg"],
  "image/vnd.dxf": [".dxf"],
  // Browsers routinely fail to type these at all. The upload check never sees
  // the bytes anyway; the router's sniffer is the real control.
  "application/octet-stream": [".dwg", ".dxf", ".msg", ".eml", ".url", ".zip", ".md", ".csv"],
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  // Common for screenshots, and every browser renders it. Added when journal
  // photographs became first-class content.
  "image/webp": [".webp"],
  "text/plain": [".txt", ".md", ".csv"],
  // Added 2026-10-03 (the owner). A `.md` is read as plain text, a `.csv` as
  // a one-sheet spreadsheet.
  "text/markdown": [".md"],
  "text/x-markdown": [".md"],
  "text/csv": [".csv"],
  // Seen in real project exports (plans/project-files-build.md, baseline):
  // macro-enabled workbooks, Outlook emails, archives, saved web pages and
  // Windows shortcuts. Each has a reader, or an honest "not read" with a
  // reason (`document-ai/0023` §3). None of them is ever served inline.
  "application/vnd.ms-excel.sheet.macroenabled.12": [".xlsm"],
  "application/vnd.ms-outlook": [".msg"],
  // An email in MIME, as Outlook sync stores it and mail programs save it
  // (added 2026-10-16, plans/outlook-sync.md phase 2).
  "message/rfc822": [".eml"],
  "application/zip": [".zip"],
  "application/x-zip-compressed": [".zip"],
  "text/html": [".html", ".htm"]
};
var ALLOWED_UPLOAD_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".dwg",
  ".dxf",
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ".txt",
  ".md",
  ".csv",
  ".xlsm",
  ".msg",
  ".eml",
  ".zip",
  ".html",
  ".htm",
  ".url"
];
function wholeMb(bytes) {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}
var UPLOAD_REFUSALS = {
  systemFile: "That is a system file, not a document, so it is not stored.",
  empty: "That file is empty.",
  tooLarge: `That file is larger than the ${wholeMb(MAX_UPLOAD_BYTES)} limit.`,
  noName: "That file has no name.",
  nameTooLong: `That file\u2019s name is longer than ${MAX_FILENAME_LENGTH} characters. Rename it and try again.`,
  tooManyPicked: `Pick up to ${MAX_DRIVE_PICK} files at a time.`
};
var DICTATION_TOO_LONG = `This recording is too long for a note \u2014 a dictation is at most ${MAX_DICTATION_SECONDS / 60} minutes (${Math.floor(MAX_DICTATION_BYTES / (1024 * 1024))} MB). Trim it on the phone and send again; the full recording stays on the device.`;
var UNFINISHED_UPLOAD_REASON = "The upload didn\u2019t finish.";
var SYSTEM_FILES = /* @__PURE__ */ new Set([".ds_store"]);
function baseName(filename) {
  return filename.split(/[/\\]/).pop() ?? "";
}
function isSystemFile(filename) {
  return SYSTEM_FILES.has(baseName(filename).toLowerCase());
}
var MAX_SOURCE_PATH_CHARS = 1024;
var MAX_SOURCE_PATH_SEGMENT_CHARS = 120;
var PATH_GAP = "\u2026";
function normaliseSourcePath(path, filename) {
  if (!path) return null;
  const segments = path.normalize("NFC").replace(/[\u0000-\u001f\u007f]+/g, " ").split(/[/\\]/).map((segment) => segment.replace(/\s+/g, " ").trim()).filter((segment) => segment.length > 0 && segment !== "." && segment !== "..").map(
    (segment) => segment.length <= MAX_SOURCE_PATH_SEGMENT_CHARS ? segment : segment.slice(0, MAX_SOURCE_PATH_SEGMENT_CHARS).trimEnd()
  );
  const name = filename?.normalize("NFC").trim();
  if (name && segments.length > 0 && segments[segments.length - 1] === name) segments.pop();
  if (segments.length === 0) return null;
  return fitPath(segments);
}
function fitPath(segments) {
  const whole = segments.join("/");
  if (whole.length <= MAX_SOURCE_PATH_CHARS) return whole;
  const first = segments[0];
  const tail = [];
  let length = first.length + 1 + PATH_GAP.length;
  for (let i = segments.length - 1; i > 0; i -= 1) {
    const next = segments[i].length + 1;
    if (length + next > MAX_SOURCE_PATH_CHARS && tail.length > 0) break;
    tail.unshift(segments[i]);
    length += next;
  }
  return [first, PATH_GAP, ...tail].join("/");
}
var EARLIEST_FILE_TIME = Date.UTC(1980, 0, 1);
var FILE_TIME_AHEAD_MS = 24 * 60 * 60 * 1e3;
function normaliseFileTime(value, now = /* @__PURE__ */ new Date()) {
  if (value === null || value === void 0 || value === "") return null;
  const at = typeof value === "number" ? new Date(value) : new Date(value.trim());
  const ms = at.getTime();
  if (!Number.isFinite(ms)) return null;
  if (ms < EARLIEST_FILE_TIME || ms > now.getTime() + FILE_TIME_AHEAD_MS) return null;
  return new Date(Math.floor(ms / 1e3) * 1e3).toISOString().replace(".000Z", "Z");
}
function extensionOf(filename) {
  const base = baseName(filename);
  const dot = base.lastIndexOf(".");
  return dot > 0 ? base.slice(dot).toLowerCase() : "";
}
function refusal(kind) {
  return { kind, reason: UPLOAD_REFUSALS[kind] };
}
function fileRefusal(file) {
  const name = file.filename.trim();
  if (isSystemFile(name)) return refusal("systemFile");
  if (file.sizeBytes !== null && file.sizeBytes <= 0) return refusal("empty");
  if (file.sizeBytes !== null && file.sizeBytes > MAX_UPLOAD_BYTES) return refusal("tooLarge");
  if (name.length === 0) return refusal("noName");
  if (name.length > MAX_FILENAME_LENGTH) return refusal("nameTooLong");
  return null;
}
function documentUploadRefusal(file) {
  const refused = fileRefusal(file);
  if (refused) return refused;
  const mime = file.mimeType.split(";")[0]?.trim().toLowerCase() ?? "";
  const extension = extensionOf(file.filename);
  const allowedExtensions = ALLOWED_UPLOAD_TYPES[mime];
  if (!allowedExtensions) {
    return { kind: "type", reason: acceptedTypes() };
  }
  if (!allowedExtensions.includes(extension)) {
    return {
      kind: "type",
      reason: `\u201C${extension || "that file"}\u201D doesn\u2019t match the file type ${mime}. ${acceptedTypes()}`
    };
  }
  return null;
}
function acceptedTypes() {
  return `We accept ${ALLOWED_UPLOAD_EXTENSIONS.join(", ")}.`;
}
function refuse(ctx, reason) {
  ctx.addIssue({ code: z5.ZodIssueCode.custom, message: reason, path: [] });
}

// src/projects.ts
var c3 = initContract3();
function durationInWeeks(startDate, endDate) {
  const days = daysBetween(startDate, endDate);
  return days === null ? null : Math.round(days / 7);
}
function daysBetween(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const start = Date.parse(`${startDate}T00:00:00Z`);
  const end = Date.parse(`${endDate}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return Math.round((end - start) / 864e5);
}
function progressToHandover(startDate, handoverDate, today) {
  const total = daysBetween(startDate, handoverDate);
  const elapsed = daysBetween(startDate, today);
  if (total === null || elapsed === null || total <= 0) return null;
  return Math.round(Math.min(100, Math.max(0, elapsed / total * 100)));
}
var NOT_COUNTRIES = /* @__PURE__ */ new Set([
  "EU",
  "EZ",
  "UN",
  "QO",
  "XA",
  "XB",
  "ZZ",
  "AC",
  "CP",
  "CQ",
  "DG",
  "EA",
  "IC",
  "TA"
]);
var REGION_NAMES = (() => {
  try {
    return new Intl.DisplayNames(["en-GB"], { type: "region", fallback: "none" });
  } catch {
    return null;
  }
})();
function canonicalRegion(code) {
  try {
    return new Intl.Locale("und", { region: code }).region ?? code;
  } catch {
    return code;
  }
}
function isCountryCode(code) {
  if (!/^[A-Z]{2}$/.test(code) || NOT_COUNTRIES.has(code)) return false;
  if (!REGION_NAMES) return true;
  return REGION_NAMES.of(code) !== void 0 && canonicalRegion(code) === code;
}
function countryCodeOf(value) {
  const code = value?.trim().toUpperCase();
  if (!code || !/^[A-Z]{2}$/.test(code)) return null;
  const current = REGION_NAMES ? canonicalRegion(code) : code;
  return isCountryCode(current) ? current : null;
}
var CountryCodeSchema = z6.string().refine(isCountryCode, "Must be the ISO 3166-1 alpha-2 code of a country, such as IT, FI or GB");
var countryList = null;
function countryCodes() {
  if (countryList) return countryList;
  const codes = [];
  if (REGION_NAMES) {
    for (let first = 65; first <= 90; first += 1) {
      for (let second = 65; second <= 90; second += 1) {
        const code = String.fromCharCode(first, second);
        if (isCountryCode(code)) codes.push(code);
      }
    }
  }
  countryList = codes.sort((a, b) => countryLabel(a).localeCompare(countryLabel(b), "en-GB"));
  return countryList;
}
function countryLabel(code) {
  if (!code) return null;
  return REGION_NAMES?.of(code) ?? code;
}
var ProjectStatusSchema = z6.enum(["setup-incomplete", "active", "archived"]);
var PROJECT_STATUS_LABELS = {
  "setup-incomplete": "In setup",
  active: "Active",
  archived: "Archived"
};
var SETUP_STEP_COUNT = 4;
var SetupStepSchema = z6.number().int().min(1).max(SETUP_STEP_COUNT);
var ProjectSchema = z6.object({
  id: z6.string().uuid(),
  name: z6.string(),
  /**
   * Null between "a file was chosen in step 1" and "someone picked a country".
   * Never null on an `active` project — `ProjectsService#activate` refuses one.
   */
  country: CountryCodeSchema.nullable(),
  region: z6.string().nullable(),
  spec: z6.string().nullable(),
  startDate: IsoDateSchema.nullable(),
  targetHandoverDate: IsoDateSchema.nullable(),
  status: ProjectStatusSchema,
  setupStep: SetupStepSchema,
  setupCompletedAt: z6.string().nullable(),
  createdAt: z6.string(),
  updatedAt: z6.string()
});
var ProjectBasicsFields = z6.object({
  name: z6.string().trim().min(1, "Project name is required").max(160),
  country: CountryCodeSchema.optional(),
  region: z6.string().trim().max(120).optional(),
  spec: z6.string().trim().max(160).optional(),
  startDate: IsoDateSchema.optional(),
  targetHandoverDate: IsoDateSchema.optional()
});
function refineDateOrder(value, ctx) {
  const { startDate, targetHandoverDate } = value;
  if (!startDate || !targetHandoverDate) return;
  if (targetHandoverDate <= startDate) {
    ctx.addIssue({
      code: z6.ZodIssueCode.custom,
      path: ["targetHandoverDate"],
      message: "Target handover must be after the project start date"
    });
  }
}
var ProjectBasicsSchema = ProjectBasicsFields.superRefine(refineDateOrder);
var ProjectBasicsPatchSchema = ProjectBasicsFields.partial().superRefine(refineDateOrder).refine((value) => Object.keys(value).length > 0, "No fields to update");
var ProjectResponseSchema = z6.object({ project: ProjectSchema });
var ProjectsResponseSchema = z6.object({ projects: z6.array(ProjectSchema) });
var PhaseOriginSchema = z6.enum(["manual", "parsed"]);
var PhaseLevelSchema = z6.number().int().nonnegative();
var PhaseSchema = z6.object({
  id: z6.string().uuid(),
  projectId: z6.string().uuid(),
  position: z6.number().int().positive(),
  level: PhaseLevelSchema,
  name: z6.string(),
  startDate: IsoDateSchema.nullable(),
  endDate: IsoDateSchema.nullable(),
  note: z6.string().nullable(),
  sourceDocumentId: z6.string().uuid().nullable(),
  origin: PhaseOriginSchema,
  editedByUser: z6.boolean()
});
var PhaseInputSchema = z6.object({
  id: z6.string().uuid().optional(),
  /**
   * 0 when a request leaves it out. Required in `PhaseInput`, the type our
   * own callers build, so a caller that copies a phase without its level is
   * a type error rather than a save that puts every sub-phase on one level.
   */
  level: PhaseLevelSchema.default(0),
  // No length bound on a name or a note (owner, 2026-10-03: "no limits ...
  // we just parse the plan"). They were 160 and 2,000 characters, and a
  // longer cell cost the phase or its tail with nothing said.
  name: z6.string().trim().min(1, "Phase name is required"),
  startDate: IsoDateSchema.nullish(),
  endDate: IsoDateSchema.nullish(),
  note: z6.string().trim().nullish(),
  sourceDocumentId: z6.string().uuid().nullish(),
  origin: PhaseOriginSchema.optional(),
  editedByUser: z6.boolean().optional()
}).superRefine((value, ctx) => {
  if (value.startDate && value.endDate && value.endDate < value.startDate) {
    ctx.addIssue({
      code: z6.ZodIssueCode.custom,
      path: ["endDate"],
      message: "Phase end date cannot be before its start date"
    });
  }
});
var PhasesResponseSchema = z6.object({ phases: z6.array(PhaseSchema) });
var PhasesInputSchema = z6.object({ phases: z6.array(PhaseInputSchema) }).superRefine((value, ctx) => {
  const refusal2 = outlineRefusal(value.phases);
  if (refusal2) ctx.addIssue({ code: z6.ZodIssueCode.custom, path: [], message: refusal2 });
});
function documentDisplayName(filename) {
  const dot = filename.lastIndexOf(".");
  const looksLikeExtension = dot > 0 && dot > filename.length - 7 && dot < filename.length - 1;
  return looksLikeExtension ? filename.slice(0, dot) : filename;
}
var DocumentSourceSchema = z6.enum([
  "local",
  "google-drive",
  "sharepoint",
  "outlook",
  "attachment",
  "archive-entry"
]);
var DOCUMENT_SOURCE_LABELS = {
  local: "This device",
  "google-drive": "Google Drive",
  sharepoint: "SharePoint",
  outlook: "Outlook",
  attachment: "Email attachment",
  "archive-entry": "Inside an archive"
};
var DocumentStorageStatusSchema = z6.enum(["pending", "stored", "failed"]);
var JobStatusSchema = z6.enum(["queued", "running", "succeeded", "failed"]);
var ProcessingJobKindSchema = z6.enum([
  "sharepoint-poll",
  "outlook-poll",
  "drive-fetch",
  "sharepoint-fetch",
  "outlook-fetch",
  "card",
  // The project check of a group of an import's settled cards, before the
  // last card of the import (2026-09-26, HID-06). The bundle job checks the
  // rest.
  "check",
  "bundle",
  "references",
  "read",
  "repair-card",
  "repair-read",
  "phase-plan"
]);
var PROCESSING_JOB_KIND_LABELS = {
  "sharepoint-poll": "Look for changes in SharePoint",
  "outlook-poll": "Look for new email in Outlook",
  "drive-fetch": "Fetch from Google Drive",
  "sharepoint-fetch": "Fetch from SharePoint",
  "outlook-fetch": "Fetch an email from Outlook",
  card: "Read the file and write its card",
  check: "Check new files against the project",
  // With no import since 2026-09-26 (RET-04): the project's own organise run.
  bundle: "Organise the files",
  references: "Redo number links",
  read: "Look at the images and fill the Brain",
  "repair-card": "Redo what failed when the file was read",
  "repair-read": "Redo what failed when the images were read",
  "phase-plan": "Read the programme"
};
var ImportStatusSchema = z6.enum([
  "open",
  "reading",
  "organising",
  "review",
  "done",
  "failed"
]);
var IMPORT_STATUS_LABELS = {
  open: "Still adding files",
  reading: "Reading the files",
  organising: "Organising them",
  review: "Ready for review",
  done: "Reviewed",
  failed: "Could not be organised"
};
var ProjectOrganiseStateSchema = z6.enum(["idle", "queued", "running", "import"]);
var PROJECT_ORGANISE_STATE_LABELS = {
  idle: "Not organised yet",
  queued: "Organising is queued.",
  running: "Organising now.",
  import: "An import is being organised. It organises these files too."
};
var ProjectOrganiseSchema = z6.object({
  /**
   * Stored files the agent may read that the project check, the families or
   * the links have not done yet. What a run would take.
   */
  files: z6.number().int().nonnegative(),
  state: ProjectOrganiseStateSchema
});
var ProjectOrganiseResponseSchema = z6.object({ organise: ProjectOrganiseSchema });
var OrganiseGapSchema = z6.enum(["check", "family", "links"]);
var ORGANISE_GAP_LABELS = {
  check: "not checked against the project",
  family: "not placed among its versions",
  links: "no links yet"
};
var ProcessingStepSchema = z6.enum([
  "fetch",
  // The same bytes as a file the project already has: the rest of the quick
  // pass is skipped (`document-ai/0016` §3).
  "copy",
  "sniff",
  "parse",
  "metadata",
  "card",
  "clear",
  "segments",
  "assets",
  // The first step of the full pass (2026-09-26, CON-19, CON-20, CON-21): the
  // pages that are read whole from their image. Before `describe`, which does
  // not read the pictures on those pages.
  "pages",
  "describe",
  "chunks",
  "render",
  "document",
  "brain",
  "children",
  // Once per import, over every file's card: `document_id` is null and
  // `import_id` is set (`document-ai/0019` §2, step C).
  //
  // The check runs first: a file about another project must not have its name
  // sent to the brief, the outline or the families call (`document-ai/0015` §8).
  "project-check",
  "brief",
  // `outline` sat here as history until 2026-10-18 (drift W-213): the step
  // went on 2026-10-03 (`document-ai/0032`), and no customer's rows hold it.
  "families",
  // Links between files (`document-ai/0024` §8): by the numbers files quote,
  // which is code, then by reading the cards, which is a model call. Two
  // steps, because they fail apart: no model skips the second, not the first.
  "references",
  "links"
]);
var PROCESSING_STEP_LABELS = {
  fetch: "Read the stored file",
  copy: "Check for an identical copy",
  sniff: "Identify the format",
  parse: "Extract the content",
  metadata: "Store the file's metadata",
  card: "Write the file's card",
  clear: "Clear the previous run",
  segments: "Store pages and sheets",
  assets: "Store images",
  pages: "Read pages from their image",
  describe: "Read what the images show",
  chunks: "Cut citable chunks",
  render: "Keep the rendered PDF",
  document: "Update the document",
  brain: "Add to the Brain",
  children: "Open attachments and archive entries",
  "project-check": "Check the files are about this project",
  brief: "Write the project brief",
  families: "Place files among their versions",
  references: "Link files by the numbers they quote",
  links: "Link files by reading them"
};
var ProcessingStepOutcomeSchema = z6.enum([
  "ok",
  "partial",
  "unsupported",
  "failed",
  "skipped"
]);
var PROCESSING_STEP_OUTCOME_LABELS = {
  ok: "Done",
  partial: "Done in part",
  unsupported: "No reader for this format",
  failed: "Failed",
  skipped: "Not needed"
};
var MetadataSourceSchema = z6.enum([
  "pdf-info",
  "xmp",
  "office-core",
  "office-app",
  "exif",
  "email-headers",
  "archive-listing",
  "html",
  "link",
  // A DWG's own file header and its plain sections, read without a converter
  // (CON-07 of the audits of 2026-09-25, 2026-09-26).
  "dwg-header",
  // When the file was last changed on the person's disk or in Drive, as the
  // upload or the pick sent it (LIN-25, 2026-09-26). Written when the file
  // arrives, not by a parser, so a new parse keeps it.
  "file-times"
]);
var METADATA_SOURCE_LABELS = {
  "pdf-info": "PDF document information",
  xmp: "XMP metadata",
  "office-core": "Office document properties",
  "office-app": "Office application properties",
  exif: "Photo (EXIF)",
  "email-headers": "Email headers",
  "archive-listing": "Archive contents",
  html: "Web page",
  link: "Link target",
  "dwg-header": "Drawing file header",
  "file-times": "File times (disk or Drive)"
};
var PARSED_METADATA_SOURCES = MetadataSourceSchema.options.filter(
  (source) => source !== "file-times"
);
var HowToReadSchema = z6.enum(["prose", "table", "drawing", "scan", "photo"]);
var HOW_TO_READ_LABELS = {
  prose: "Prose",
  table: "Table",
  drawing: "Drawing",
  scan: "Scan",
  photo: "Photograph"
};
var CardPartySchema = z6.object({
  name: z6.string(),
  roleAsWritten: z6.string().nullable()
});
function cardPartiesOf(value) {
  if (!Array.isArray(value)) return [];
  const parties = [];
  for (const entry of value) {
    if (typeof entry === "string") {
      if (entry.trim()) parties.push({ name: entry, roleAsWritten: null });
      continue;
    }
    const parsed = CardPartySchema.safeParse(entry);
    if (parsed.success && parsed.data.name.trim()) parties.push(parsed.data);
  }
  return parties;
}
function cardPartyText(party) {
  return party.roleAsWritten ? `${party.name} (${party.roleAsWritten})` : party.name;
}
var CardSignatureSchema = z6.object({
  organisation: z6.string(),
  signed: z6.boolean()
});
function cardSignaturesOf(value) {
  if (!Array.isArray(value)) return [];
  const lines = [];
  for (const entry of value) {
    const parsed = CardSignatureSchema.safeParse(entry);
    if (parsed.success && parsed.data.organisation.trim()) lines.push(parsed.data);
  }
  return lines;
}
var MadeBySchema = z6.enum(["code", "model", "person"]);
var MADE_BY_LABELS = {
  code: "Read from the file",
  model: "Read by the model",
  person: "Set by a person"
};
var ReferenceKindSchema = z6.enum([
  "purchase-order",
  "invoice",
  "contract",
  "drawing",
  "revision",
  "offer",
  "delivery-note",
  "certificate",
  // Any other document's own number: a report, a method statement, a manual, a
  // letter, an RFI, a variation. Added 2026-09-25, so that `other` can mean
  // "not a document number" and never join (`document-ai/0024` §10).
  "document",
  // A number that names the project, the site or a plot of land rather than
  // one document: printed on every drawing, so it identifies none of them.
  // Never matched to link files (`document-ai/0024` §8.2). Added 2026-09-24,
  // after the Austrian drawings each "owned" their land-plot number. Since
  // 2026-09-25 it also holds a permit, a planning application, a grid
  // connection, an award or a registration that the whole project holds.
  "project",
  "other",
  // Not numbers: what a file says about other documents in words (2026-09-26,
  // LIN-03, `document-ai/0024` §12). An entry of the annex list a document
  // gives, "Annex 5: Site layout plan", and a document it names without a
  // number of its own, "the enclosed offer (basis)". Both are the card's
  // own lists, stored here as rows the file does not own, so a re-card
  // replaces them with its numbers. Neither ever joins two files.
  "annex",
  "named-document"
]);
var REFERENCE_KIND_LABELS = {
  "purchase-order": "Purchase order",
  invoice: "Invoice",
  contract: "Contract",
  drawing: "Drawing",
  revision: "Revision",
  offer: "Offer",
  "delivery-note": "Delivery note",
  certificate: "Certificate",
  document: "Document number",
  project: "Project, site or plot number",
  other: "Other (not a document number)",
  annex: "Annex it lists",
  "named-document": "Document it names"
};
var NUMBER_REFERENCE_KINDS = ReferenceKindSchema.options.filter(
  (kind) => kind !== "annex" && kind !== "named-document"
);
var LINKING_REFERENCE_KINDS = [
  "purchase-order",
  "invoice",
  "contract",
  "drawing",
  "offer",
  "delivery-note",
  "certificate",
  "document"
];
var ProjectAliasKindSchema = z6.enum(["name"]);
var PROJECT_ALIAS_KIND_LABELS = {
  name: "Name"
};
var PartyRoleSchema = z6.enum([
  "client",
  "owner",
  "this-company",
  "contractor",
  "subcontractor",
  "planner",
  "consultant",
  "grid-operator",
  "authority",
  "landowner",
  "bidder",
  "other"
]);
var PARTY_ROLES = PartyRoleSchema.options;
var PARTY_ROLE_LABELS = {
  client: "Client",
  owner: "Owner",
  "this-company": "This company",
  contractor: "Contractor",
  subcontractor: "Subcontractor",
  planner: "Planner",
  consultant: "Consultant",
  "grid-operator": "Grid operator",
  authority: "Authority",
  landowner: "Landowner",
  bidder: "Bidder",
  other: "Other"
};
var PARTY_ROLE_DESCRIPTIONS = {
  client: "the work on this project is done for it",
  owner: "it owns or will own the plant",
  "this-company": "the company whose files these are",
  contractor: "it carries out work on this project, other than this company",
  subcontractor: "it carries out part of the work, under this company or another contractor",
  planner: "it designs or plans this project",
  consultant: "it surveys, tests, inspects or advises on this project",
  "grid-operator": "it connects this project to the grid",
  authority: "it issues a permit or an approval for this project",
  landowner: "it owns or leases the land the plant stands on",
  bidder: "it offered for work or supply on this project and was not chosen, or nothing shows its offer was taken",
  other: "it has a role on this project that none of the above fits"
};
var BriefPartySchema = z6.object({
  name: z6.string(),
  role: PartyRoleSchema,
  documentIds: z6.array(z6.string().uuid())
});
var BriefSupplierSchema = z6.object({
  name: z6.string(),
  /** What it supplies that is built into the plant: "trackers", "combiner boxes". */
  supplies: z6.string(),
  documentIds: z6.array(z6.string().uuid())
});
var ProjectBriefSchema = z6.object({
  /** Two to four sentences. */
  summary: z6.string(),
  /** What is being built, and where. */
  built: z6.string().nullable(),
  client: z6.string().nullable(),
  /** This company's role on the project: "installs the DC side for HelioWerk". */
  role: z6.string().nullable(),
  parties: z6.array(BriefPartySchema),
  suppliers: z6.array(BriefSupplierSchema)
});
var ProjectBriefResponseSchema = z6.object({
  brief: ProjectBriefSchema.nullable(),
  /** `model`, the one writer today: a person's brief has no route (2026-10-18, drift W-018). */
  madeBy: MadeBySchema.nullable(),
  /**
   * For a brief a model wrote: when the files it was written from were read,
   * which the next import reads the files changed since against (2026-09-26).
   * It stays as it was when some files could not be read, so they are read
   * again.
   */
  writtenAt: z6.string().nullable()
});
var DocumentCategoryRefSchema = z6.object({
  key: DocumentCategoryKeySchema,
  name: z6.string()
});
var DocumentMainCategorySchema = DocumentCategoryRefSchema.extend({
  madeBy: MadeBySchema
});
var SetDocumentCategoryInputSchema = z6.object({
  // Not `DocumentCategoryKeySchema`: a key in the wrong form is refused by the
  // API in the same one sentence as a key the tenant's list lacks
  // (`architecture/0031`), not in the schema's words.
  category: z6.string().trim().min(1).max(80)
});
var ProjectChangeKindSchema = z6.enum([
  // The outline's six kinds (`outline-written`, `areas-placed`,
  // `document-areas-set`, `area-renamed`, `area-removed`, `areas-merged`)
  // went with it (2026-10-03, `document-ai/0032`) and left the vocabulary on
  // 2026-10-18 (drift W-214): no customer's rows carried them.
  // The project check (`document-ai/0013`).
  "file-status-set",
  // The check would have hidden a file, and asked a person instead (2026-09-26).
  "status-asked",
  // Families (`document-ai/0016`).
  "copy-found",
  "family-placed",
  "placement-asked",
  "family-set",
  "family-repaired",
  // Links (`document-ai/0024`, Phase 4).
  "link-made",
  "link-asked",
  "link-kept",
  "link-removed",
  // A person deleted a file. Written so the log keeps its name after the row
  // is gone: the log used to lose every line about a deleted file.
  "file-removed",
  "change-undone",
  // A person moved a file to another of the tenant's categories
  // (`document-ai/0032`, 2026-10-03). `before` and `after` hold
  // `{ category }`, the key or null.
  "category-set"
]);
var PROJECT_CHANGE_KIND_LABELS = {
  "file-status-set": "A file's status changed",
  "status-asked": "A file left for a person to hide or keep",
  "copy-found": "An identical copy found",
  "family-placed": "A file placed among its versions",
  "placement-asked": "A placement left for a person",
  "family-set": "A person placed a file",
  "family-repaired": "A family repaired after a file left it",
  "link-made": "Two files linked",
  "link-asked": "A link left for a person",
  "link-kept": "A person kept a link",
  "link-removed": "A link removed",
  "file-removed": "A file deleted",
  "change-undone": "A change undone",
  "category-set": "A person moved a file to another category"
};
var UNDOABLE_CHANGE_KINDS = ["family-placed", "family-set"];
var ProjectChangeSchema = z6.object({
  id: z6.string().uuid(),
  kind: ProjectChangeKindSchema,
  /** The bundle it was part of. Null for a change a person made later. */
  importId: z6.string().uuid().nullable(),
  documentId: z6.string().uuid().nullable(),
  /** The file's title or filename, when the change is about one file. */
  documentName: z6.string().nullable(),
  /** What happened, in words. Never empty. */
  summary: z6.string(),
  madeBy: MadeBySchema,
  /** The entry that undid this one, when one did. */
  undoneById: z6.string().uuid().nullable(),
  /**
   * Whether a screen should offer an Undo. Not the control — `undoChange` is,
   * and it still answers 409 when the files have moved since. This only decides
   * whether the button is drawn, so a person is not offered an undo that
   * nothing would accept.
   */
  canUndo: z6.boolean(),
  createdAt: z6.string()
});
var ChangesResponseSchema = z6.object({ changes: z6.array(ProjectChangeSchema) });
var FamilyRoleSchema = z6.enum([
  "current",
  "superseded",
  "alternative-format",
  "signature-state",
  "series-member",
  "identical-copy"
]);
var FAMILY_ROLE_LABELS = {
  current: "Current version",
  superseded: "Superseded",
  "alternative-format": "Another format",
  "signature-state": "Earlier signature state",
  "series-member": "One of a series",
  "identical-copy": "Identical copy"
};
var ANSWERING_FAMILY_ROLES = ["current", "series-member"];
function roleAnswers(role) {
  return ANSWERING_FAMILY_ROLES.includes(role);
}
var PlacementRelationSchema = z6.enum([
  "on-its-own",
  "replaces",
  "replaced-by",
  "other-format-of",
  "earlier-signature-of",
  "series-with"
]);
var PLACEMENT_RELATION_LABELS = {
  "on-its-own": "On its own, with no other versions",
  replaces: "A newer version of",
  "replaced-by": "An older version of",
  "other-format-of": "The same document, in another format, as",
  "earlier-signature-of": "An earlier signature state of",
  "series-with": "Part of a series with"
};
var RelationToFileSchema = PlacementRelationSchema.exclude(["on-its-own"]);
var PlaceDocumentInputSchema = z6.union([
  z6.object({ relation: z6.literal("on-its-own") }),
  z6.object({ relation: RelationToFileSchema, documentId: z6.string().uuid() })
]);
var QuestionFactsSchema = z6.object({
  documentId: z6.string().uuid(),
  /**
   * "PDF made: 2026-08-13 02:18 UTC with PScript5.dll", "pages: 29",
   * "shares 8 of 29 pages with “WR-2.pdf”". Each is evidence, never proof.
   */
  facts: z6.array(z6.string())
});
var PlacementQuestionSchema = z6.object({
  /** The family's one question: every file asked in it holds the same id. */
  id: z6.string().uuid(),
  /** What the model called the document, in a few words. Null when it gave no name. */
  name: z6.string().nullable(),
  relation: RelationToFileSchema,
  documentId: z6.string().uuid(),
  reason: z6.string(),
  /** Why code left the file for a person, in words: "The model was not sure about …". */
  why: z6.array(z6.string()),
  /**
   * What this file and the file it would relate to show about themselves, as
   * the families call was shown it, with the other files by name (VER-01: the
   * dates of the files are hints for a person, never proof in code).
   */
  facts: z6.array(QuestionFactsSchema)
});
var DocumentRefSchema = z6.object({
  id: z6.string().uuid(),
  name: z6.string()
});
var FileStatusSchema = z6.enum([
  "this-project",
  "other-project",
  "template",
  "private",
  "junk"
]);
var FILE_STATUS_LABELS = {
  "this-project": "This project\u2019s file",
  "other-project": "Another project\u2019s file",
  template: "Template",
  private: "Private",
  junk: "Junk"
};
var FILE_STATUS_COUNT_WORDS = {
  "this-project": ["this project\u2019s", "this project\u2019s"],
  "other-project": ["another project\u2019s", "another project\u2019s"],
  template: ["template", "templates"],
  private: ["private", "private"],
  junk: ["junk", "junk"]
};
var FILE_STATUS_PHRASES = {
  "this-project": "this project\u2019s file",
  "other-project": "another project\u2019s file",
  template: "a template",
  private: "private",
  junk: "junk"
};
var HIDDEN_FILE_STATUSES = [
  "other-project",
  "template",
  "private",
  "junk"
];
function statusHides(status) {
  return status !== null && status !== "this-project";
}
var HiddenFileStatusSchema = z6.enum(HIDDEN_FILE_STATUSES);
var StatusDoubtKindSchema = z6.enum([
  "shares-party",
  "shares-number",
  "names-this-project",
  "not-sure",
  "no-evidence",
  "looks-like-version",
  "inside-hidden-file",
  "copy-filed-elsewhere",
  "listed-by-this-project"
]);
var STATUS_DOUBT_LABELS = {
  "shares-party": "Shares a party",
  "shares-number": "Shares a number",
  "names-this-project": "Names this project",
  "not-sure": "The model was not sure",
  "no-evidence": "No quote from the file",
  "looks-like-version": "May be a version of a visible file",
  "inside-hidden-file": "Came in a hidden file",
  "copy-filed-elsewhere": "A copy is filed in another folder",
  "listed-by-this-project": "A file of this project lists it"
};
var StatusDoubtSchema = z6.object({
  kind: StatusDoubtKindSchema,
  /**
   * The party, number, name or title, as the file writes it; the container's
   * name for `inside-hidden-file`; the copy's folder for
   * `copy-filed-elsewhere`; the list's entry for `listed-by-this-project`.
   * Null for a doubt about the verdict.
   */
  value: z6.string().nullable(),
  /**
   * The files of this project that hold it too; the container for
   * `inside-hidden-file`; the copy for `copy-filed-elsewhere`; the file that
   * lists it for `listed-by-this-project`. Empty for a doubt about the
   * verdict.
   */
  documentIds: z6.array(z6.string().uuid())
});
var StatusQuestionSchema = z6.object({
  /** The status the check would have set, or the hidden container's (`inside-hidden-file`). */
  status: HiddenFileStatusSchema,
  /** The model's one sentence, or code's for a file inside a hidden one. */
  reason: z6.string().nullable(),
  /** The model's quote from the file's own content, when code found it there. */
  evidence: z6.string().nullable(),
  doubts: z6.array(StatusDoubtSchema).min(1)
});
var DocumentStatusDoubtSchema = z6.object({
  kind: StatusDoubtKindSchema,
  value: z6.string().nullable(),
  /** The files of this project that hold it too, that are still here. */
  documents: z6.array(DocumentRefSchema)
});
var DocumentStatusQuestionSchema = z6.object({
  status: HiddenFileStatusSchema,
  reason: z6.string().nullable(),
  evidence: z6.string().nullable(),
  doubts: z6.array(DocumentStatusDoubtSchema)
});
var DocumentStatusSchema = z6.object({
  /** Null when nobody has said: not checked, or checked and it names no project. */
  status: FileStatusSchema.nullable(),
  /** One sentence, in the words of whoever set it. */
  reason: z6.string().nullable(),
  madeBy: MadeBySchema.nullable(),
  /** When the check last looked. Null means it never has. */
  checkedAt: z6.string().nullable(),
  /**
   * Whether its status keeps it from the agent. A file the check has not
   * looked at yet is kept out too while its import is still being read or
   * organised (the product owner, 2026-09-26, `data-model/0013` §8); that is
   * the views' rule, and this does not say it.
   */
  hidden: z6.boolean(),
  /**
   * The check's question, when it would have hidden the file but did not
   * (2026-09-26). The status is then null and the file is visible. Null when
   * there is no question.
   */
  question: DocumentStatusQuestionSchema.nullable(),
  /**
   * The question the check asked before the file was hidden: a person, or a
   * group answer, hid it after the check said it was not sure (2026-09-26,
   * tracker HID-12). Read from the change log, since a status clears the
   * question. Remove on such a file asks again and names the doubt. Null for
   * a visible file, and for a hide nobody doubted.
   */
  wasAsked: DocumentStatusQuestionSchema.nullable()
});
var DocumentCardStateSchema = z6.object({
  /** A card exists: written by the model, by a person, or taken from an original. */
  written: z6.boolean(),
  /**
   * Why there is none, once the card step has finished without one. The step's
   * own user-facing reason, never its internal detail. Null for a file with a
   * card, and for one the step has not reached yet.
   */
  missingBecause: z6.string().nullable()
});
var SetDocumentStatusInputSchema = z6.object({
  status: FileStatusSchema,
  reason: z6.string().trim().max(300).optional()
});
var MAX_STATUS_GROUP = 500;
var SetDocumentStatusesInputSchema = z6.object({
  documentIds: z6.array(z6.string().uuid()).min(1).max(MAX_STATUS_GROUP),
  status: FileStatusSchema
});
var SetDocumentStatusesResponseSchema = z6.object({
  /** How many files now have the status. A file a person already gave it counts too. */
  updated: z6.number().int().nonnegative()
});
var FamilyMemberSchema = DocumentRefSchema.extend({
  role: FamilyRoleSchema,
  /** The file it defers to, when its role has one. */
  relatedDocumentId: z6.string().uuid().nullable(),
  documentDate: z6.string().nullable(),
  revision: z6.string().nullable()
});
var IdentityKindSchema = z6.enum(["email", "photo"]);
var IDENTITY_VERSION_RULE = {
  email: "An email is never an older or newer version of another file. Each message stays valid for its own date.",
  photo: "A photograph is never an older or newer version of another file. Each one shows the moment it was taken."
};
var DocumentFamilySchema = z6.object({
  /** Null when the file has no relatives: it is current, on its own. */
  id: z6.string().uuid().nullable(),
  /** What the family is called: "Ramming plan". */
  name: z6.string().nullable(),
  series: z6.boolean(),
  role: FamilyRoleSchema,
  /** The file this one defers to. Null for a role that answers. */
  related: DocumentRefSchema.nullable(),
  /** Null until placement has looked at the file. */
  madeBy: MadeBySchema.nullable(),
  /** One line on why, from whoever placed it. */
  reason: z6.string().nullable(),
  placedAt: z6.string().nullable(),
  /** The change an Undo on this file reverses, when there is one to reverse. */
  undoChangeId: z6.string().uuid().nullable(),
  /** The family's other files. Empty for a file on its own. */
  members: z6.array(FamilyMemberSchema),
  /**
   * When code holds an identity fact for the file: then it is never offered as
   * an older or newer version. Null when code does not know, which is not a
   * claim that it is anything else.
   */
  identity: IdentityKindSchema.nullable()
});
var QuestionMemberSchema = z6.object({
  document: DocumentRefSchema,
  /** Read as "Is this file … the other file?". */
  relation: RelationToFileSchema,
  /** The file it would relate to. */
  other: DocumentRefSchema,
  /** The model's reason. */
  reason: z6.string(),
  /** Why code left it for a person. */
  why: z6.array(z6.string())
});
var DocumentQuestionSchema = z6.object({
  id: z6.string().uuid(),
  /** What the model called the document. Null when it gave no name. */
  name: z6.string().nullable(),
  members: z6.array(QuestionMemberSchema).min(1),
  /** Each file the question names, members and the files they relate to, with its facts. */
  files: z6.array(z6.object({ document: DocumentRefSchema, facts: z6.array(z6.string()) }))
});
var UndoResponseSchema = z6.object({ changeId: z6.string().uuid() });
var LinkKindSchema = z6.enum([
  "refers-to",
  "annex-of",
  "responds-to",
  "source-of",
  // A file that is some of the pages of another, from the part to the whole:
  // the first four pages of a signed order, scanned alone (VER-09, the
  // default of 2026-09-26, `document-ai/0024` §13). Never a family role: a
  // part is not a version of the whole, and both answer.
  "part-of"
]);
var LINK_KIND_LABELS = {
  "refers-to": "Refers to",
  "annex-of": "Annex of",
  "responds-to": "Responds to",
  "source-of": "Source of",
  "part-of": "Part of"
};
var LINK_KIND_PHRASES = {
  "refers-to": { out: "refers to", in: "is referred to by" },
  "annex-of": { out: "is an annex of", in: "has as an annex" },
  "responds-to": { out: "responds to", in: "is answered by" },
  "source-of": { out: "is the source of", in: "is made from" },
  "part-of": { out: "is a part of", in: "has as a part" }
};
var READ_LINK_KINDS = [
  "refers-to",
  "annex-of",
  "responds-to",
  "source-of",
  "part-of"
];
var LinkFoundBySchema = z6.enum(["reference", "email-reply", "reading"]);
var LINK_FOUND_BY_LABELS = {
  reference: "A number it quotes",
  "email-reply": "The email it replies to",
  reading: "Read from the two files"
};
var LinkStateSchema = z6.enum(["live", "asked", "removed"]);
var LINK_STATE_LABELS = {
  live: "Linked",
  asked: "Waiting for a person",
  removed: "Removed"
};
var LinkEvidenceSchema = z6.object({
  numbers: z6.array(z6.object({ quoted: z6.string(), owned: z6.string(), kind: ReferenceKindSchema })).optional(),
  /**
   * For an `email-reply` link (2026-09-26, ORG-06): the Message-ID of the
   * email replied to, which the reply's In-Reply-To header names.
   */
  messageId: z6.string().optional()
});
var LinkedFileSchema = DocumentRefSchema.extend({
  /** False once the file was deleted; the name is the one it had then. */
  exists: z6.boolean()
});
var DocumentLinkSchema = z6.object({
  id: z6.string().uuid(),
  kind: LinkKindSchema,
  /** `out`: this file → the other. `in`: the other → this file. */
  direction: z6.enum(["out", "in"]),
  /** The file the link is written from. */
  from: LinkedFileSchema,
  /** The file the link points at, as it was linked. */
  to: LinkedFileSchema,
  /** Where following the link lands, when that is not `to` itself. */
  current: DocumentRefSchema.nullable(),
  foundBy: LinkFoundBySchema,
  madeBy: MadeBySchema,
  state: LinkStateSchema,
  /** One sentence a person can check. */
  reason: z6.string().nullable(),
  evidence: LinkEvidenceSchema.nullable(),
  /** The bundle whose organising made it. */
  importId: z6.string().uuid().nullable(),
  createdAt: z6.string().nullable(),
  removed: z6.object({ at: z6.string(), madeBy: MadeBySchema, reason: z6.string().nullable() }).nullable()
});
var ContainedFileSchema = LinkedFileSchema.extend({
  /**
   * The folder the file sat in inside its container: for `container`, where
   * this file sat in it; for one of `contents`, where that file sat in this
   * one. A zip entry's folders, without its own name. Null at the container's
   * top level, and always for an email's attachment, which sits in no folder.
   * It was the raw `source_ref` until 2026-10-18, which showed people the
   * parser's "attachment/0".
   */
  path: z6.string().nullable()
});
var ContainmentSchema = z6.object({
  container: ContainedFileSchema.nullable(),
  contents: z6.array(ContainedFileSchema)
});
var LinksResponseSchema = z6.object({
  links: z6.array(DocumentLinkSchema),
  /** One file's containment, with `documentId`; null for a project-wide list. */
  containment: ContainmentSchema.nullable()
});
var LinkResponseSchema = z6.object({ link: DocumentLinkSchema });
var SetLinkStateInputSchema = z6.object({ state: z6.enum(["live", "removed"]) });
var ProjectDocumentSchema = z6.object({
  id: z6.string().uuid(),
  projectId: z6.string().uuid(),
  /**
   * The tenant's category the file lives in (`document-ai/0032`), and who set
   * it. Null when it is not sorted yet: no card has chosen one and no person
   * has moved it, or its key has left the tenant's list.
   */
  category: DocumentMainCategorySchema.nullable(),
  /**
   * The other categories the card says it is also relevant to, in the
   * tenant's order. Never the main one; a key no longer in the list is left
   * out.
   */
  alsoRelevant: z6.array(DocumentCategoryRefSchema),
  filename: z6.string(),
  mimeType: z6.string(),
  sizeBytes: z6.number().int().nonnegative(),
  storageStatus: DocumentStorageStatusSchema,
  source: DocumentSourceSchema,
  sourceRef: z6.string().nullable(),
  /** The document this one was unpacked from: an email or an archive. Null for an upload. */
  parentDocumentId: z6.string().uuid().nullable(),
  /** The bundle it arrived in. Null for a file that arrived on its own. */
  importId: z6.string().uuid().nullable(),
  /**
   * When the file was deleted in SharePoint or Outlook, or moved out of the
   * project's folder (`plans/sharepoint-sync.md`, question 15). Such a file is
   * out of answers (an agent reader never sees a non-null value) but kept, so
   * past answers can open what they cited. Null while there, and if not synced.
   */
  removedAtSource: z6.string().datetime().nullable(),
  // ---- The card, as far as a user sees it (`document-ai/0015` layer 2). The
  // rest of what the card reads — the parties, the project it names, how it has
  // to be read — stays on the server (`data-model/0013` §8).
  /** What the document calls itself, read from its content. Null until carded. */
  title: z6.string().nullable(),
  /** Free text, in the file's own terms: "ramming plan", "purchase order". Not
   *  a closed list, because every project has its own kinds of document. */
  docType: z6.string().nullable(),
  /** The date the document states — a title block, a revision table, a letter
   *  date. Not the upload time, which is `createdAt` and is the same for every
   *  file in one bundle. ISO date, no time: a title block has no clock. */
  documentDate: z6.string().nullable(),
  /** The revision the document states, as written: "02", "Rev C", "V03". */
  revision: z6.string().nullable(),
  /** Where it sits among its versions (`document-ai/0016`). */
  family: DocumentFamilySchema,
  /** What it is, and whether the agent may read it (`document-ai/0013`). */
  status: DocumentStatusSchema,
  /** Whether anything has said what it is, and why not when nothing has. */
  card: DocumentCardStateSchema,
  /** The model's question about it, waiting for a person. Null when none. */
  question: DocumentQuestionSchema.nullable(),
  pageCount: z6.number().int().nonnegative().nullable(),
  /**
   * How far the out-of-band parse has got, or null when no job was ever
   * recorded — a row that predates this field, or one whose bytes never landed.
   *
   * Extraction cannot sit on the request path: every image in a document goes
   * to a vision model, and a 75-page PDF with 100 images is minutes of calls.
   * So `completeUpload` answers as soon as the bytes are verified, and this is
   * what tells a client whether the card and the Brain rows for a document are
   * final or still being written.
   *
   * **Additive, and only additive.** A document is listed from the moment it is
   * stored, exactly as before; this says what is still happening to it, never
   * whether it exists. `failed` here means the parse failed — the file is
   * stored, downloadable and in the Brain regardless.
   */
  processingStatus: JobStatusSchema.nullable(),
  /**
   * The MIME type `GET .../preview` will actually serve, or null when nothing
   * can be shown.
   *
   * Not the same question as `mimeType`, and that is the point: a `.docx` is
   * rendered to PDF to be read at all, and once that render is kept the preview
   * for a Word document is `application/pdf` even though the document is not
   * one. A client that switched on `mimeType` would show the segments panel for
   * a file it could have displayed, and one that switched on
   * `isInlineViewable(mimeType)` would agree with it.
   *
   * Null means genuinely nothing to show — a DWG, or a `.docx` parsed on a
   * container without LibreOffice. The viewer says so and the extraction is the
   * whole answer, exactly as before.
   */
  previewType: z6.string().nullable(),
  /**
   * What of the file could not be read, once every try has run out
   * (2026-09-26, `architecture/0028` §6). One or two sentences, such as "3 of
   * 40 pictures could not be read." Null for a file read in full, and while a
   * try is still to come. The file is stored and can be opened either way.
   */
  readFailure: z6.string().nullable(),
  /**
   * The pictures of the file that have no reading after its latest full read
   * (2026-09-26, tracker RET-12): a call that failed, a type no model takes,
   * a picture past the file's allowance, a scanned page that could not be
   * rendered. The `describe` step's own count. Null while nothing has read the
   * pictures, and when the pictures were stored again after the last read.
   */
  picturesNotRead: z6.number().int().nonnegative().nullable(),
  /**
   * What an organise step has not done for it yet (2026-09-26, RET-12). Empty
   * for a file that is organised, and for a hidden file.
   */
  organiseGaps: z6.array(OrganiseGapSchema),
  createdAt: z6.string()
});
function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
var INLINE_VIEWABLE_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
  "text/plain"
];
function isInlineViewable(mimeType) {
  const type = mimeType.split(";")[0]?.trim().toLowerCase() ?? "";
  return INLINE_VIEWABLE_TYPES.includes(type);
}
var HEIC_TYPES = ["image/heic", "image/heif"];
var HEIC_REJECTION = "iPhone photos (HEIC) can\u2019t be shown in a browser yet. On the phone: Settings \u2192 Camera \u2192 Formats \u2192 Most Compatible, or share the photo as JPEG.";
var UploadUrlInputSchema = z6.object({
  filename: z6.string().trim(),
  mimeType: z6.string().trim().min(1).max(255),
  sizeBytes: z6.number().int(),
  /**
   * The bundle this file belongs to (`document-ai/0019` §2), from
   * `POST /projects/:id/imports`. Absent for a file that arrives on its own —
   * which then gets no review, only its own card, which also sorts it into
   * its category (`document-ai/0032`).
   */
  importId: z6.string().uuid().optional(),
  /**
   * The folder the file was in, from the folder it was dropped or picked in
   * down: "Site A/03 Budget/old" (2026-09-26, `document-ai/0015` §8). Absent
   * for a file picked on its own, whose folder the browser does not give.
   * The API runs `normaliseSourcePath` over it, so any length is taken and a
   * long one is shortened, never refused.
   */
  sourcePath: z6.string().optional(),
  /**
   * When the file was last changed on the person's disk: the browser's
   * `File.lastModified`, as an ISO time (2026-09-26, LIN-25). Absent when
   * the browser gives none. The API runs `normaliseFileTime` over it, so a
   * time it cannot read is dropped, never refused.
   */
  modifiedAt: z6.string().max(64).optional()
}).superRefine((file, ctx) => {
  const refused = fileRefusal(file);
  if (refused) refuse(ctx, refused.reason);
});
var UploadUrlResponseSchema = z6.object({
  documentId: z6.string().uuid(),
  uploadUrl: z6.string().url(),
  storageKey: z6.string(),
  /** Seconds. */
  expiresIn: z6.number().int().positive()
});
var DocumentResponseSchema = z6.object({ document: ProjectDocumentSchema });
var DocumentsResponseSchema = z6.object({
  documents: z6.array(ProjectDocumentSchema)
});
var DriveImportInputSchema = z6.object({
  files: z6.array(
    z6.object({
      id: z6.string().min(1),
      name: z6.string(),
      mimeType: z6.string().min(1).max(255),
      sizeBytes: z6.number().int().nonnegative().optional(),
      /**
       * Its folders in Drive, below the drive's own root: "Projects/P1/Invoices"
       * (2026-09-26). Absent when the picker could not look them up. The
       * API runs `normaliseSourcePath` over it.
       */
      sourcePath: z6.string().optional(),
      /**
       * When the file was last changed in Drive: the picker's
       * `lastEditedUtc`, as an ISO time (2026-09-26, LIN-25). Absent when
       * the picker gives none. The API runs `normaliseFileTime` over it.
       */
      modifiedAt: z6.string().max(64).optional()
    })
  ).min(1),
  /**
   * Join a bundle the client already has open, so everything added there,
   * dropped or picked, is organised and reviewed together. Absent, the pick
   * is a bundle of its own, opened and closed by the request
   * (`document-ai/0019` §2).
   */
  importId: z6.string().uuid().optional()
}).superRefine((pick, ctx) => {
  if (pick.files.length > MAX_DRIVE_PICK) refuse(ctx, UPLOAD_REFUSALS.tooManyPicked);
});
var DriveImportFailureSchema = z6.object({
  /** The Drive file id, so the UI can match a failure to what was picked. */
  fileId: z6.string(),
  name: z6.string(),
  /** Safe to render. See the API's `import` handler for what is withheld. */
  reason: z6.string()
});
var ProjectImportSchema = z6.object({
  id: z6.string().uuid(),
  projectId: z6.string().uuid(),
  /**
   * Where its files came from. A `sharepoint` import is opened by the sync
   * when it finds changes in the project's folder, and closed once the folder
   * has been quiet for a while (`plans/sharepoint-sync.md`, question 9). An
   * `outlook` import the same, for the project's Outlook folders
   * (`plans/outlook-sync.md`, question 25).
   */
  source: z6.enum(["local", "google-drive", "sharepoint", "outlook"]),
  /**
   * Opened for the project's programme, from setup's Basics or Milestones
   * (2026-10-03, `document-ai/0019` §2). It is organised as any
   * import is; the review labels it. Until the outline went (the same day,
   * `document-ai/0032`) it was also the one import that never built one.
   */
  programme: z6.boolean(),
  status: ImportStatusSchema,
  /** Stored files a person uploaded or picked: the ones with no parent. */
  files: z6.number().int().nonnegative(),
  /** Stored files unpacked from one of them: a zip's entries, an email's attachments. */
  unpacked: z6.number().int().nonnegative(),
  /** Of `files` and `unpacked` together, those with a card. */
  carded: z6.number().int().nonnegative(),
  /** Of `files` and `unpacked` together, those read in full. An identical copy counts as read. */
  read: z6.number().int().nonnegative(),
  /** Files that arrived and could not be carded or read. A failed Drive fetch is in `failures`. */
  failed: z6.number().int().nonnegative(),
  refused: z6.number().int().nonnegative(),
  /**
   * Picked Drive files the project already holds, unchanged: the same Drive
   * file with the same bytes. Not stored again (decided 2026-09-22,
   * `document-ai/0016` §11), and counted so "picked six, got four" still adds up.
   */
  unchanged: z6.number().int().nonnegative(),
  /**
   * Picked files that never arrived, and why, in words the person who picked
   * them can act on. A file fetched by the worker fails where no response can
   * carry it, so the import is where "picked six, got four" stays observable
   * (`architecture/0003`). Files that did arrive and then could not be read are
   * on their documents, not here.
   *
   * Once the import has closed, a browser upload that never finished is here
   * too, with `UNFINISHED_UPLOAD_REASON` and its `documentId`, so a person can
   * remove it (`document-ai/0019` §8, 2026-09-25). A failed Drive fetch left no
   * document, and has no id.
   */
  failures: z6.array(
    z6.object({
      name: z6.string(),
      reason: z6.string(),
      documentId: z6.string().uuid().optional()
    })
  ),
  openedAt: z6.string().datetime(),
  closedAt: z6.string().datetime().nullable(),
  reviewedAt: z6.string().datetime().nullable()
});
var ImportResponseSchema = z6.object({ import: ProjectImportSchema });
var ImportsResponseSchema = z6.object({ imports: z6.array(ProjectImportSchema) });
var OpenImportInputSchema = z6.object({
  /** Only a browser upload is opened by a client. A Drive pick opens its own. */
  source: z6.literal("local"),
  /** The import holds the project's programme (`ProjectImportSchema.programme`). */
  programme: z6.boolean().optional()
});
var DriveImportResponseSchema = z6.object({
  import: ProjectImportSchema,
  /** Files queued for fetching. */
  accepted: z6.number().int().nonnegative(),
  failed: z6.array(DriveImportFailureSchema)
});
var SegmentKindSchema = z6.enum(["page", "sheet", "document"]);
var AssetRoleSchema = z6.enum(["decoration", "text-image", "figure", "evidence"]);
var ImageSubjectSchema = z6.enum(["document", "scene"]);
var IMAGE_SUBJECT_LABELS = {
  document: "A page of a document",
  scene: "A scene"
};
var PageReadingKindSchema = z6.enum(["drawing", "figure", "transcription", "lost-words"]);
var PAGE_READING_KIND_LABELS = {
  drawing: "A drawing page",
  figure: "A page whose figure carries content",
  transcription: "A page with no usable text layer",
  "lost-words": "A page whose text layer lost words"
};
var PageReadingStatusSchema = z6.enum(["read", "partial", "failed", "not-needed", "blank"]);
var PAGE_READING_STATUS_LABELS = {
  read: "Read",
  partial: "Read in part",
  failed: "Could not be read",
  "not-needed": "No reading needed",
  blank: "Blank page"
};
var ReadingItemKindSchema = z6.enum([
  "title-block",
  "legend",
  "table-row",
  "label",
  "assignment",
  "note",
  "not-read",
  "line",
  "question"
]);
var READING_ITEM_KIND_LABELS = {
  "title-block": "Title block",
  legend: "Legend entry",
  "table-row": "Table row",
  label: "Label",
  assignment: "Assignment by colour or position",
  note: "Note",
  "not-read": "Not read",
  line: "Transcribed line",
  question: "Question for a person"
};
var ReadingSourceSchema = z6.enum(["text-layer", "image-only"]);
var READING_SOURCE_LABELS = {
  "text-layer": "Text layer",
  "image-only": "Seen in the image only"
};
var SegmentTextSourceSchema = z6.enum(["text-layer", "transcribed"]);
var SEGMENT_TEXT_SOURCE_LABELS = {
  "text-layer": "From the text layer",
  transcribed: "Transcribed from the image"
};
var PageQuestionSchema = z6.object({
  id: z6.string().uuid(),
  /** One sentence a person can check against the page. */
  text: z6.string(),
  /** What each image read there, such as the symbols a legend entry was read as. */
  readings: z6.array(z6.string())
});
var BBoxSchema = z6.object({
  x: z6.number().min(0).max(1),
  y: z6.number().min(0).max(1),
  w: z6.number().min(0).max(1),
  h: z6.number().min(0).max(1)
});
var CitationLocatorSchema = z6.discriminatedUnion("kind", [
  z6.object({
    kind: z6.literal("page"),
    page: z6.number().int().min(1),
    /** The nearest heading above the text, when the parser found one. */
    heading: z6.string().optional(),
    bbox: BBoxSchema.optional(),
    /**
     * A verbatim fragment of the cited text, so the reader can Ctrl-F for it.
     *
     * This is the mitigation for the one accepted flaw in the DOCX path: pages
     * come from LibreOffice's rendering, which usually agrees with Word but can
     * drift by a page on complex layouts or with missing fonts. A snippet finds
     * the passage whether or not the page number landed.
     */
    snippet: z6.string().optional()
  }),
  z6.object({
    kind: z6.literal("sheet-range"),
    /** The sheet's name, which is what the user sees on the tab. */
    sheet: z6.string(),
    sheetIndex: z6.number().int().min(0).optional(),
    /**
     * The spreadsheet's own 1-based row numbers — never indices into a filtered
     * list, or "row 12" will not be row 12 when the file is opened.
     */
    rowStart: z6.number().int().min(1),
    rowEnd: z6.number().int().min(1),
    /** Header names, when the chunk covers only some columns. */
    columns: z6.array(z6.string()).optional()
  }),
  z6.object({
    kind: z6.literal("asset"),
    assetId: z6.string().uuid(),
    page: z6.number().int().min(1).optional(),
    /**
     * The worksheet a picture of a workbook is on, by the name on its tab
     * (2026-09-26, CON-12). A sheet has no page, so this is where the reader
     * goes.
     */
    sheet: z6.string().optional(),
    /** The cell such a picture is anchored at, such as `B12`: "near cell B12". */
    cell: z6.string().regex(/^[A-Z]{1,3}[1-9]\d{0,6}$/).optional(),
    /** 1-based within its page, for "figure 2" — not the asset's `sequence`. */
    figureNo: z6.number().int().min(1).optional(),
    bbox: BBoxSchema.optional()
  }),
  z6.object({ kind: z6.literal("document") })
]);
function formatCitation(locator, opts) {
  const filename = opts?.filename?.trim() || null;
  if (locator.kind === "document") {
    return filename ? `in ${filename}` : "in this document";
  }
  const body = renderLocator(locator);
  return filename ? `${filename} \u2014 ${body}` : body;
}
function renderLocator(locator) {
  switch (locator.kind) {
    case "page":
      return locator.heading ? `page ${locator.page} \u2014 ${locator.heading}` : `page ${locator.page}`;
    case "sheet-range": {
      const { sheet, rowStart, rowEnd } = locator;
      if (rowStart === rowEnd) return `${sheet}, row ${rowStart}`;
      const lo = Math.min(rowStart, rowEnd);
      const hi = Math.max(rowStart, rowEnd);
      return `${sheet}, rows ${lo}\u2013${hi}`;
    }
    case "asset": {
      const parts = [];
      if (locator.page !== void 0) parts.push(`page ${locator.page}`);
      if (locator.sheet !== void 0) parts.push(locator.sheet);
      if (locator.cell !== void 0) parts.push(`near cell ${locator.cell}`);
      parts.push(locator.figureNo !== void 0 ? `figure ${locator.figureNo}` : "figure");
      return parts.join(", ");
    }
  }
}
var IMG_MARKER_PATTERN = /\[\[img:(\d+)(?:\|([^\n]*?))?\]\]/;
var MAX_CAPTION_CHARS = 200;
function formatImageMarker(sequence, caption) {
  const n = Number.isFinite(sequence) ? Math.max(0, Math.trunc(sequence)) : 0;
  const clean = caption ? sanitiseCaption(caption) : "";
  return clean ? `[[img:${n}|${clean}]]` : `[[img:${n}]]`;
}
function sanitiseCaption(raw) {
  return raw.replace(/\[\[|\]\]/g, " ").replace(/[\u0000-\u001F\u007F]/g, " ").replace(/\s+/g, " ").trim().slice(0, MAX_CAPTION_CHARS).replace(/[\]\s]+$/, "");
}
function parseImageMarkers(text) {
  const pattern = freshMarkerPattern();
  const markers = [];
  for (const match of text.matchAll(pattern)) {
    const sequence = Number(match[1]);
    if (!Number.isSafeInteger(sequence)) continue;
    const caption = match[2]?.trim();
    markers.push({
      sequence,
      caption: caption ? caption : null,
      index: match.index,
      length: match[0].length
    });
  }
  return markers;
}
function stripImageMarkers(text) {
  return trimLineEnds(text.replace(freshMarkerPattern(), "")).replace(/\n{3,}/g, "\n\n").trim();
}
function trimLineEnds(text) {
  return text.replace(/[^\S\n]+/g, (run, offset) => {
    const after = text[offset + run.length];
    if (after === void 0 || after === "\n") return "";
    for (let at = run.length - 1; at > 0; at -= 1) {
      const char = run[at];
      if (char === "\r" || char === "\u2028" || char === "\u2029") return run.slice(at);
    }
    return run;
  });
}
function freshMarkerPattern() {
  return new RegExp(IMG_MARKER_PATTERN.source, "g");
}
var DocumentSegmentSchema = z6.object({
  id: z6.string().uuid(),
  documentId: z6.string().uuid(),
  /** 0-based ordering. Always present, unlike `pageNo`. */
  sequence: z6.number().int().nonnegative(),
  kind: SegmentKindSchema,
  /** 1-based page, or the sheet index — which may legitimately be 0. */
  pageNo: z6.number().int().nonnegative().nullable(),
  label: z6.string().nullable(),
  markdown: z6.string().nullable(),
  text: z6.string().nullable(),
  /** The page's size in PDF points, as the parser read it; null for a sheet. */
  widthPt: z6.number().int().positive().nullable(),
  heightPt: z6.number().int().positive().nullable(),
  /**
   * Where `text` came from (CON-20, 2026-09-26). `transcribed` is a page with
   * no usable text layer whose text was read from its image.
   */
  textSource: SegmentTextSourceSchema,
  /**
   * The page reading's questions for a person to check, such as a legend in
   * doubt. Empty for every other page.
   */
  questions: z6.array(PageQuestionSchema),
  createdAt: z6.string()
});
var DocumentAssetSchema = z6.object({
  id: z6.string().uuid(),
  documentId: z6.string().uuid(),
  segmentId: z6.string().uuid().nullable(),
  /** What `[[img:N]]` resolves on. */
  sequence: z6.number().int().nonnegative(),
  role: AssetRoleSchema,
  mimeType: z6.string(),
  widthPx: z6.number().int().positive().nullable(),
  heightPx: z6.number().int().positive().nullable(),
  bbox: BBoxSchema.nullable(),
  caption: z6.string().nullable(),
  altText: z6.string().nullable(),
  ocrText: z6.string().nullable(),
  /**
   * Null until a vision model has seen it, or when none was available. The
   * empty string is a picture the model read and found nothing in, a blank
   * page (2026-09-26): there is nothing to show.
   */
  description: z6.string().nullable(),
  /** Non-null means this is a repeat and inherits the original's description. */
  duplicateOfId: z6.string().uuid().nullable(),
  checksumSha256: z6.string().nullable(),
  createdAt: z6.string()
});
var DocumentChunkSchema = z6.object({
  id: z6.string().uuid(),
  documentId: z6.string().uuid(),
  segmentId: z6.string().uuid().nullable(),
  sequence: z6.number().int().nonnegative(),
  text: z6.string(),
  locator: CitationLocatorSchema,
  /** Set when the chunk *is* an image description rather than page text. */
  assetId: z6.string().uuid().nullable(),
  tokenEstimate: z6.number().int().nonnegative().nullable(),
  /**
   * Derived, not stored: `formatCitation(locator)`. On the wire so no client
   * ever needs a second renderer and none can disagree about the wording.
   */
  citation: z6.string(),
  createdAt: z6.string()
});
var DocumentSegmentsResponseSchema = z6.object({
  segments: z6.array(DocumentSegmentSchema)
});
var DocumentAssetsResponseSchema = z6.object({
  assets: z6.array(DocumentAssetSchema)
});
var ParsedPhaseSchema = z6.object({
  /** Its level in the programme's outline. 0 for a job result stored before levels. */
  level: PhaseLevelSchema.default(0),
  // No length bound, as on `PhaseInputSchema` (2026-10-03).
  name: z6.string().min(1),
  startDate: IsoDateSchema.nullish(),
  endDate: IsoDateSchema.nullish(),
  note: z6.string().nullish()
});
var ProjectSuggestionSchema = z6.object({
  name: z6.string().max(160).nullable(),
  country: CountryCodeSchema.nullable(),
  region: z6.string().max(120).nullable(),
  spec: z6.string().max(160).nullable(),
  attempted: z6.boolean()
});
var PlanParseInputSchema = z6.object({ documentId: z6.string().uuid() });
var PlanParseJobSchema = z6.object({
  jobId: z6.string().uuid(),
  status: JobStatusSchema,
  /** Present on `succeeded`. An empty array is a success, not a failure. */
  phases: z6.array(ParsedPhaseSchema).optional(),
  /**
   * Present on `succeeded`. Step 1 pre-fills the basics from this; step 2 has a
   * project already and ignores it.
   */
  suggestion: ProjectSuggestionSchema.optional(),
  error: z6.string().optional(),
  /** The document the phases came from, for the "from <filename>" hint. */
  sourceDocumentId: z6.string().uuid().optional()
});
var JurisdictionSchema = z6.object({
  code: CountryCodeSchema,
  name: z6.string(),
  /** role → doc type ids. */
  roleProfiles: z6.record(z6.string(), z6.array(z6.string())),
  /** doc type id → local label. */
  docLabels: z6.record(z6.string(), z6.string()),
  docIssuingBodies: z6.record(z6.string(), z6.string())
});
var ComplianceFieldTypeSchema = z6.enum(["text", "number", "select"]);
var COMPLIANCE_FIELD_TYPE_LABELS = {
  text: "Text",
  number: "Number",
  select: "Choose from a list"
};
var ComplianceRequirementSchema = z6.object({
  id: z6.string().uuid(),
  label: z6.string(),
  dataType: ComplianceFieldTypeSchema.nullable(),
  selectOptions: z6.array(z6.string()).nullable()
});
var RequirementInputSchema = z6.object({
  id: z6.string().uuid().optional(),
  label: z6.string().trim().min(1, "Label is required").max(120),
  dataType: ComplianceFieldTypeSchema,
  selectOptions: z6.array(z6.string().trim().min(1).max(80)).max(40).optional()
}).superRefine((value, ctx) => {
  if (value.dataType === "select" && (value.selectOptions ?? []).length === 0) {
    ctx.addIssue({
      code: z6.ZodIssueCode.custom,
      path: ["selectOptions"],
      message: "A select field needs at least one option"
    });
  }
});
var RequirementsResponseSchema = z6.object({
  requirements: z6.array(ComplianceRequirementSchema)
});
var RequirementsInputSchema = z6.object({
  requirements: z6.array(RequirementInputSchema).max(40)
});
var ConnectorStatusSchema = z6.object({
  connected: z6.boolean(),
  configured: z6.boolean(),
  accountEmail: z6.string().nullable(),
  /** The browser needs this for the Picker; it is a public API key, not a secret. */
  pickerApiKey: z6.string().nullable(),
  clientId: z6.string().nullable()
});
var ConnectorAuthUrlSchema = z6.object({
  url: z6.string().url(),
  state: z6.string()
});
var ConnectorCallbackInputSchema = z6.object({
  code: z6.string().min(1),
  state: z6.string().min(1)
});
var projectsContract = c3.router({
  list: {
    method: "GET",
    path: "/projects",
    responses: { 200: ProjectsResponseSchema, 401: ErrorSchema },
    summary: "Every project in the tenant, newest first \u2014 archived included"
  },
  get: {
    method: "GET",
    path: "/projects/:id",
    responses: { 200: ProjectResponseSchema, 401: ErrorSchema, 404: ErrorSchema },
    summary: "One project. An unknown or foreign id is 404, never 403"
  },
  create: {
    method: "POST",
    path: "/projects",
    body: ProjectBasicsSchema,
    responses: {
      201: ProjectResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema
    },
    summary: "Create a project in `setup-incomplete`"
  },
  patch: {
    method: "PATCH",
    path: "/projects/:id",
    body: ProjectBasicsPatchSchema,
    responses: {
      200: ProjectResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Update project basics \u2014 the review step's in-place edits, and editing a live project after setup. Status is never touched."
  },
  activate: {
    method: "POST",
    path: "/projects/:id/activate",
    body: z6.object({}),
    responses: {
      200: ProjectResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Complete setup: status `active`, `setupCompletedAt` set"
  },
  archive: {
    method: "POST",
    path: "/projects/:id/archive",
    body: z6.object({}),
    responses: {
      200: ProjectResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Retire a finished site. Idempotent"
  },
  unarchive: {
    method: "POST",
    path: "/projects/:id/unarchive",
    body: z6.object({}),
    responses: {
      200: ProjectResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Restore to `active` if setup ever completed, else `setup-incomplete`"
  },
  setSetupStep: {
    method: "PUT",
    path: "/projects/:id/setup-step",
    body: z6.object({ step: SetupStepSchema }),
    responses: {
      200: ProjectResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Advance the resume cursor. Monotonic \u2014 Back never loses progress"
  },
  listPhases: {
    method: "GET",
    path: "/projects/:id/phases",
    responses: { 200: PhasesResponseSchema, 401: ErrorSchema, 404: ErrorSchema },
    summary: "Phases in display order"
  },
  replacePhases: {
    method: "PUT",
    path: "/projects/:id/phases",
    body: PhasesInputSchema,
    responses: {
      200: PhasesResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Bulk replace every phase, in one transaction"
  },
  listDocuments: {
    method: "GET",
    path: "/projects/:id/documents",
    responses: { 200: DocumentsResponseSchema, 401: ErrorSchema, 404: ErrorSchema },
    summary: "Stored documents (never `pending` or `failed` rows)"
  },
  getDocument: {
    method: "GET",
    path: "/projects/:id/documents/:docId",
    responses: { 200: DocumentResponseSchema, 401: ErrorSchema, 404: ErrorSchema },
    summary: "One document. The viewer's header, and what it polls while parsing"
  },
  listSegments: {
    method: "GET",
    path: "/projects/:id/documents/:docId/segments",
    responses: {
      200: DocumentSegmentsResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    // The citable surfaces, in reading order — a PDF page, a rendered DOCX page,
    // a sheet. `document-ai/0001` §5 listed these schemas as declared with "no
    // route returns them"; this is that route. It is what lets the viewer show
    // a DOCX or an XLSX at all, neither of which a browser can render, and what
    // puts "page 41" next to the text that is on page 41.
    summary: "What the Brain read, one citable surface at a time"
  },
  listAssets: {
    method: "GET",
    path: "/projects/:id/documents/:docId/assets",
    responses: {
      200: DocumentAssetsResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    // Metadata only. The bytes come from `GET .../assets/:assetId/content`,
    // which answers 302 to a presigned URL and is off-contract for the same
    // reason `download` is.
    summary: "Figures and scans lifted out of the document, with descriptions"
  },
  uploadUrl: {
    method: "POST",
    path: "/projects/:id/documents/upload-url",
    body: UploadUrlInputSchema,
    responses: {
      201: UploadUrlResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      413: ErrorSchema
    },
    summary: "Reserve a document row and presign a direct-to-S3 PUT"
  },
  completeUpload: {
    method: "POST",
    path: "/projects/:id/documents/:docId/complete",
    body: z6.object({}),
    responses: {
      200: DocumentResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema,
      // The bytes that landed are over the limit. The object and the row are
      // both deleted (2026-09-25), so a retry of it finds nothing: a 404.
      413: ErrorSchema
    },
    summary: "Verify the object landed, then queue the quick pass: the card, then the full read"
  },
  reopenUpload: {
    method: "POST",
    path: "/projects/:id/documents/:docId/upload-url",
    body: z6.object({}),
    responses: {
      201: UploadUrlResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      // Its bytes are already stored: there is nothing to upload again.
      409: ErrorSchema
    },
    // A retry keeps its row (`document-ai/0019` §8, 2026-09-25). Asking for a
    // new row each time left the old one behind, pending for good, and counted
    // as a file. The same row and the same key get a fresh URL. A row whose
    // import has closed arrives on its own, as a retried file always has.
    summary: "Presign the same row again, for a retry of an upload that did not finish"
  },
  openImport: {
    method: "POST",
    path: "/projects/:id/imports",
    body: OpenImportInputSchema,
    responses: {
      201: ImportResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Start a bundle: the files uploaded with its id are organised together"
  },
  closeImport: {
    method: "POST",
    path: "/projects/:id/imports/:importId/close",
    body: z6.object({}),
    responses: {
      200: ImportResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    // Idempotent: closing a closed import answers with it as it stands. A client
    // that retries after a network error must not be told it did something wrong.
    summary: "Say that is every file; the bundle is organised once they are read"
  },
  reviewImport: {
    method: "POST",
    path: "/projects/:id/imports/:importId/review",
    body: z6.object({}),
    responses: {
      200: ImportResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      // Still being read: there is nothing yet to have reviewed.
      409: ErrorSchema
    },
    // Idempotent, like the close: an import already reviewed answers with it as
    // it stands, so a client retrying after a lost reply is not told it did
    // something wrong. This is the only way a bundle with files in it reaches
    // `done` (`document-ai/0017` §9, answered 2026-09-22).
    summary: "A person has been through the bundle; mark it reviewed"
  },
  organiseProject: {
    method: "POST",
    path: "/projects/:id/organise",
    body: z6.object({}),
    responses: {
      202: ProjectOrganiseResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    // "Organise now" on the Brain screen (2026-09-26, RET-04). Idempotent: a
    // run already queued is the answer, not a second one. Starts nothing while
    // an import of the project is being organised, or when every file is done.
    summary: "Organise the files that no import has organised yet"
  },
  getImport: {
    method: "GET",
    path: "/projects/:id/imports/:importId",
    responses: {
      200: ImportResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "One bundle, with how far it has got"
  },
  listImports: {
    method: "GET",
    path: "/projects/:id/imports",
    responses: {
      200: ImportsResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Every bundle for the project, newest first"
  },
  importFromDrive: {
    method: "POST",
    path: "/projects/:id/documents/import/google-drive",
    body: DriveImportInputSchema,
    responses: {
      202: DriveImportResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Server-side fetch of picked Drive files into our own storage"
  },
  setDocumentStatus: {
    method: "PUT",
    path: "/projects/:id/documents/:docId/status",
    body: SetDocumentStatusInputSchema,
    responses: {
      200: DocumentResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      // A file whose bytes never landed: there is nothing to keep.
      409: ErrorSchema
    },
    summary: "A person says what a file is. Keeping a flagged file makes it this project\u2019s again, and the check never re-judges it; removing one is the delete route"
  },
  setDocumentStatuses: {
    method: "PUT",
    path: "/projects/:id/documents/statuses",
    body: SetDocumentStatusesInputSchema,
    responses: {
      200: SetDocumentStatusesResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      // Any id that is not a stored file of this project: nothing is changed.
      404: ErrorSchema,
      409: ErrorSchema
    },
    summary: "A person says what a group of files is, as one answer per file: keep every file in a group, or hide every file the check asked about"
  },
  setDocumentCategory: {
    method: "PUT",
    path: "/projects/:id/documents/:docId/category",
    body: SetDocumentCategoryInputSchema,
    responses: {
      200: DocumentResponseSchema,
      // A category the tenant's list does not have, in one sentence.
      400: ErrorSchema,
      401: ErrorSchema,
      // A file of another project, or of another tenant.
      404: ErrorSchema,
      // A file whose bytes never landed: there is nothing to sort.
      409: ErrorSchema
    },
    summary: "A person moves a file to one of the tenant's categories, an empty one included. A card written later never moves it back"
  },
  placeDocument: {
    method: "PUT",
    path: "/projects/:id/documents/:docId/family",
    body: PlaceDocumentInputSchema,
    responses: {
      200: DocumentResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema
    },
    summary: "A person places a file among its versions, or answers the model's question about it. A later import never moves it; a loop is a 409"
  },
  listChanges: {
    method: "GET",
    path: "/projects/:id/changes",
    query: z6.object({
      /** One bundle's changes, for the review screen. Omit for the project's. */
      importId: z6.string().uuid().optional()
    }),
    responses: {
      200: ChangesResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "What has been done to this project's organisation, newest first"
  },
  undoChange: {
    method: "POST",
    path: "/projects/:id/changes/:changeId/undo",
    body: c3.noBody(),
    responses: {
      200: UndoResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema
    },
    summary: "Undo a placement. A new entry in the change log; a 409 when the files have changed since, or it was already undone"
  },
  listLinks: {
    method: "GET",
    path: "/projects/:id/links",
    query: z6.object({
      /** One file's links, in and out, and its containment. */
      documentId: z6.string().uuid().optional(),
      /** The links one bundle's organising made, for the review screen. */
      importId: z6.string().uuid().optional(),
      /** Live, or waiting for a person. Omit for both. */
      state: z6.enum(["live", "asked"]).optional()
    }),
    responses: {
      200: LinksResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "The links a person sees: live, and those waiting for an answer"
  },
  setLinkState: {
    method: "PATCH",
    path: "/projects/:id/links/:linkId",
    body: SetLinkStateInputSchema,
    responses: {
      200: LinkResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      // A link that was removed stays removed.
      409: ErrorSchema
    },
    summary: "A person answers a link the model was unsure of, or removes one. A removed link keeps its row and is never made again"
  },
  getBrief: {
    method: "GET",
    path: "/projects/:id/brief",
    responses: { 200: ProjectBriefResponseSchema, 401: ErrorSchema, 404: ErrorSchema },
    summary: "The project's brief, and who wrote it and when"
  },
  deleteDocument: {
    method: "DELETE",
    path: "/projects/:id/documents/:docId",
    // `noBody()`, not `z.object({})` — the latter makes a bodyless DELETE a 400,
    // so `curl -X DELETE` fails even though the browser client (which always
    // sends `{}`) works. A DELETE identified entirely by its path should not
    // require a JSON body.
    body: c3.noBody(),
    // 409: a file of the project's linked SharePoint folder, which is deleted
    // in SharePoint (`plans/sharepoint-sync.md`, question 19), or an email of
    // one of its Outlook folders, which is removed in Outlook
    // (`plans/outlook-sync.md`).
    responses: { 204: c3.noBody(), 401: ErrorSchema, 404: ErrorSchema, 409: ErrorSchema },
    summary: "Delete the row and the object (bucket versioning retains the bytes)"
  },
  startPlanParse: {
    method: "POST",
    path: "/projects/:id/plan-parse",
    body: PlanParseInputSchema,
    responses: {
      202: PlanParseJobSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      // A file whose bytes never landed: there is nothing to read.
      409: ErrorSchema
    },
    summary: "Parse a stored programme into phase proposals"
  },
  planParseStatus: {
    method: "GET",
    path: "/projects/:id/plan-parse/:jobId",
    responses: { 200: PlanParseJobSchema, 401: ErrorSchema, 404: ErrorSchema },
    summary: "Poll a parse job"
  }
});
var jurisdictionsContract = c3.router({
  get: {
    method: "GET",
    path: "/jurisdictions/:code",
    responses: {
      200: z6.object({ jurisdiction: JurisdictionSchema.nullable() }),
      400: ErrorSchema,
      401: ErrorSchema
    },
    summary: "Role profiles and local doc labels. Null when unconfigured"
  }
});
var complianceContract = c3.router({
  list: {
    method: "GET",
    path: "/compliance/requirements",
    responses: { 200: RequirementsResponseSchema, 401: ErrorSchema },
    summary: "Every requirement, document- and info-kind alike"
  },
  replaceInfo: {
    method: "PUT",
    path: "/compliance/requirements",
    body: RequirementsInputSchema,
    responses: { 200: RequirementsResponseSchema, 400: ErrorSchema, 401: ErrorSchema },
    summary: "Bulk replace the `info` rows. Document-kind rows are untouched"
  }
});
var connectorsContract = c3.router({
  googleDriveStatus: {
    method: "GET",
    path: "/connectors/google-drive",
    responses: { 200: ConnectorStatusSchema, 401: ErrorSchema },
    summary: "Whether Drive is configured for this deployment and linked by this user"
  },
  googleDriveAuthUrl: {
    method: "POST",
    path: "/connectors/google-drive/auth-url",
    body: z6.object({}),
    responses: { 200: ConnectorAuthUrlSchema, 401: ErrorSchema, 503: ErrorSchema },
    summary: "Begin the consent flow. `state` is signed, single-use and user-bound"
  },
  googleDriveCallback: {
    method: "POST",
    path: "/connectors/google-drive/callback",
    body: ConnectorCallbackInputSchema,
    responses: {
      200: z6.object({ connected: z6.literal(true), accountEmail: z6.string().nullable() }),
      400: ErrorSchema,
      401: ErrorSchema,
      503: ErrorSchema
    },
    summary: "Exchange the code for tokens and store them encrypted"
  },
  googleDriveDisconnect: {
    method: "DELETE",
    path: "/connectors/google-drive",
    // See `deleteDocument` — a bodyless DELETE must not be a 400.
    body: c3.noBody(),
    responses: { 204: c3.noBody(), 401: ErrorSchema },
    summary: "Revoke at Google and delete the row"
  }
});

// src/brain.ts
var c4 = initContract4();
var KnowledgeSourceTypeSchema = z7.enum([
  "manual",
  "journal",
  "decision",
  "project-doc",
  "upload",
  "photo",
  "other"
]);
var KNOWLEDGE_SOURCE_LABELS = {
  manual: "Manual",
  journal: "Journal",
  decision: "Decision",
  "project-doc": "Document",
  upload: "Note",
  photo: "Photo",
  other: "Other"
};
var KnowledgeEntrySchema = z7.object({
  id: z7.string().uuid(),
  projectId: z7.string().uuid(),
  title: z7.string(),
  body: z7.string(),
  sourceType: KnowledgeSourceTypeSchema,
  sourceRef: z7.string().nullable(),
  tags: z7.array(z7.string()),
  /** Pre-rendered by `formatCitation()` at write time; never re-rendered here. */
  citation: z7.string().nullable(),
  /**
   * Where the text is in its file: the page, heading, sheet and rows, or
   * figure (2026-09-27). `citation` is this rendered as one line; the screen
   * needs the parts to open the file at the page. Null when the row has none,
   * or a stored one that no longer checks.
   */
  locator: CitationLocatorSchema.nullable(),
  /** The document a `project-doc` row came from, so the client can link to it. */
  documentId: z7.string().uuid().nullable(),
  /**
   * The journal day a `photo` row came from, so the client can *show* it.
   *
   * Projected for the same reason `documentId` is, and with the same limit: it
   * is a row id the caller already has routes for, never a storage key. A photo
   * entry's `sourceRef` is the photograph's id, and the existing content route
   * needs the day as well — without this the Brain can name a picture it has no
   * way to display, which is what it did.
   */
  journalEntryId: z7.string().uuid().nullable(),
  createdAt: z7.string(),
  updatedAt: z7.string()
});
var BrainDocumentItemSchema = z7.object({
  kind: z7.literal("document"),
  /** The `project_documents` id — what the viewer and download routes take. */
  id: z7.string().uuid(),
  projectId: z7.string().uuid(),
  filename: z7.string(),
  mimeType: z7.string(),
  sizeBytes: z7.number().int().nonnegative(),
  /** The card's title and kind of document. Null until the card is written. */
  title: z7.string().nullable(),
  docType: z7.string().nullable(),
  /**
   * The tenant's category it lives in, by key and name (`document-ai/0032`).
   * Null when it is not sorted yet. An "Also relevant here" group says it.
   */
  category: z7.object({ key: DocumentCategoryKeySchema, name: z7.string() }).nullable(),
  /**
   * Its role among its versions (`document-ai/0016`). An identical copy is
   * never listed: it is folded into its original.
   */
  role: FamilyRoleSchema,
  /** The file it defers to, by name: "Superseded by …". Null for a role that answers. */
  relatedName: z7.string().nullable(),
  /** The model asked a person where it belongs, and nobody has answered yet. */
  question: z7.boolean(),
  /**
   * What the file is (`document-ai/0013`). A hidden file is not in the ordinary
   * lists or the search at all; it is listed only by the `status` filter, which
   * is how a person finds what is waiting on them.
   */
  status: DocumentStatusSchema,
  pageCount: z7.number().int().nonnegative().nullable(),
  source: DocumentSourceSchema,
  /**
   * When the file left its source, a SharePoint folder or Outlook folders, as
   * `ProjectDocument.removedAtSource` says it (question 15 of the SharePoint
   * plan): kept, and not used in answers. Null for a file there, or not synced.
   */
  removedAtSource: z7.string().datetime().nullable(),
  /**
   * How far the out-of-band parse has got, or null when no job was recorded.
   *
   * A document is listed from the moment its bytes are verified — never gated
   * on the parse — so this is what lets the row say "Reading…" instead of
   * appearing minutes later or, worse, appearing complete when it is not.
   */
  processingStatus: JobStatusSchema.nullable(),
  /**
   * What of the file could not be read, once every try has run out, as
   * `ProjectDocument.readFailure` says it (2026-09-26). Null for a file read
   * in full, and while a try is still to come.
   */
  readFailure: z7.string().nullable(),
  /** The pictures with no reading, as `ProjectDocument.picturesNotRead` counts them (2026-09-26). */
  picturesNotRead: z7.number().int().nonnegative().nullable(),
  /** What an organise step has not done for it yet, as `ProjectDocument.organiseGaps` (2026-09-26). */
  organiseGaps: z7.array(OrganiseGapSchema),
  /**
   * Where the search matched, when it matched inside the file rather than on
   * its name. All three are null for an unfiltered listing.
   */
  snippet: z7.string().nullable(),
  citation: z7.string().nullable(),
  /** Drives `?page=` on the viewer link. Null when the locator had no page. */
  matchedPage: z7.number().int().min(1).nullable(),
  createdAt: z7.string()
});
var BrainPhotoRefSchema = z7.object({
  id: z7.string().uuid(),
  /** The journal entry it is filed against — the link target. */
  entryId: z7.string().uuid(),
  date: IsoDateSchema
});
var BrainEntryItemSchema = z7.object({
  kind: z7.literal("entry"),
  id: z7.string().uuid(),
  projectId: z7.string().uuid(),
  title: z7.string(),
  snippet: z7.string(),
  /** Never `project-doc` — those are folded into their document. */
  sourceType: KnowledgeSourceTypeSchema,
  citation: z7.string().nullable(),
  tags: z7.array(z7.string()),
  /** Present only on `photo` rows. */
  photo: BrainPhotoRefSchema.nullable(),
  createdAt: z7.string()
});
var BrainItemSchema = z7.discriminatedUnion("kind", [
  BrainDocumentItemSchema,
  BrainEntryItemSchema
]);
var BrainCategoryShelfSchema = z7.object({
  key: DocumentCategoryKeySchema,
  name: z7.string(),
  description: z7.string(),
  /**
   * The files whose main category it is, listed with `category=<key>`. Each
   * file is on one shelf, so these and `notSorted` add up to the files.
   */
  documents: z7.number().int().nonnegative(),
  /**
   * The files that live in another category and list this one: its "Also
   * relevant here" group, listed with `relevant=<key>`. Not in `documents`.
   */
  alsoRelevant: z7.number().int().nonnegative()
});
var KnowledgeSummarySchema = z7.object({
  total: z7.number().int(),
  /** Count per source type. Every key present, zeros included. */
  bySourceType: z7.record(KnowledgeSourceTypeSchema, z7.number().int()),
  /**
   * Every one of the tenant's categories, in its order, zeros included
   * (`document-ai/0032`). Counted over the same files as `total`'s
   * documents: stored, and not an identical copy.
   */
  categories: z7.array(BrainCategoryShelfSchema),
  /**
   * The files with no main category: no card has chosen one and no person
   * has moved them, or their key has left the tenant's list. The "Not sorted
   * yet" shelf, listed with `sorted=no`. Not an organise gap: no organise
   * step sorts a file.
   */
  notSorted: z7.number().int().nonnegative(),
  /**
   * The files the agent may read that an organise step has not done yet: not
   * checked, not placed among their versions, or with no links
   * (2026-09-26, tracker RET-12). The "Not organised yet" shelf, listed with
   * `organised=no`. Until then the shelf counted only the files in no area,
   * and in the Austrian set 21 of 21 files had no links while it counted none.
   */
  unorganisedDocuments: z7.number().int().nonnegative(),
  /**
   * Files removed at their source, SharePoint or Outlook, and what was
   * unpacked from them: kept, out of answers, and listed with `removed=yes`
   * (2026-10-18). A file hidden for another reason is not counted.
   */
  removedAtSource: z7.number().int().nonnegative(),
  /**
   * Files with a question waiting for a person: the "Needs a decision" shelf,
   * listed with `decision=open` (decided 2026-09-22).
   *
   * Since 2026-09-26 (tracker RET-04) it counts every kind of question, not
   * only where a file sits among its versions: whether it belongs to this
   * project (the check's question) and a link from it the model was not sure
   * of. A run over the project with no import writes these too, and no import
   * review shows them. Default, 2026-09-26, the owner can change it.
   */
  needsDecision: z7.number().int().nonnegative(),
  /**
   * Stored files folded into an original because their bytes are the same
   * (`families/copies.ts`). None of them is counted in `total`. The Sources tab
   * says how many (2026-10-03, the owner).
   */
  duplicates: z7.number().int().nonnegative(),
  /**
   * The files no import has organised yet, and where the project's own
   * organise run is (2026-09-26, RET-04): what "Organise now" would take.
   */
  organise: ProjectOrganiseSchema,
  /**
   * Files the agent may not read, counted per status (`data-model/0013` §8:
   * "hidden files appear only as counts"). `total` is every one of them; the
   * counts say what they are, so "63 files hidden: 61 templates" reads as a
   * fact rather than a mystery.
   */
  hidden: z7.object({
    total: z7.number().int().nonnegative(),
    byStatus: z7.record(FileStatusSchema, z7.number().int().nonnegative()),
    /**
     * The files the check asks about, per status it would have set
     * (2026-09-26, tracker HID-11). They are not hidden, so `total` does not
     * count them. Each hidden status has a shelf that asks, and it lists these
     * first, then the hidden ones: `byStatus` and `asked` together are its
     * count.
     */
    asked: z7.record(FileStatusSchema, z7.number().int().nonnegative())
  })
});
var KnowledgeNoteInputSchema = z7.object({
  title: z7.string().trim().min(1).max(300),
  body: z7.string().trim().min(1).max(5e4),
  tags: z7.array(z7.string().trim().min(1).max(60)).max(20).optional()
});
var brainContract = c4.router({
  summary: {
    method: "GET",
    path: "/projects/:id/brain",
    responses: {
      200: KnowledgeSummarySchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "What this site's Brain holds, counted by source"
  },
  list: {
    method: "GET",
    path: "/projects/:id/brain/items",
    // The first `query` object in the contract. `limit` and `offset` arrive as
    // strings on the wire, so they MUST be coerced — without it every call
    // including the default one fails response validation under
    // `strictStatusCodes` and the screen just renders empty with no clue why.
    query: z7.object({
      q: z7.string().trim().max(200).optional(),
      sourceType: KnowledgeSourceTypeSchema.optional(),
      /**
       * Only the files whose main category this is: a category shelf
       * (`document-ai/0032`). Implies documents. A key the tenant's list does
       * not have is a 400.
       */
      // Any string: a key in the wrong form gets the API's one sentence, as a
      // key the tenant's list lacks does (`architecture/0031`).
      category: z7.string().trim().min(1).max(80).optional(),
      /**
       * Only the files that live in another category and list this one: the
       * shelf's "Also relevant here" group. Implies documents.
       */
      relevant: z7.string().trim().min(1).max(80).optional(),
      /** `no`: only the files with no main category, the "Not sorted yet" shelf. Implies documents. */
      sorted: z7.literal("no").optional(),
      /** Only the files with a question waiting: a placement, a hide the check doubts, or a link (2026-09-26). Implies documents. */
      decision: z7.literal("open").optional(),
      /**
       * Only the files with this status. Implies documents, and it is the one
       * way a hidden file is listed: a person has to be able to find what is
       * waiting on them (decided 2026-09-22). For a hidden status, the files
       * the check asks about with that status come first (2026-09-26, HID-11).
       */
      status: FileStatusSchema.optional(),
      /**
       * `no`: only the files an organise step has not done yet, the "Not
       * organised yet" shelf (2026-09-26, RET-12). Implies documents.
       */
      organised: z7.literal("no").optional(),
      /**
       * `yes`: only the files removed at their source, SharePoint or Outlook,
       * and what was unpacked from them: the "Removed at source" shelf
       * (2026-10-18, QA finding 9, the owner's decision). They are kept and out
       * of answers. Implies documents. It reads the base table, as a status
       * filter does, and still never lists a file hidden for another reason.
       */
      removed: z7.literal("yes").optional(),
      limit: z7.coerce.number().int().min(1).max(100).default(50),
      offset: z7.coerce.number().int().min(0).default(0)
    }),
    responses: {
      200: z7.object({
        items: z7.array(BrainItemSchema),
        total: z7.number().int()
      }),
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    // `q` is a case-insensitive match over filenames, titles and bodies — which
    // for a document means its chunks, so a hit on page 41 surfaces the file
    // and says page 41. This is browse and filter, NOT retrieval: semantic
    // search needs embeddings over `document_chunks`, deliberately not in the
    // schema yet. The `limit` cap is what protects this, not an index.
    summary: "Browse and filter: files and entries, never chunks"
  },
  get: {
    method: "GET",
    path: "/projects/:id/brain/entries/:entryId",
    responses: {
      200: z7.object({ entry: KnowledgeEntrySchema }),
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "One entry, with its full body"
  },
  create: {
    method: "POST",
    path: "/projects/:id/brain/entries",
    body: KnowledgeNoteInputSchema,
    responses: {
      201: z7.object({ entry: KnowledgeEntrySchema }),
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Add a hand-written note to this site's Brain"
  },
  remove: {
    method: "DELETE",
    path: "/projects/:id/brain/entries/:entryId",
    body: c4.noBody(),
    responses: {
      204: c4.noBody(),
      401: ErrorSchema,
      404: ErrorSchema,
      // Only hand-written notes are deletable here. A `project-doc` row is
      // owned by the ingest path and would be re-created from `document_chunks`
      // the next time its document is read — so a "successful" delete would
      // quietly undo itself. 409 says the honest thing: delete the document
      // instead.
      409: ErrorSchema
    },
    summary: "Delete a note. Only `upload` entries can be deleted here"
  }
});

// src/chat.ts
import { initContract as initContract6 } from "@ts-rest/core";
import { z as z10 } from "zod";

// src/decisions.ts
import { initContract as initContract5 } from "@ts-rest/core";
import { z as z8 } from "zod";
var c5 = initContract5();
var DecisionSourceSchema = z8.enum([
  "email",
  "whatsapp",
  "journal",
  "vendor-message",
  "brain"
]);
var DECISION_SOURCE_LABELS = {
  email: "Email",
  whatsapp: "WhatsApp",
  journal: "Site journal",
  "vendor-message": "Vendor message",
  brain: "Site Brain"
};
var DecisionStateSchema = z8.enum(["accepted", "accepted-with-changes"]);
var DECISION_STATE_LABELS = {
  accepted: "Accepted",
  "accepted-with-changes": "Accepted with changes"
};
var DecisionSchema = z8.object({
  id: z8.string().uuid(),
  projectId: z8.string().uuid(),
  /**
   * What was decided, in one line. When a person changed the decision on
   * acceptance, it states the decision as changed (`data-model/0014`).
   */
  title: z8.string(),
  /**
   * **The decision itself, as the Brain wrote it, verbatim.**
   *
   * Never rewritten by a human — see `modificationNote`. A person who disagrees
   * adds to the record rather than replacing it. The Brain drafts this with
   * their change, so it states the decision as changed; what the answer
   * proposed stays in the stored exchange (`data-model/0014` §7).
   */
  summary: z8.string(),
  rationale: z8.string(),
  source: DecisionSourceSchema,
  sourceLabel: z8.string(),
  date: IsoDateSchema,
  tags: z8.array(z8.string()),
  /** The material it was taken from — for a Brain decision, the exchange. */
  fullThread: z8.string(),
  /** The question it was elevated from, when it came from the Brain. */
  questionId: z8.string().uuid().nullable(),
  /** What the person changed, in their words. Null when they took it as-is. */
  modificationNote: z8.string().nullable(),
  acceptedAt: z8.string().nullable()
});
function decisionState(decision) {
  return decision.modificationNote ? "accepted-with-changes" : "accepted";
}
var DecisionElevateInputSchema = z8.object({
  /**
   * What the person changed, when they used *Modify & accept*.
   *
   * Absent or blank means they accepted it as proposed. Not a rewrite of the
   * statement: it is recorded beside it and attributed. The title and the
   * statement are drafted with it (`data-model/0014` §7).
   */
  modificationNote: z8.string().trim().min(1).max(2e3).optional()
});
var decisionsContract = c5.router({
  /**
   * Every decision on this project, newest first.
   *
   * Unpaged, deliberately. A project accumulates decisions at the rate people
   * actually decide things — the seed's busiest site has ten — and a pager over
   * a list that short is furniture. `MAX_DECISIONS` is where that assumption
   * stops being true and it says so out loud rather than truncating in silence.
   */
  list: {
    method: "GET",
    path: "/projects/:id/decisions",
    query: z8.object({
      /**
       * Substring match over the title, the summary, the rationale and the
       * tags — "search decisions, vendors, tags" in the design.
       *
       * Not over `fullThread`: that holds the whole exchange an elevated
       * decision came from, so matching it would return decisions whose *only*
       * connection to the term is a passing mention in a transcript, ranked
       * identically to one that is actually about it.
       */
      q: z8.string().trim().max(200).optional()
    }),
    responses: {
      200: z8.object({
        decisions: z8.array(DecisionSchema),
        /** Every decision on the project, before `q` narrowed it. */
        total: z8.number().int().min(0)
      }),
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Decisions recorded on this project, newest first"
  }
});
var MAX_DECISIONS = 500;

// src/answer-steps.ts
import { z as z9 } from "zod";
var AGENT_TOOL_NAMES = [
  "list_files",
  "search",
  "open_file",
  "versions",
  "links",
  "metadata",
  "crew",
  "findings",
  "kept_looks",
  "look",
  "rows_for_code",
  "code_execution"
];
var AgentToolNameSchema = z9.enum(AGENT_TOOL_NAMES);
var AnswerStepKindSchema = z9.enum([
  "context",
  "turn",
  "tool",
  "note",
  "limit",
  "wrap-up",
  "review"
]);
var AnswerStepOutcomeSchema = z9.enum([
  "running",
  "ok",
  "empty",
  "repeat",
  "step-limit",
  "cancelled",
  "timeout",
  "error"
]);
var AnswerEndedBySchema = z9.enum([
  "answer",
  "step-limit",
  "repeating",
  "context",
  "time",
  "too-long",
  "max-tokens",
  "stopped",
  "restart",
  "refusal",
  "error"
]);
var LookTargetKindSchema = z9.enum(["drawing", "page", "picture", "photo"]);
var count = z9.number().int().min(0);
var isoDay = z9.string().regex(/^\d{4}-\d{2}-\d{2}$/);
var BoxSchema = z9.object({
  x: z9.number().min(0).max(1),
  y: z9.number().min(0).max(1),
  w: z9.number().min(0).max(1),
  h: z9.number().min(0).max(1)
});
var LookTargetSchema = z9.object({
  kind: LookTargetKindSchema,
  documentId: z9.string().uuid().nullable(),
  /** The file's title as written, or its name. Null for a journal photo. */
  title: z9.string().nullable(),
  /** 1-based page, for a page or a picture on a page. */
  page: z9.number().int().min(1).nullable(),
  journalPhotoId: z9.string().uuid().nullable(),
  /** The day of a journal photo. */
  date: isoDay.nullable()
});
var ContextDetailSchema = z9.object({
  tool: z9.literal("context"),
  /** Visible files, identical copies not counted. */
  files: count,
  /**
   * The tenant's categories that hold a visible file (2026-10-03,
   * `document-ai/0032`). It counted the project's own areas until then; a
   * stored detail of that shape no longer parses, and its step says its
   * plain line.
   */
  categories: count,
  journalEntries: count,
  journalNotes: count,
  photos: count,
  decisions: count,
  /**
   * The days the journal in the block covers, oldest first (2026-10-18): the
   * review is told them, so a "nothing on that day" can be checked against
   * what the agent had (`document-ai/0030` §2.8). A detail stored before
   * then has none.
   */
  journalDays: z9.array(isoDay).default([]),
  /** Journal entries, notes and decisions left out because they did not fit, oldest first (§1.5). */
  leftOut: z9.object({
    journalEntries: count,
    journalNotes: count,
    decisions: count,
    /** The first day kept: what was left out is older. */
    before: isoDay.nullable()
  }).nullable(),
  /** Note dictations not filed into a note, which cannot be cited and are not in it (Q9). */
  unfiledDictations: count,
  /** The size of block 2, in characters. */
  chars: count,
  /**
   * The reply language the agent was given by name (2026-09-27,
   * `document-ai/0021` §13.6): the naming call's answer, or English when it
   * could not tell. Null when no language was named: the call failed, timed
   * out, or no model is configured. Optional because block 2's builder writes
   * the rest of this detail and knows nothing of it.
   */
  replyLanguage: z9.string().nullable().optional()
});
var ListFilesDetailSchema = z9.object({
  tool: z9.literal("list_files"),
  /**
   * The category filtered on (`document-ai/0032`): its name in the tenant's
   * list, or what was asked for when no category matched; null for no filter.
   */
  category: z9.string().nullable(),
  folder: z9.string().nullable(),
  /** Files on this page, and in the whole list. */
  listed: count,
  total: count,
  documentIds: z9.array(z9.string().uuid())
});
var SearchDetailSchema = z9.object({
  tool: z9.literal("search"),
  /** As the model wrote them, in its order. */
  terms: z9.array(z9.string()),
  scope: z9.enum(["all", "files", "category"]),
  /** The category's name in the tenant's list, for `category` (`document-ai/0032`). */
  scopeName: z9.string().nullable(),
  /** How many files the scope named, for `files`. */
  scopeFiles: count.nullable(),
  hits: count,
  filesHit: count,
  filesSearched: count,
  /** Whether the files searched were every visible file (rule C1, §1.9). */
  everyVisibleFile: z9.boolean(),
  /** Hits SQL counted that no snippet could be cut for (G20). Counted, never dropped. */
  unplaced: count,
  /** This page of results: each file with its hits. */
  results: z9.array(
    z9.object({ documentId: z9.string().uuid(), title: z9.string(), hits: count })
  ),
  /** 1-based page of the results, and how many pages there are. */
  page: z9.number().int().min(1),
  pages: z9.number().int().min(1)
});
var OpenFileDetailSchema = z9.object({
  tool: z9.literal("open_file"),
  documentId: z9.string().uuid(),
  title: z9.string(),
  part: z9.enum(["size", "pages", "passages", "rows", "reading"]),
  /** 1-based, inclusive. Null for `size`. */
  from: z9.number().int().min(1).nullable(),
  to: z9.number().int().min(1).nullable(),
  /** How many of that unit the file has. Null when not known. */
  total: count.nullable(),
  /** Each passage returned, with its display number once the answer cites it. */
  passages: z9.array(
    z9.object({
      n: z9.number().int().min(1).nullable(),
      citation: z9.string()
    })
  ),
  /** What the file has that this call did not show, in the tool's words. */
  notShown: z9.array(z9.string()),
  /**
   * For part rows (2026-09-27): the sheet read, as written; its place among
   * the file's sheets with rows, from 1, and how many there are, numbered as
   * `rows_for_code` numbers them; and which of the sheet's passages this call
   * showed, from 1, of how many. `total` above is that sheet's last row, not
   * the file's, so the coverage record keeps each sheet apart and counts the
   * file whole only when every sheet is. Optional: a step stored before them
   * has none.
   */
  sheet: z9.string().nullable().optional(),
  sheetIndex: count.nullable().optional(),
  sheetsInFile: count.nullable().optional(),
  sheetPassages: z9.object({ from: z9.number().int().min(1), to: z9.number().int().min(1), total: count }).nullable().optional()
});
var VersionsDetailSchema = z9.object({
  tool: z9.literal("versions"),
  documentId: z9.string().uuid(),
  title: z9.string(),
  versions: count,
  current: count,
  /** Members of the family the agent may not read. Counted, never named. */
  hidden: count
});
var LinksDetailSchema = z9.object({
  tool: z9.literal("links"),
  documentId: z9.string().uuid(),
  title: z9.string(),
  /** Files linked, both ends visible, across every page. */
  links: count,
  page: z9.number().int().min(1),
  pages: z9.number().int().min(1)
});
var MetadataDetailSchema = z9.object({
  tool: z9.literal("metadata"),
  matches: count,
  /** Whether every match is a photo, so the line says "photos". A stored kind. */
  photos: z9.boolean(),
  filter: z9.object({
    documentId: z9.string().uuid().nullable(),
    takenFrom: isoDay.nullable(),
    takenTo: isoDay.nullable(),
    author: z9.string().nullable(),
    sender: z9.string().nullable(),
    field: z9.string().nullable(),
    value: z9.string().nullable()
  })
});
var CrewDetailSchema = z9.object({
  tool: z9.literal("crew"),
  people: count,
  notCleared: count,
  /** The day compliance was computed for (C18). */
  asOf: isoDay
});
var FindingsDetailSchema = z9.object({ tool: z9.literal("findings") });
var KeptLooksDetailSchema = z9.object({
  tool: z9.literal("kept_looks"),
  target: LookTargetSchema,
  listed: count,
  opened: count,
  lookIds: z9.array(z9.string().uuid())
});
var LookDetailSchema = z9.object({
  tool: z9.literal("look"),
  target: LookTargetSchema,
  box: BoxSchema.nullable(),
  /** The size of the image sent, in pixels, and the scale it was fitted by. */
  width: count.nullable(),
  height: count.nullable(),
  scale: z9.number().positive().nullable(),
  model: z9.string().nullable(),
  /** The kept look it made, or null when it was not kept (§1.4). */
  lookId: z9.string().uuid().nullable(),
  /** Why a new look was made, in the model's words (`0030` §2.7.4). */
  reason: z9.string().nullable()
});
var RowsForCodeDetailSchema = z9.object({
  tool: z9.literal("rows_for_code"),
  documentId: z9.string().uuid(),
  title: z9.string(),
  /** The sheet's name as written, or null for a table of a page. */
  sheet: z9.string().nullable(),
  rows: count,
  columns: count,
  /** The file's name in the code container. */
  file: z9.string(),
  /**
   * Whether this call gave the whole file: a workbook's only sheet, with no
   * cell, group or line left out (2026-09-26, slice F at the merge). One sheet
   * of several, or a page's tables, is part of a file. Optional: a step
   * stored before it has none, and is read as part.
   */
  whole: z9.boolean().optional(),
  /** For a sheet: its place among the workbook's sheets, from 1, and how many there are. */
  sheetIndex: count.nullable().optional(),
  sheetsInFile: count.nullable().optional()
});
var CodeExecutionDetailSchema = z9.object({
  tool: z9.literal("code_execution"),
  code: z9.string(),
  lines: count,
  /** The output whole (G25): the display folds it, it is never cut. */
  output: z9.string(),
  exitCode: z9.number().int().nullable(),
  files: z9.array(z9.string())
});
var StepDetailSchema = z9.discriminatedUnion("tool", [
  ContextDetailSchema,
  ListFilesDetailSchema,
  SearchDetailSchema,
  OpenFileDetailSchema,
  VersionsDetailSchema,
  LinksDetailSchema,
  MetadataDetailSchema,
  CrewDetailSchema,
  FindingsDetailSchema,
  KeptLooksDetailSchema,
  LookDetailSchema,
  RowsForCodeDetailSchema,
  CodeExecutionDetailSchema
]);
var AnswerNoteSchema = z9.object({
  text: z9.string(),
  form: z9.enum(["text", "thinking"]),
  markersRemoved: count,
  /**
   * False for the note that stood in for unfinished work when a turn was cut
   * off (§1.2). Kept on the trail, and not shown. Decided by the stop reason
   * and the block's place, never by its words.
   */
  shown: z9.boolean()
});
var AnswerStepSchema = z9.object({
  /** 0 is what the model was given; the rest in the order they started. */
  seq: z9.number().int().min(0),
  /** The model turn it belongs to. Turns count from 1; row 0 is turn 0. */
  turn: z9.number().int().min(0),
  kind: AnswerStepKindSchema,
  /** The tool, for a `tool` row. A plain string: see `AGENT_TOOL_NAMES`. */
  tool: z9.string().nullable(),
  outcome: AnswerStepOutcomeSchema,
  /** The model's few words on why, shown as "Why:". As written. */
  purpose: z9.string().nullable(),
  /**
   * The display numbers of the cited sources this step returned; for a
   * `text` note, the ones it cites. Filled when the answer's numbers are known.
   */
  sources: z9.array(z9.number().int().min(1)),
  /** For `repeat`: the step it repeats. */
  repeatOf: z9.number().int().min(0).nullable(),
  /** For `timeout`: the tool's limit, in seconds. */
  timeoutSeconds: count.nullable(),
  /** For `error`: the user-safe message. Never a stack trace, never model output. */
  failure: z9.string().nullable(),
  note: AnswerNoteSchema.nullable(),
  /** For a `limit` row: which guard, an `AnswerEndedBy` value as a string. */
  limit: z9.string().nullable(),
  /** Parse with `StepDetailSchema`. */
  detail: z9.unknown(),
  startedAt: z9.string(),
  finishedAt: z9.string().nullable()
});
var AnswerTrailCountsSchema = z9.object({
  filesOpened: count.default(0),
  searches: count.default(0),
  lists: count.default(0),
  versions: count.default(0),
  links: count.default(0),
  metadata: count.default(0),
  crew: count.default(0),
  keptLooksRead: count.default(0),
  looks: z9.object({
    drawing: count.default(0),
    page: count.default(0),
    picture: count.default(0),
    photo: count.default(0)
  }).default({}),
  filesToCode: count.default(0),
  codeRuns: count.default(0),
  findings: count.default(0)
});
var AnswerTrailSchema = z9.object({
  /** Every call is a step: kept-look reads and server code calls included (§1.6). */
  steps: count,
  turns: count,
  elapsedMs: count,
  /** An `AnswerEndedBy` value, as a string (the rule for `done`). */
  endedBy: z9.string(),
  counts: AnswerTrailCountsSchema
});
var AnswerCoverageSchema = z9.object({
  /** Visible files, identical copies not counted. */
  filesVisible: count,
  filesListed: count,
  filesOpened: count,
  /** Files the agent may not read, all statuses together, and by status. */
  hidden: count,
  hiddenByStatus: z9.record(z9.string(), count).default({}),
  /** Files the project check has not examined yet, and files whose bytes are not stored. */
  waitingForCheck: count,
  notStored: count,
  /** Whether a search in the trail covered every visible file (rule C1). */
  searchedEveryFile: z9.boolean(),
  crewRead: z9.boolean(),
  /** What block 2 left out because it did not fit (§1.5), or null. */
  leftOut: z9.object({
    journalEntries: count,
    journalNotes: count,
    decisions: count,
    before: isoDay.nullable()
  }).nullable(),
  /** Files opened and read only in part, with the unit counted. */
  partlyRead: z9.array(
    z9.object({
      documentId: z9.string().uuid(),
      title: z9.string(),
      unit: z9.string(),
      read: count,
      total: count
    })
  ),
  /** Files opened that ingest could not read whole (`visible_read_state`). */
  cutByIngest: z9.array(z9.object({ documentId: z9.string().uuid(), title: z9.string() })),
  /**
   * Files opened per category, against the visible files whose main category
   * it is (`document-ai/0032`). Names as the tenant's list writes them.
   */
  categories: z9.array(z9.object({ name: z9.string(), opened: count, visible: count }))
});
var AnswerUnfinishedSchema = z9.object({
  /** An `AnswerEndedBy` value other than `answer`, as a string. */
  reason: z9.string(),
  steps: count
  // `area`, the project area it was reading when it stopped, went on
  // 2026-10-18 (drift W-215): always null since the outline went
  // (2026-10-03, `document-ai/0032`), and coverage is per category.
});
function num(n, locale) {
  return new Intl.NumberFormat(locale).format(n);
}
function counted(n, one, many, locale) {
  return `${num(n, locale)} ${n === 1 ? one : many}`;
}
function day(iso, locale) {
  const date = /* @__PURE__ */ new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC"
  });
}
function percent(share, locale) {
  const digits = share > 0 && share < 5e-3 ? { maximumSignificantDigits: 1 } : { maximumFractionDigits: 0 };
  return new Intl.NumberFormat(locale, { style: "percent", ...digits }).format(share);
}
function quoted(text) {
  return `\u201C${text}\u201D`;
}
function capitalise(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
function range(from, to, locale) {
  if (from === null) return "";
  if (to === null || to === from) return num(from, locale);
  return `${num(from, locale)}\u2013${num(to, locale)}`;
}
function targetPhrase(target, locale) {
  const title = target.title !== null ? ` of ${quoted(target.title)}` : "";
  switch (target.kind) {
    case "drawing":
    case "page":
      return target.page !== null ? `page ${num(target.page, locale)}${title}` : `a page${title}`;
    case "picture":
      return target.page !== null ? `a picture on page ${num(target.page, locale)}${title}` : `a picture${target.title !== null ? ` in ${quoted(target.title)}` : ""}`;
    case "photo":
      return target.date !== null ? `a journal photo of ${day(target.date, locale)}` : "a journal photo";
  }
}
function termsPhrase(terms) {
  return terms.map(quoted).join(", ");
}
function scopePhrase(detail, locale) {
  if (detail.scope === "category" && detail.scopeName !== null) return `the files in ${quoted(detail.scopeName)}`;
  if (detail.scope === "files" && detail.scopeFiles !== null) {
    return counted(detail.scopeFiles, "file", "files", locale);
  }
  return "all files";
}
var PART_UNITS = {
  pages: ["page", "pages"],
  passages: ["passage", "passages"],
  rows: ["row", "rows"]
};
function metadataFilterPhrase(filter, locale) {
  const parts = [];
  if (filter.takenFrom !== null && (filter.takenTo === null || filter.takenTo === filter.takenFrom)) {
    parts.push(`taken on ${day(filter.takenFrom, locale)}`);
  } else if (filter.takenFrom !== null && filter.takenTo !== null) {
    parts.push(`taken between ${day(filter.takenFrom, locale)} and ${day(filter.takenTo, locale)}`);
  } else if (filter.takenTo !== null) {
    parts.push(`taken by ${day(filter.takenTo, locale)}`);
  }
  if (filter.author !== null) parts.push(`by ${quoted(filter.author)}`);
  if (filter.sender !== null) parts.push(`from ${quoted(filter.sender)}`);
  if (filter.field !== null) {
    parts.push(filter.value !== null ? `with ${quoted(filter.field)} ${quoted(filter.value)}` : `with ${quoted(filter.field)}`);
  }
  return parts.length > 0 ? parts.join(" ") : "by their metadata";
}
function runningLine(tool, detail, locale) {
  switch (detail?.tool ?? tool) {
    case "search":
      return detail?.tool === "search" ? `Searching ${scopePhrase(detail, locale)} for ${termsPhrase(detail.terms)}\u2026` : "Searching\u2026";
    case "open_file":
      return detail?.tool === "open_file" ? `Opening ${quoted(detail.title)}\u2026` : "Opening a file\u2026";
    case "list_files":
      if (detail?.tool === "list_files" && detail.category !== null) return `Listing files in ${quoted(detail.category)}\u2026`;
      if (detail?.tool === "list_files" && detail.folder !== null) return `Listing files in the folder ${quoted(detail.folder)}\u2026`;
      return "Listing files\u2026";
    case "versions":
      return detail?.tool === "versions" ? `Checking the versions of ${quoted(detail.title)}\u2026` : "Checking the versions\u2026";
    case "links":
      return detail?.tool === "links" ? `Following the links of ${quoted(detail.title)}\u2026` : "Following the links\u2026";
    case "metadata":
      return "Finding files by their metadata\u2026";
    case "crew":
      return "Checking the crew roster\u2026";
    case "kept_looks":
      return detail?.tool === "kept_looks" ? `Reading kept looks of ${targetPhrase(detail.target, locale)}\u2026` : "Reading kept looks\u2026";
    case "look":
      return detail?.tool === "look" ? `Looking at ${targetPhrase(detail.target, locale)}\u2026` : "Looking\u2026";
    case "rows_for_code":
      return detail?.tool === "rows_for_code" ? `Giving ${quoted(detail.title)} to code\u2026` : "Giving rows to code\u2026";
    case "code_execution":
      return "Running code\u2026";
    case "findings":
      return "Asking for findings\u2026";
    default:
      return `Running ${quoted(tool)}\u2026`;
  }
}
function doneLine(tool, detail, locale) {
  if (detail === null) return tool === "findings" ? "Findings are not made yet" : `Ran ${quoted(tool)}`;
  switch (detail.tool) {
    case "context": {
      const journal = `the journal (${counted(detail.journalEntries, "entry", "entries", locale)}, ${counted(detail.journalNotes, "note", "notes", locale)})`;
      return `Started from the project (${counted(detail.files, "file", "files", locale)} in ${counted(detail.categories, "category", "categories", locale)}), ${journal} and ${counted(detail.decisions, "decision", "decisions", locale)}`;
    }
    case "search": {
      const found = detail.hits === 0 ? "no hits" : `${counted(detail.hits, "hit", "hits", locale)} in ${counted(detail.filesHit, "file", "files", locale)}`;
      return `Searched ${scopePhrase(detail, locale)} for ${termsPhrase(detail.terms)}: ${found}`;
    }
    case "open_file": {
      if (detail.part === "size") return `Checked the size of ${quoted(detail.title)}`;
      if (detail.part === "reading") {
        return `Read the reading of page ${range(detail.from, detail.to, locale)} of ${quoted(detail.title)}`;
      }
      const [one, many] = PART_UNITS[detail.part];
      const span = detail.from !== null && detail.to !== null ? detail.to - detail.from + 1 : null;
      const unit = detail.from !== null && detail.to !== null && detail.to !== detail.from ? many : one;
      const share = span !== null && detail.total !== null ? ` (${num(span, locale)} of ${num(detail.total, locale)})` : "";
      const sheet = detail.part === "rows" && detail.sheet != null ? `, sheet ${quoted(detail.sheet)}` : "";
      return `Read ${unit} ${range(detail.from, detail.to, locale)} of ${quoted(detail.title)}${sheet}${share}`;
    }
    case "list_files": {
      const where = detail.category !== null ? ` in ${quoted(detail.category)}` : detail.folder !== null ? ` in the folder ${quoted(detail.folder)}` : "";
      return `Listed ${num(detail.listed, locale)} of ${counted(detail.total, "file", "files", locale)}${where}`;
    }
    case "versions":
      return `Found ${counted(detail.versions, "version", "versions", locale)} of ${quoted(detail.title)}, ${num(detail.current, locale)} current`;
    case "links":
      return `Found ${counted(detail.links, "file", "files", locale)} linked to ${quoted(detail.title)}`;
    case "metadata":
      return `Found ${counted(detail.matches, detail.photos ? "photo" : "file", detail.photos ? "photos" : "files", locale)} ${metadataFilterPhrase(detail.filter, locale)}`;
    case "crew":
      return `Checked the crew as of ${day(detail.asOf, locale)}: ${counted(detail.people, "person", "people", locale)}, ${num(detail.notCleared, locale)} not cleared`;
    case "findings":
      return "Findings are not made yet";
    case "kept_looks":
      return detail.opened > 0 ? `Read ${counted(detail.opened, "kept look", "kept looks", locale)} of ${targetPhrase(detail.target, locale)}` : `Listed ${counted(detail.listed, "kept look", "kept looks", locale)} of ${targetPhrase(detail.target, locale)}`;
    case "look":
      return detail.box !== null ? `Looked at a region of ${targetPhrase(detail.target, locale)}` : `Looked at ${targetPhrase(detail.target, locale)}`;
    case "rows_for_code":
      return `Gave ${counted(detail.rows, "row", "rows", locale)} of ${quoted(detail.title)} to code`;
    case "code_execution":
      return `Ran code (${counted(detail.lines, "line", "lines", locale)})`;
  }
}
function failedAction(tool, detail, locale) {
  switch (detail?.tool ?? tool) {
    case "open_file":
      return detail?.tool === "open_file" ? `open ${quoted(detail.title)}` : "open the file";
    case "search":
      return "search";
    case "list_files":
      return "list the files";
    case "versions":
      return detail?.tool === "versions" ? `read the versions of ${quoted(detail.title)}` : "read the versions";
    case "links":
      return detail?.tool === "links" ? `read the links of ${quoted(detail.title)}` : "read the links";
    case "metadata":
      return "read the metadata";
    case "crew":
      return "read the crew roster";
    case "kept_looks":
      return "read the kept looks";
    case "look":
      return detail?.tool === "look" ? `look at ${targetPhrase(detail.target, locale)}` : "look";
    case "rows_for_code":
      return detail?.tool === "rows_for_code" ? `give ${quoted(detail.title)} to code` : "give the rows to code";
    case "code_execution":
      return "run the code";
    default:
      return `run ${quoted(tool)}`;
  }
}
var LIMIT_LINES = {
  "step-limit": "Stopped: the step limit was reached",
  repeating: "Stopped: the same step was asked for again",
  context: "Stopped: the context was nearly full",
  time: "Stopped: the time limit was reached",
  "too-long": "Stopped: the request was over the context window",
  "max-tokens": "Stopped: the output limit was reached",
  stopped: "Stopped by you",
  restart: "Stopped by a server restart"
};
function describeStep(step, locale) {
  const parsed = StepDetailSchema.safeParse(step.detail);
  const detail = parsed.success ? parsed.data : null;
  switch (step.kind) {
    case "context":
      return detail?.tool === "context" ? doneLine("context", detail, locale) : "Started from the project";
    case "note":
      return step.note?.text ?? "";
    case "turn":
      return `Model turn ${num(step.turn, locale)}`;
    case "limit":
      return (step.limit !== null ? LIMIT_LINES[step.limit] : void 0) ?? "Stopped early";
    case "wrap-up":
      return "Asked for an answer from what was read";
    case "review":
      return step.outcome === "running" ? "Checking the answer\u2026" : "Checked the answer";
    case "tool":
      break;
  }
  const tool = step.tool ?? "tool";
  switch (step.outcome) {
    case "running":
      return runningLine(tool, detail, locale);
    case "ok":
    case "empty":
      return doneLine(tool, detail, locale);
    case "repeat":
      return `Not run again: the same ${tool === "search" ? "search" : "call"} as step ${num(step.repeatOf ?? 0, locale)}`;
    case "step-limit":
      return "Not run: the step limit was reached";
    case "cancelled":
      return "Not run: the answer was stopped";
    case "timeout":
      return step.timeoutSeconds !== null ? `Timed out after ${num(step.timeoutSeconds, locale)} s` : "Timed out";
    case "error": {
      const action = `Could not ${failedAction(tool, detail, locale)}`;
      return step.failure ? `${action}: ${step.failure}` : action;
    }
  }
}
function trailFromSteps(steps, opts) {
  const counts = AnswerTrailCountsSchema.parse({});
  const opened = /* @__PURE__ */ new Set();
  const toCode = /* @__PURE__ */ new Set();
  let calls = 0;
  let turns = 0;
  for (const step of steps) {
    turns = Math.max(turns, step.turn);
    if (step.kind !== "tool") continue;
    calls += 1;
    if (step.outcome !== "ok" && step.outcome !== "empty") continue;
    const parsed = StepDetailSchema.safeParse(step.detail);
    const detail = parsed.success ? parsed.data : null;
    switch (step.tool) {
      case "open_file":
        if (detail?.tool === "open_file") opened.add(detail.documentId);
        break;
      case "search":
        counts.searches += 1;
        break;
      case "list_files":
        counts.lists += 1;
        break;
      case "versions":
        counts.versions += 1;
        break;
      case "links":
        counts.links += 1;
        break;
      case "metadata":
        counts.metadata += 1;
        break;
      case "crew":
        counts.crew += 1;
        break;
      case "kept_looks":
        counts.keptLooksRead += detail?.tool === "kept_looks" ? detail.opened : 0;
        break;
      case "look":
        if (detail?.tool === "look") counts.looks[detail.target.kind] += 1;
        break;
      case "rows_for_code":
        if (detail?.tool === "rows_for_code") toCode.add(detail.documentId);
        break;
      case "code_execution":
        counts.codeRuns += 1;
        break;
      case "findings":
        counts.findings += 1;
        break;
    }
  }
  counts.filesOpened = opened.size;
  counts.filesToCode = toCode.size;
  return { steps: calls, turns, elapsedMs: Math.max(0, Math.round(opts.elapsedMs)), endedBy: opts.endedBy, counts };
}
function times(n, verb, locale) {
  if (n === 1) return `${verb} once`;
  if (n === 2) return `${verb} twice`;
  return `${verb} ${num(n, locale)} times`;
}
function duration(ms, locale) {
  const seconds = Math.round(ms / 1e3);
  if (seconds < 60) return `${num(seconds, locale)} s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest === 0 ? `${num(minutes, locale)} min` : `${num(minutes, locale)} min ${num(rest, locale)} s`;
}
var STOPPED_SUFFIX = {
  "step-limit": "stopped at the step limit",
  repeating: "stopped: a step was repeated",
  context: "stopped: the context was nearly full",
  time: "stopped at the time limit",
  "too-long": "stopped: the request was too long",
  "max-tokens": "cut off at the output limit",
  stopped: "stopped by you",
  restart: "stopped by a server restart"
};
function summariseSteps(trail, locale) {
  const c13 = trail.counts;
  const early = trail.endedBy !== "answer";
  const suffix = early ? ` \xB7 ${STOPPED_SUFFIX[trail.endedBy] ?? "stopped early"}` : "";
  if (trail.steps === 0) {
    return early ? `No files opened${suffix}` : "Answered from the journal, the decisions and the project's categories. No files opened.";
  }
  const looks = [
    [c13.looks.drawing, "drawing", "drawings"],
    [c13.looks.page, "page", "pages"],
    [c13.looks.picture, "picture", "pictures"],
    [c13.looks.photo, "photo", "photos"]
  ].filter(([n]) => n > 0).map(([n, one, many]) => counted(n, one, many, locale));
  const parts = [
    c13.filesOpened > 0 ? `opened ${counted(c13.filesOpened, "file", "files", locale)}` : null,
    c13.searches > 0 ? times(c13.searches, "searched", locale) : null,
    c13.lists > 0 ? times(c13.lists, "listed files", locale) : null,
    c13.versions > 0 ? times(c13.versions, "checked versions", locale) : null,
    c13.links > 0 ? times(c13.links, "followed links", locale) : null,
    c13.metadata > 0 ? times(c13.metadata, "searched metadata", locale) : null,
    c13.crew > 0 ? "checked the crew" : null,
    c13.keptLooksRead > 0 ? `read ${counted(c13.keptLooksRead, "kept look", "kept looks", locale)}` : null,
    looks.length > 0 ? `looked at ${looks.join(" and ")}` : null,
    c13.filesToCode > 0 ? `gave ${counted(c13.filesToCode, "file", "files", locale)} to code` : null,
    c13.codeRuns > 0 ? times(c13.codeRuns, "ran code", locale) : null,
    c13.findings > 0 ? "asked for findings" : null
  ].filter((part) => part !== null);
  const done = parts.length > 0 ? capitalise(parts.join(", ")) : capitalise(counted(trail.steps, "step", "steps", locale));
  return `${done} \xB7 ${duration(trail.elapsedMs, locale)}${suffix}`;
}
function formatCoverage(coverage, locale) {
  const sentences = [
    `Read ${num(coverage.filesOpened, locale)} of ${counted(coverage.filesVisible, "file", "files", locale)}.`
  ];
  if (coverage.hidden > 0) sentences.push(`${num(coverage.hidden, locale)} hidden.`);
  if (coverage.waitingForCheck > 0) {
    sentences.push(`${num(coverage.waitingForCheck, locale)} waiting for the project check.`);
  }
  for (const part of coverage.partlyRead) {
    if (part.total > 0) sentences.push(`Read ${percent(part.read / part.total, locale)} of ${quoted(part.title)}.`);
  }
  for (const cut of coverage.cutByIngest) sentences.push(`${quoted(cut.title)} was not read whole at upload.`);
  const left = coverage.leftOut;
  if (left !== null) {
    const what = [
      left.journalEntries > 0 ? counted(left.journalEntries, "journal entry", "journal entries", locale) : null,
      left.journalNotes > 0 ? counted(left.journalNotes, "note", "notes", locale) : null,
      left.decisions > 0 ? counted(left.decisions, "decision", "decisions", locale) : null
    ].filter((part) => part !== null);
    if (what.length > 0) {
      const before = left.before !== null ? ` from before ${day(left.before, locale)}` : "";
      sentences.push(`Not read${before}: ${what.join(", ")}.`);
    }
  }
  return sentences.join(" ");
}
var UNFINISHED_SENTENCES = {
  "step-limit": (steps) => `stopped after ${steps} steps.`,
  repeating: () => "stopped because a step was asked for again.",
  context: () => "stopped when the context was nearly full.",
  time: () => "stopped at the time limit.",
  "too-long": () => "the request was over the context window.",
  "max-tokens": () => "cut off at the output limit.",
  stopped: (steps) => `stopped by you after ${steps} steps.`,
  restart: () => "stopped by a server restart."
};
function formatUnfinished(unfinished, locale) {
  const said = UNFINISHED_SENTENCES[unfinished.reason]?.(num(unfinished.steps, locale)) ?? "stopped early.";
  return `Unfinished: ${said}`;
}
var InstalledIsoDateSchema = z9.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected a YYYY-MM-DD date").refine((value) => {
  const parsed = /* @__PURE__ */ new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}, "Not a real calendar date");
var InstalledQuestionTopicSchema = z9.enum([
  "contract-commercial",
  "design-technical",
  "safety-compliance",
  "permits-legal",
  "schedule-programme",
  "crew-people",
  "daily-record",
  "general"
]);
var InstalledChatSourceLinkSchema = z9.discriminatedUnion("kind", [
  z9.object({ kind: z9.literal("brain-entry"), entryId: z9.string().uuid() }),
  z9.object({
    kind: z9.literal("document"),
    documentId: z9.string().uuid(),
    page: z9.number().int().min(1).optional()
  }),
  z9.object({ kind: z9.literal("journal"), entryId: z9.string() }),
  z9.object({
    kind: z9.literal("journal-note"),
    noteId: z9.string().uuid(),
    date: InstalledIsoDateSchema
  }),
  z9.object({ kind: z9.literal("crew"), personId: z9.string().uuid() })
]);
var InstalledChatSourceSchema = z9.object({
  n: z9.number().int().min(1),
  kind: z9.enum(["document", "note", "manual", "decision", "journal", "crew"]),
  title: z9.string(),
  citation: z9.string(),
  snippet: z9.string(),
  date: z9.string().nullable(),
  link: InstalledChatSourceLinkSchema,
  original: InstalledChatSourceLinkSchema.optional()
});
var InstalledAnswerConfidenceSchema = z9.enum(["high", "partial", "needs-confirmation"]);
var AskEventSchemaAsInstalled = z9.discriminatedUnion("type", [
  z9.object({ type: z9.literal("status"), stage: z9.enum(["selecting", "reading", "answering"]) }),
  z9.object({
    type: z9.literal("sources"),
    topic: InstalledQuestionTopicSchema,
    sources: z9.array(InstalledChatSourceSchema)
  }),
  z9.object({ type: z9.literal("delta"), text: z9.string() }),
  z9.object({ type: z9.literal("reasoning"), text: z9.string() }),
  z9.object({
    type: z9.literal("done"),
    messageId: z9.string().uuid(),
    confidence: InstalledAnswerConfidenceSchema.nullable(),
    confidenceNote: z9.string().nullable()
  }),
  z9.object({ type: z9.literal("error"), message: z9.string() })
]);

// src/chat.ts
var c6 = initContract6();
var QuestionTopicSchema = z10.enum([
  "contract-commercial",
  "design-technical",
  "safety-compliance",
  "permits-legal",
  "schedule-programme",
  "crew-people",
  "daily-record",
  "general"
]);
var TOPIC_LABELS = {
  "contract-commercial": "Contract & commercial",
  "design-technical": "Design & technical",
  "safety-compliance": "Safety & compliance",
  "permits-legal": "Permits & legal",
  "schedule-programme": "Schedule & programme",
  "crew-people": "Crew & people",
  "daily-record": "Daily record",
  general: "General"
};
var SourceBoxSchema = z10.object({
  x: z10.number().min(0).max(1),
  y: z10.number().min(0).max(1),
  w: z10.number().min(0).max(1),
  h: z10.number().min(0).max(1)
});
var ChatSourceLinkSchema = z10.discriminatedUnion("kind", [
  /** Any `knowledge_base` row, whatever its kind; `entryKind` says which. */
  z10.object({ kind: z10.literal("brain-entry"), entryId: z10.string().uuid() }),
  /**
   * The stored file itself. `page` comes from the chunk's `CitationLocator`, so
   * a viewer can open at the cited page rather than at page 1.
   */
  z10.object({
    kind: z10.literal("document"),
    documentId: z10.string().uuid(),
    page: z10.number().int().min(1).optional(),
    /** The region a look read, on that page or picture. */
    box: SourceBoxSchema.optional()
  }),
  z10.object({
    kind: z10.literal("journal"),
    entryId: z10.string(),
    /**
     * The day's photo a look read, and the region of it (G19). The day stays
     * the destination; a region with no photo is never sent.
     */
    photoId: z10.string().uuid().optional(),
    box: SourceBoxSchema.optional()
  }),
  /**
   * A note filed against a date, which is **not** a day's record.
   *
   * Its own variant rather than reusing `journal`: an unflagged note creates no
   * `journal_entries` row at all, so there is no entry id to open and
   * `{ kind: 'journal', entryId: <a note id> }` would send a reader to a 404.
   * The date rides along because the destination is the day, not the row — the
   * Journal screen shows notes in a dated feed.
   */
  z10.object({
    kind: z10.literal("journal-note"),
    noteId: z10.string().uuid(),
    date: IsoDateSchema,
    /** A photo filed with the note that a look read, and the region (G19). */
    photoId: z10.string().uuid().optional(),
    box: SourceBoxSchema.optional()
  }),
  z10.object({ kind: z10.literal("crew"), personId: z10.string().uuid() })
]);
var ChatSourceKindSchema = z10.enum([
  "document",
  "note",
  "manual",
  "decision",
  "journal",
  "crew"
]);
var CHAT_SOURCE_LABELS = {
  document: "Document",
  note: "Note",
  manual: "Manual",
  decision: "Decision",
  journal: "Journal",
  crew: "Crew"
};
var CITE_MARKER_PATTERN = /\[\[\s*cite\s*:\s*(\d+)\s*\]\]/i;
function freshCitePattern() {
  return new RegExp(CITE_MARKER_PATTERN.source, "gi");
}
function formatCiteMarker(n) {
  const safe = Number.isFinite(n) ? Math.max(1, Math.trunc(n)) : 1;
  return `[[cite:${safe}]]`;
}
function normaliseCiteMarkers(text) {
  return text.replace(
    freshCitePattern(),
    (_marker, digits) => formatCiteMarker(Number(digits))
  );
}
function parseCiteMarkers(text) {
  const seen = /* @__PURE__ */ new Set();
  for (const match of text.matchAll(freshCitePattern())) {
    const n = Number(match[1]);
    if (Number.isFinite(n)) seen.add(n);
  }
  return [...seen];
}
function stripCiteMarkers(text) {
  return text.replace(freshCitePattern(), "").replace(/[ \t]+([.,;:!?)\]])/g, "$1").replace(/[ \t]{2,}/g, " ").replace(/[ \t]+\n/g, "\n").trim();
}
var ChatSourceSchema = z10.object({
  n: z10.number().int().min(1),
  kind: ChatSourceKindSchema,
  title: z10.string(),
  /**
   * Pre-rendered at ingest time by `formatCitation()` and passed through
   * untouched. Not re-rendered here, and not re-rendered on the client: one
   * renderer is what stops the chat, the Brain and the document viewer
   * describing the same chunk three different ways.
   */
  citation: z10.string(),
  /** A short verbatim extract, so the reader can check the claim in place. */
  snippet: z10.string(),
  /**
   * When this source is *from*, as `YYYY-MM-DD`. Null when nothing dates it.
   * For a file, the date it states itself — a title block, a letter date — and
   * the upload date only when it states none (`document-ai/0016` §5): a bundle
   * arrives at one moment, so upload dates cannot order revisions.
   *
   * Load-bearing, not decoration. A great many real questions turn on one
   * source having superseded another — a value approved in July replacing a
   * field workaround from June, a batch decision revised after a re-inspection —
   * and without a date the two are just contradictory. Shown on the card so a
   * reader can see the same ordering the answer reasoned over.
   */
  date: z10.string().nullable(),
  /**
   * Where the cited passage is. Clicking the citation goes here.
   *
   * For a document chunk this is the Brain entry, not the file — the entry
   * shows the exact passage that was cited, whereas a 75-page PDF opens at
   * page 1 and leaves the reader to find it.
   */
  link: ChatSourceLinkSchema,
  /**
   * The original file, when the passage came from one.
   *
   * A second link rather than a replacement for the first, because they answer
   * different questions: `link` is "show me what it said", this is "show me the
   * document it said it in" — and the page number makes that land on the right
   * page rather than the front cover.
   */
  original: ChatSourceLinkSchema.optional(),
  // ---- Added for the answering agent (Phase 5, 2026-09-26). Each is optional,
  // so an installed client parses a source that has them, and a stored answer
  // written before them still parses.
  /** The steps of the answer that returned this source (`AnswerStep.seq`): "Found in step 4". */
  steps: z10.array(z10.number().int().min(0)).optional(),
  /**
   * The note that cited it, when only a note did (§1.8 of the design): "Cited
   * in the note at step 4". The answer's numbers then have a gap here.
   */
  noteStep: z10.number().int().min(0).optional(),
  /** The value was seen in the image only: a look read it, and the text layer does not hold it. */
  imageOnly: z10.boolean().optional(),
  /**
   * What `date` is: the date the file states, or the day it was uploaded
   * because it states none (R87). Null when nothing dates it. An installed
   * client that ignores this shows an upload day as a date.
   */
  dateKind: z10.enum(["stated", "uploaded"]).nullable().optional(),
  /** Identical copies of the file, each by its name and folder as filed: "Also filed as" (R92). */
  copies: z10.array(z10.object({ name: z10.string(), folder: z10.string().nullable() })).optional()
});
var ChatRoleSchema = z10.enum(["user", "assistant"]);
var AnswerConfidenceSchema = z10.enum(["high", "partial", "needs-confirmation"]);
var SemanticToneSchema = z10.enum(["ok", "warn", "bad"]);
var ANSWER_CONFIDENCE_TONES = {
  high: "ok",
  partial: "warn",
  "needs-confirmation": "warn"
};
var ANSWER_CONFIDENCE_LABELS = {
  high: "High confidence",
  partial: "Partial",
  "needs-confirmation": "Needs confirmation"
};
var ChatMessageSchema = z10.object({
  id: z10.string().uuid(),
  threadId: z10.string().uuid(),
  role: ChatRoleSchema,
  content: z10.string(),
  /** Empty for a user turn, and for an assistant turn that cited nothing. */
  sources: z10.array(ChatSourceSchema),
  topic: QuestionTopicSchema.nullable(),
  /**
   * Null on a user turn, and on an answer nothing assessed.
   *
   * "Not assessed" and "poorly supported" are different facts and this column
   * holds both as distinct values — null renders as no band at all, rather
   * than as a doubt nobody actually expressed.
   */
  confidence: AnswerConfidenceSchema.nullable(),
  /** Why, in a sentence. Null when the review found nothing to say. */
  confidenceNote: z10.string().nullable(),
  /**
   * MOB-80: the headline value — "26 Nm", "EUR 259,000", or the count or
   * finding where the question has no single value. Emitted by the review
   * pass beside the confidence; null when nothing assessed the answer, and
   * the phone then falls back to the prose. The console renders the prose
   * regardless and never reads this.
   */
  headline: z10.string().nullable(),
  /** Up to three supporting sentences under the headline. Empty when none. */
  supporting: z10.array(z10.string()),
  createdAt: z10.string(),
  // ---- Added for the answering agent (Phase 5, 2026-09-26). Optional, and
  // null on a user turn, on a scripted demo answer and on an answer written
  // before them. Each client writes their lines in its own locale from these
  // counts (`answer-steps.ts`).
  /** What the agent did, counted: the collapsed line of the step display. */
  trail: AnswerTrailSchema.nullable().optional(),
  /** What the answer read against what was visible: the line under the answer. */
  coverage: AnswerCoverageSchema.nullable().optional(),
  /** Why the answer stopped before it finished, when it did. */
  unfinished: AnswerUnfinishedSchema.nullable().optional(),
  /** A person marked this answer as wrong (`document-ai/0030` §2.7.6). */
  markedWrong: z10.boolean().optional()
});
var ChatThreadSchema = z10.object({
  id: z10.string().uuid(),
  projectId: z10.string().uuid(),
  title: z10.string(),
  createdAt: z10.string(),
  updatedAt: z10.string()
});
var ChatThreadSummarySchema = ChatThreadSchema.extend({
  messageCount: z10.number().int()
});
var ChatThreadInputSchema = z10.object({
  /**
   * A client-minted thread id (MOB-78).
   *
   * Optional, and the whole reason a question can be queued on a phone: the
   * outbox retries, a reply lost over site signal is ordinary, and without an
   * id the server cannot tell a retry from a second question — so it would
   * file both, and the Brain would answer twice. `createNote`'s exact
   * pattern: an id that already names a thread on this project answers 200
   * with that thread; a fresh one answers 201.
   */
  id: z10.string().uuid().optional(),
  /**
   * The opening question. Optional: the "New chat" button creates an empty
   * thread, while the hero creates one already carrying its question.
   *
   * When present it becomes the thread's title. The user turn itself is
   * recorded by `ask`, not here — sending it in both places would show the
   * question twice.
   */
  question: z10.string().trim().min(1).max(4e3).optional()
});
var QuestionSummarySchema = z10.object({
  /** The `chat_threads` row. `/brain/ask/:questionId` opens it. */
  id: z10.string().uuid(),
  question: z10.string(),
  askedAt: z10.string(),
  /** How many numbered sources the answer cited. Zero is a real answer. */
  sourceCount: z10.number().int().min(0),
  /**
   * The single source's pre-rendered citation, when the answer cited exactly
   * one — *"Manual §4.2"*, *"Contract §11"*.
   *
   * Only for one source, deliberately. With several there is no one citation to
   * name and picking the first would present an arbitrary source as *the*
   * source, which is the opposite of what the count is telling the reader.
   */
  citation: z10.string().nullable(),
  /** How well the sources supported it. Null when nothing assessed it. */
  confidence: AnswerConfidenceSchema.nullable(),
  /**
   * The decision this question was elevated to, or null.
   *
   * What the marker in the history list is reading — it separates what somebody
   * committed to from what was merely asked.
   */
  decisionId: z10.string().uuid().nullable(),
  /**
   * Whether the answer has ever finished.
   *
   * A question whose answer failed or was cancelled keeps its row — the user's
   * words are not thrown away — but it has no answer to show, and a history
   * that renders it identically to an answered one is lying about what is
   * there.
   */
  answered: z10.boolean()
});
var AskStageSchema = z10.enum([
  "thinking",
  "working",
  "answering",
  "checking"
]);
var AskEventSchema = z10.discriminatedUnion("type", [
  z10.object({ type: z10.literal("status"), stage: AskStageSchema }),
  z10.object({
    type: z10.literal("sources"),
    // Null while the answer is running (2026-09-26): the review writes the
    // topic at the end, and `done` carries it.
    topic: QuestionTopicSchema.nullable(),
    sources: z10.array(ChatSourceSchema)
  }),
  /**
   * A slice of answer text. Concatenate in arrival order; never re-order.
   *
   * `turn` is the model turn that wrote it (2026-09-26). Whether a turn's text
   * is the answer or a note is decided by `streamedAnswer` in `ask-stream.ts`:
   * a note only when the server sent a `text` note step of that turn.
   */
  z10.object({
    type: z10.literal("delta"),
    text: z10.string(),
    turn: z10.number().int().min(0).optional()
  }),
  /**
   * One step of the answer, sent when it starts and again when it finishes,
   * with the same `seq` (2026-09-26). A client upserts by `seq`. An installed
   * phone cannot parse it, and skips it.
   */
  z10.object({ type: z10.literal("step"), step: AnswerStepSchema }),
  /**
   * The model's summarised reasoning, when it is thinking before answering.
   * Cosmetic — a client may render it as a progress line or drop it entirely.
   */
  z10.object({ type: z10.literal("reasoning"), text: z10.string() }),
  /**
   * The answer was persisted. `messageId` is the assistant message's row.
   *
   * Carries the confidence because it is only known here — the review runs
   * against the finished answer, so there is nothing to send earlier. Without
   * it the band would appear a refetch later than the answer it belongs to,
   * which reads as the app changing its mind.
   */
  z10.object({
    type: z10.literal("done"),
    messageId: z10.string().uuid(),
    confidence: AnswerConfidenceSchema.nullable(),
    confidenceNote: z10.string().nullable(),
    // Phase 5 (2026-09-26). Optional, by the rule above.
    trail: AnswerTrailSchema.nullable().optional(),
    coverage: AnswerCoverageSchema.nullable().optional(),
    unfinished: AnswerUnfinishedSchema.nullable().optional(),
    /** The review's topic, so the live screen needs no refetch. Null when unreviewed. */
    topic: QuestionTopicSchema.nullable().optional()
  }),
  /** User-safe text. Never a stack trace, never model output. */
  z10.object({ type: z10.literal("error"), message: z10.string() })
]);
var AskInputSchema = z10.object({
  question: z10.string().trim().min(1).max(4e3)
});
var chatContract = c6.router({
  /**
   * The question history, newest first.
   *
   * Reads `chat_threads` and `chat_messages` like the admin's thread list
   * (`AdminChatThreadSummary`), and is not the same shape: this one is the
   * product's history surface. The tenant's own `listThreads` went on
   * 2026-10-18 (drift W-133). See `product/0005-a-question-is-answered-once.md`.
   */
  listQuestions: {
    method: "GET",
    path: "/projects/:id/chat/questions",
    query: z10.object({
      limit: z10.coerce.number().int().min(1).max(100).default(10),
      offset: z10.coerce.number().int().min(0).default(0)
    }),
    responses: {
      200: z10.object({
        questions: z10.array(QuestionSummarySchema),
        /**
         * Every question this project holds, not the length of this page.
         *
         * The panel needs it to know whether there is another page at all —
         * and a list that just stops at the page size is indistinguishable
         * from one that has run out, which is the silent truncation a paged
         * surface exists to avoid.
         */
        total: z10.number().int().min(0)
      }),
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Questions asked of this site's Brain, newest first"
  },
  /**
   * Elevate an answered question to a decision.
   *
   * **409 when the question already has one**, and when it has no answer to
   * elevate. Both are state, not a malformed request — and a second decision
   * from one answer would put two records of the same commitment into a corpus
   * that later cites them against each other.
   */
  elevateToDecision: {
    method: "POST",
    path: "/projects/:id/chat/questions/:questionId/decision",
    body: DecisionElevateInputSchema,
    responses: {
      201: z10.object({ decision: DecisionSchema }),
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema,
      // The statement is written by a model. With none configured there is
      // nothing to elevate, and inventing one from the answer's first sentence
      // would file a decision nobody wrote.
      503: ErrorSchema
    },
    summary: "Accept an answer as a decision, and add it to the Brain"
  },
  // `listThreads` (GET `/projects/:id/chat/threads`), `renameThread` (PATCH
  // `…/threads/:threadId`) and `deleteThread` (DELETE the same) went on
  // 2026-10-18 (drift W-133): no client called them once the history was
  // built on `listQuestions` (product/0013). The admin's thread list is its
  // own route (`@gb/admin-contracts`, `chat.threads`).
  createThread: {
    method: "POST",
    path: "/projects/:id/chat/threads",
    body: ChatThreadInputSchema,
    responses: {
      201: z10.object({ thread: ChatThreadSchema }),
      // **This thread already exists** — the caller sent an `id` it had
      // already spent, which is a retry after a lost reply (MOB-78). Same
      // body as the 201, read back from the row.
      200: z10.object({ thread: ChatThreadSchema }),
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      // The id names a thread on another project — a client bug worth
      // refusing loudly rather than answering about a different site.
      409: ErrorSchema
    },
    summary: "Start a conversation, optionally with its first question"
  },
  getThread: {
    method: "GET",
    path: "/projects/:id/chat/threads/:threadId",
    responses: {
      200: z10.object({
        thread: ChatThreadSchema,
        messages: z10.array(ChatMessageSchema),
        /**
         * The decision this question was elevated to, or null.
         *
         * On the record's own response rather than read out of the history
         * list: the history is paged, so a question opened from page three is
         * simply absent from the page the screen holds — and "absent" would
         * read as "not yet elevated" and offer to file it a second time.
         */
        decisionId: z10.string().uuid().nullable()
      }),
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "One conversation and every turn in it"
  },
  /**
   * The steps of the thread's answer, in order (Phase 5, 2026-09-26).
   *
   * Written as each step finishes, so while the answer runs this returns the
   * steps so far, and a screen reloaded mid-answer polls it. Empty for a
   * question answered before the agent, and for a scripted demo answer.
   */
  listSteps: {
    method: "GET",
    path: "/projects/:id/chat/threads/:threadId/steps",
    responses: {
      200: z10.object({ steps: z10.array(AnswerStepSchema) }),
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "The steps the Brain took to answer, in order"
  },
  /**
   * Mark an answer as wrong, or take the mark back (Phase 5, 2026-09-26,
   * `document-ai/0030` §2.7.6).
   *
   * A kept look that a marked answer made or read is not taken as enough for
   * the next question on its target.
   */
  markWrong: {
    method: "POST",
    path: "/projects/:id/chat/threads/:threadId/messages/:messageId/wrong",
    body: z10.object({ wrong: z10.boolean() }),
    responses: {
      200: z10.object({ markedWrong: z10.boolean() }),
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Mark an answer as wrong, or take the mark back"
  },
  /**
   * Stop the answer that is running on this thread (Phase 5, 2026-09-26).
   *
   * Stored on the question, because the answer runs on after the connection
   * closes and the request can reach a task that is not running it
   * (`product/0005` §2.6). `requested` is false when there was nothing left to
   * stop: the question is already answered.
   */
  cancelAsk: {
    method: "POST",
    path: "/projects/:id/chat/threads/:threadId/cancel",
    body: c6.noBody(),
    responses: {
      200: z10.object({ requested: z10.boolean() }),
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Stop the answer that is running"
  }
});

// src/crew.ts
import { initContract as initContract7 } from "@ts-rest/core";
import { z as z11 } from "zod";
var c7 = initContract7();
var CrewRoleSchema = z11.enum([
  "installer",
  "electrician",
  "crane-operator",
  "supervisor",
  "site-safety"
]);
var DocTypeSchema = z11.enum([
  "cscs",
  "cscs-gold",
  "rams",
  "working-at-height",
  "manual-handling",
  "ecs",
  "18th-edition",
  "cpcs",
  "medical-fitness",
  "smsts",
  "first-aid",
  "nebosh-general"
]);
var DOC_TYPE_LABELS = {
  cscs: "CSCS card",
  "cscs-gold": "CSCS gold card",
  rams: "RAMS briefing",
  "working-at-height": "Working at height",
  "manual-handling": "Manual handling",
  ecs: "ECS card",
  "18th-edition": "18th Edition (BS 7671)",
  cpcs: "CPCS card",
  "medical-fitness": "Medical fitness",
  smsts: "SMSTS",
  "first-aid": "First aid",
  "nebosh-general": "NEBOSH General"
};
function docTypeLabel(type, docLabels) {
  return docLabels?.[type] ?? DOC_TYPE_LABELS[type] ?? type;
}
var PersonStatusSchema = z11.enum(["active", "onboarding"]);
var ComplianceStatusSchema = z11.enum([
  "valid",
  "expiring",
  "expired",
  "missing"
]);
var CREW_ROLE_LABELS = {
  installer: "Installer",
  electrician: "Electrician",
  "crane-operator": "Crane operator",
  supervisor: "Supervisor",
  "site-safety": "Site safety"
};
function crewRoleLabel(role) {
  const parsed = CrewRoleSchema.safeParse(role);
  return parsed.success ? CREW_ROLE_LABELS[parsed.data] : role;
}
var COMPLIANCE_STATUS_LABELS = {
  valid: "Valid",
  expiring: "Expiring",
  expired: "Expired",
  missing: "Missing"
};
var COMPLIANCE_STATUS_LABELS_STANDALONE = {
  ...COMPLIANCE_STATUS_LABELS,
  expiring: "Expiring within 30 days",
  missing: "Missing document"
};
var CrewCertificateSchema = z11.object({
  id: z11.string().uuid(),
  personId: z11.string().uuid(),
  type: DocTypeSchema,
  issuingBody: z11.string().nullable(),
  reference: z11.string().nullable(),
  issuedDate: z11.string().nullable(),
  expiryDate: z11.string().nullable(),
  confirmed: z11.boolean(),
  /** Null when there is no scan — every seeded certificate is like this. */
  filename: z11.string().nullable(),
  mimeType: z11.string().nullable(),
  sizeBytes: z11.number().nullable(),
  /**
   * Null when there is no file.
   *
   * This was `z.string()` with the three legal values named in a comment, while
   * `DocumentStorageStatusSchema` — the same three, for the same S3-row-before-
   * the-bytes reason — already existed next door. A vocabulary written in prose
   * is one nothing checks.
   */
  storageStatus: DocumentStorageStatusSchema.nullable(),
  /** No `storageKey`: the bucket layout is not the client's business, and a key
   *  the client has seen is a key it could try to send back. */
  hasFile: z11.boolean()
});
var CrewDocStatusSchema = z11.object({
  type: DocTypeSchema,
  label: z11.string(),
  status: ComplianceStatusSchema,
  issuingBody: z11.string().nullable(),
  expiryDate: z11.string().nullable(),
  /** Days until expiry; negative once expired, null when nothing is on file. */
  days: z11.number().nullable(),
  /** The confirmed certificate behind this slot, when there is one. */
  certificate: CrewCertificateSchema.nullable(),
  /** An upload waiting to be confirmed. The slot still counts as unmet. */
  pending: CrewCertificateSchema.nullable()
});
var CrewMemberSchema = z11.object({
  id: z11.string().uuid(),
  name: z11.string(),
  initials: z11.string(),
  role: CrewRoleSchema,
  roleLabel: z11.string(),
  contact: z11.string().nullable(),
  /** The start of their *current* period at this project. */
  startDate: z11.string(),
  /** True once every required document is confirmed and unexpired. */
  cleared: z11.boolean(),
  /** Worst state across the documents their role requires. */
  overall: ComplianceStatusSchema,
  perDoc: z11.array(CrewDocStatusSchema),
  /**
   * `onboarding` while any required document is still unmet — the design lists
   * those separately and excludes them from the compliance tiles, because a
   * half-onboarded person is not a compliance failure yet.
   */
  onboarding: z11.boolean(),
  /** Their latest period here is closed. Kept on the roster for the trail. */
  offSite: z11.boolean()
});
var CrewBreakdownSchema = z11.object({
  valid: z11.number().int(),
  expiring: z11.number().int(),
  /** Missing *and* expired: the design groups both under one red tile. */
  missing: z11.number().int()
});
var CrewRosterSchema = z11.object({
  /** On site now and fully required-documented. Drives the tiles. */
  onSite: z11.array(CrewMemberSchema),
  onboarding: z11.array(CrewMemberSchema),
  offSite: z11.array(CrewMemberSchema),
  breakdown: CrewBreakdownSchema,
  /**
   * The day the statuses were derived against, so the client labels
   * "9d left" from the same reference the server counted from rather than
   * from a browser clock that may disagree.
   */
  asOf: z11.string(),
  /**
   * Null when the project's country has no configured profile. The screen still
   * renders — it just cannot say what anyone is required to hold.
   */
  jurisdiction: z11.object({
    code: CountryCodeSchema,
    name: z11.string(),
    roleProfiles: z11.record(z11.string(), z11.array(z11.string())),
    docLabels: z11.record(z11.string(), z11.string()),
    /**
     * Who normally issues each document type here. Used to pre-fill the
     * issuing body on the confirm form — a fact about the certificate type,
     * not a guess about the card in front of the user, which is why it is
     * safe to fill in where an expiry date would not be.
     */
    docIssuingBodies: z11.record(z11.string(), z11.string())
  }).nullable()
});
var CrewPeriodSchema = z11.object({
  id: z11.string().uuid(),
  personId: z11.string().uuid(),
  name: z11.string(),
  initials: z11.string(),
  role: CrewRoleSchema,
  roleLabel: z11.string(),
  startDate: z11.string(),
  /** Null while they are still on site. */
  endDate: z11.string().nullable(),
  stintNo: z11.number().int().positive(),
  stintTotal: z11.number().int().positive()
});
var AddCrewMemberInputSchema = z11.object({
  name: z11.string().trim().min(1).max(120),
  /** The design's drawer defaults to installer and says so. */
  role: CrewRoleSchema.default("installer"),
  contact: z11.string().trim().max(60).optional()
});
var CrewUploadUrlInputSchema = z11.object({
  type: DocTypeSchema,
  filename: z11.string().trim(),
  mimeType: z11.string().trim().min(1).max(255),
  sizeBytes: z11.number().int()
}).superRefine((file, ctx) => {
  const refused = fileRefusal(file);
  if (refused) refuse(ctx, refused.reason);
});
var CrewUploadUrlResponseSchema = z11.object({
  documentId: z11.string().uuid(),
  uploadUrl: z11.string().url(),
  /** Seconds. */
  expiresIn: z11.number().int().positive()
});
var CertificateSuggestionSchema = z11.object({
  issuingBody: z11.string().nullable(),
  reference: z11.string().nullable(),
  issuedDate: z11.string().nullable(),
  expiryDate: z11.string().nullable(),
  /** False when no model ran at all, so the UI can say so plainly. */
  attempted: z11.boolean()
});
var ConfirmCertificateInputSchema = z11.object({
  issuingBody: z11.string().trim().min(1).max(200),
  reference: z11.string().trim().max(120).optional(),
  issuedDate: IsoDateSchema.optional(),
  expiryDate: IsoDateSchema
});
var CrewCountOnDateSchema = z11.object({
  date: IsoDateSchema,
  count: z11.number().int().nonnegative()
});
var CrewMemberResponseSchema = z11.object({ person: CrewMemberSchema });
var CrewCertificateResponseSchema = z11.object({
  certificate: CrewCertificateSchema
});
var crewContract = c7.router({
  roster: {
    method: "GET",
    path: "/projects/:id/crew",
    responses: { 200: CrewRosterSchema, 401: ErrorSchema, 404: ErrorSchema },
    summary: "Everyone with a period at this project, with compliance derived"
  },
  periods: {
    method: "GET",
    path: "/projects/:id/crew/periods",
    responses: {
      200: z11.object({ periods: z11.array(CrewPeriodSchema) }),
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "The audit trail: one row per on-site period, newest first"
  },
  /**
   * The crew count for one date — the journal composer's headcount prefill.
   *
   * A dedicated route rather than a field on `GET /journal/days`, because the
   * dates that need it are exactly the dates that summary endpoint does **not**
   * return. That endpoint answers only for dates holding something, which is
   * what makes absence mean *Not logged* and what the catch-up queue derives
   * itself from; a gap has no row there by construction, and the composer's
   * `Fill in` opens on precisely those gaps.
   *
   * One date, not a range: the composer opens for one day at a time, and the
   * queue lists gaps without a crew count per row.
   *
   * `roster` is not the answer to this. It is hardcoded to today, and every
   * existing caller wants that — so this is a sibling rather than a signature
   * change.
   */
  countOn: {
    method: "GET",
    path: "/projects/:id/crew/count",
    // Required. A missing or malformed date is a 400 here rather than a silent
    // default to today: a prefill quietly counting the wrong day is worse than
    // no prefill, because the number looks the same either way.
    query: z11.object({ date: IsoDateSchema }),
    responses: {
      200: CrewCountOnDateSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "People with an open period here on one date \u2014 a suggestion, never attendance"
  },
  add: {
    method: "POST",
    path: "/projects/:id/crew",
    body: AddCrewMemberInputSchema,
    responses: {
      201: CrewMemberResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema
    },
    summary: "Create a person and open their first period here"
  },
  offboard: {
    method: "POST",
    path: "/projects/:id/crew/:personId/offboard",
    body: z11.object({}),
    responses: {
      200: CrewMemberResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema
    },
    summary: "Close the open period. The person and their documents remain"
  },
  reonboard: {
    method: "POST",
    path: "/projects/:id/crew/:personId/reonboard",
    body: z11.object({}),
    responses: {
      200: CrewMemberResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema
    },
    summary: "Open a new period \u2014 this is what puts someone in Reports twice"
  },
  uploadUrl: {
    method: "POST",
    path: "/crew/:personId/documents/upload-url",
    body: CrewUploadUrlInputSchema,
    responses: {
      201: CrewUploadUrlResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      413: ErrorSchema
    },
    summary: "Reserve an unconfirmed certificate row and presign a PUT to S3"
  },
  completeUpload: {
    method: "POST",
    path: "/crew/:personId/documents/:docId/complete",
    body: z11.object({}),
    responses: {
      200: z11.object({
        certificate: CrewCertificateSchema,
        suggestion: CertificateSuggestionSchema
      }),
      401: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema
    },
    summary: "Verify the object landed and read the card. Returns suggestions only \u2014 nothing is held until confirmed"
  },
  confirmDocument: {
    method: "PUT",
    path: "/crew/:personId/documents/:docId",
    body: ConfirmCertificateInputSchema,
    responses: {
      200: CrewCertificateResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema
    },
    summary: "Accept the fields. The certificate now counts towards compliance"
  },
  deleteDocument: {
    method: "DELETE",
    path: "/crew/:personId/documents/:docId",
    // As with `projects.deleteDocument` — a bodyless DELETE must not 400.
    body: c7.noBody(),
    responses: { 204: c7.noBody(), 401: ErrorSchema, 404: ErrorSchema },
    summary: "Discard an upload, or remove a certificate that is on file"
  }
});

// src/journals.ts
import { initContract as initContract8 } from "@ts-rest/core";
import { z as z12 } from "zod";
var c8 = initContract8();
var WeatherSchema = z12.enum(["clear", "partial-sun", "rain", "high-wind", "fog"]);
var DeliveryConditionSchema = z12.enum([
  "ok",
  "damaged",
  "wrong-spec",
  "partial",
  "rejected"
]);
var IncidentSeveritySchema = z12.enum(["none", "minor", "reportable"]);
var InspectionResultSchema = z12.enum(["pass", "pass-with-notes", "fail"]);
var ModelPassStatusSchema = z12.enum(["pending", "done", "skipped", "failed"]);
var ClockTimeSchema = z12.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Expected an HH:MM time");
var JournalDeliverySchema = z12.object({
  supplier: z12.string().trim().min(1).max(200),
  item: z12.string().trim().min(1).max(300),
  qty: z12.string().trim().min(1).max(60),
  condition: DeliveryConditionSchema
});
var JournalIncidentSchema = z12.object({
  severity: IncidentSeveritySchema,
  text: z12.string().trim().min(1).max(4e3)
});
var ToolboxTalkSchema = z12.object({
  topic: z12.string().trim().min(1).max(400),
  durationMin: z12.number().int().min(0).max(600),
  attendees: z12.number().int().min(0).max(2e3),
  ledBy: z12.string().trim().max(200).optional()
});
var JournalInspectionSchema = z12.object({
  area: z12.string().trim().min(1).max(300),
  result: InspectionResultSchema,
  inspector: z12.string().trim().min(1).max(200),
  notes: z12.string().trim().max(4e3).optional()
});
var JournalVisitorSchema = z12.object({
  name: z12.string().trim().min(1).max(200),
  organisation: z12.string().trim().max(200),
  purpose: z12.string().trim().max(400),
  arrived: ClockTimeSchema,
  departed: ClockTimeSchema
});
var PlanVsActualSchema = z12.object({
  planned: z12.string().trim().min(1).max(600),
  actual: z12.string().trim().min(1).max(600),
  /** Free text. Empty string when the day matched plan exactly. */
  variance: z12.string().trim().max(600)
});
var JournalEntrySchema = z12.object({
  id: z12.string().uuid(),
  projectId: z12.string().uuid(),
  date: IsoDateSchema,
  // Nullable, because a day is filled in progressively: photographs reach a
  // site record before the prose does, and the alternative was a stub row
  // carrying `headcount: 0` and a weather nobody observed. "Not recorded yet"
  // and "nobody was on site" are different claims on a document that gets
  // signed off — the same distinction the nullable blocks below already make.
  //
  // Sign-off is the backstop: `approve` refuses a day while any of these three
  // is blank, so "signed off" still means the record is complete.
  headcount: z12.number().int().nullable(),
  weather: WeatherSchema.nullable(),
  workCompleted: z12.string().nullable(),
  deliveries: z12.array(JournalDeliverySchema),
  incidents: z12.array(JournalIncidentSchema),
  approved: z12.boolean(),
  sentTo: z12.array(z12.string()),
  /**
   * The day's own commentary, typed in the office.
   *
   * Named `officeNotes` and not `notes` because three other things here are
   * already called a note — a `JournalNote` below, the Brain's
   * `KnowledgeNoteInput`, and the `journal` Brain source type — and a fourth
   * bare `notes` made every sentence in review ambiguous. The user-facing word
   * stays "note".
   */
  officeNotes: z12.string().nullable(),
  // The richer blocks are nullable rather than defaulted to `[]`: "nobody
  // recorded toolbox talks" and "there were no toolbox talks" are different
  // claims on a document that gets signed off.
  toolboxTalks: z12.array(ToolboxTalkSchema).nullable(),
  inspections: z12.array(JournalInspectionSchema).nullable(),
  visitors: z12.array(JournalVisitorSchema).nullable(),
  planVsActual: z12.array(PlanVsActualSchema).nullable(),
  createdAt: z12.string(),
  updatedAt: z12.string()
});
var JournalEntryResponseSchema = z12.object({ entry: JournalEntrySchema });
var JournalEntriesResponseSchema = z12.object({
  entries: z12.array(JournalEntrySchema)
});
var JournalEntryFields = z12.object({
  date: IsoDateSchema,
  // Only `date` is required to open a day. Everything else can arrive later,
  // including hours later — see the note on `JournalEntrySchema`. `nullish`
  // rather than `optional` so a form can explicitly clear a field it filled in
  // by mistake, which `undefined` alone cannot express through a PATCH.
  headcount: z12.number().int().min(0).max(2e3).nullish(),
  weather: WeatherSchema.nullish(),
  workCompleted: z12.string().trim().min(1).max(8e3).nullish(),
  deliveries: z12.array(JournalDeliverySchema).max(50).optional(),
  incidents: z12.array(JournalIncidentSchema).max(50).optional(),
  officeNotes: z12.string().trim().max(2e4).nullish(),
  toolboxTalks: z12.array(ToolboxTalkSchema).max(20).nullish(),
  inspections: z12.array(JournalInspectionSchema).max(50).nullish(),
  visitors: z12.array(JournalVisitorSchema).max(50).nullish(),
  planVsActual: z12.array(PlanVsActualSchema).max(50).nullish()
});
var JournalEntryInputSchema = JournalEntryFields;
var JournalEntryPatchSchema = JournalEntryFields.omit({ date: true }).partial().refine((v) => Object.keys(v).length > 0, "No fields to update");
var JournalApproveSchema = z12.object({
  sentTo: z12.array(z12.string().email()).max(20).optional()
});
function missingForSignOff(entry) {
  const missing = [];
  if (entry.headcount === null) missing.push("headcount");
  if (entry.weather === null) missing.push("weather");
  if (!entry.workCompleted?.trim()) missing.push("work completed");
  return missing;
}
function formatMissingForSignOff(missing) {
  if (missing.length <= 1) return missing[0] ?? "";
  return `${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}`;
}
var ExtractedDeliveryConditionSchema = z12.enum([
  "ok",
  "damaged",
  "wrong-spec",
  // Not a fifth condition — the absence of one. The note did not say.
  "unknown"
]);
var ExtractedIncidentSeveritySchema = z12.enum([
  "none",
  "minor",
  "reportable",
  "unknown"
]);
var SourceQuoteSchema = z12.string().trim().min(1).max(500).nullable();
var ExtractedDeliverySchema = z12.object({
  supplier: z12.string().trim().min(1).max(200).nullable(),
  item: z12.string().trim().min(1).max(300).nullable(),
  /** Free text, not a number: "80", "1 box", "two pallets" are all real. */
  qty: z12.string().trim().min(1).max(60).nullable(),
  condition: ExtractedDeliveryConditionSchema,
  sourceQuote: SourceQuoteSchema
});
var ExtractedIncidentSchema = z12.object({
  severity: ExtractedIncidentSeveritySchema,
  text: z12.string().trim().min(1).max(4e3).nullable(),
  sourceQuote: SourceQuoteSchema
});
var NoteExtractionSuggestionSchema = z12.object({
  deliveries: z12.array(ExtractedDeliverySchema).max(50),
  incidents: z12.array(ExtractedIncidentSchema).max(50),
  /**
   * Nullable like every extracted field: a headcount the prose *states* — "22
   * on site" — never one counted from names or inferred from anything. Null is
   * the ordinary answer. Added 2026-08-25 with `weather` for dictation
   * (`architecture/0019` §2): a spoken day routinely carries both, and
   * `extractFromDraft` gains them on the same terms for free.
   */
  headcount: z12.number().int().min(0).max(2e3).nullable(),
  /** Only when the prose states the condition — "rained all morning" — and
   *  never deduced from consequences like mud or a lost half day. */
  weather: WeatherSchema.nullable(),
  /**
   * `false` when no model was bound, so the composer can say the sections are
   * empty because nothing read the note — not because the note held nothing.
   * A silent empty list would read as "no deliveries mentioned", which is a
   * claim the system is in no position to make.
   */
  modelRan: z12.boolean()
});
var NoteExtractionInputSchema = z12.object({
  body: z12.string().trim().min(1).max(2e4)
});
var NoteExtractionStatusSchema = ModelPassStatusSchema;
var JournalNoteKindSchema = z12.enum(["note", "no-work"]);
var NOTE_KIND_LABELS = {
  note: "Note",
  "no-work": "No work"
};
var JournalNoteSchema = z12.object({
  id: z12.string().uuid(),
  projectId: z12.string().uuid(),
  date: IsoDateSchema,
  kind: JournalNoteKindSchema,
  /** Null on a `no-work` note, and only there. */
  body: z12.string().nullable(),
  /**
   * Photographs filed with this note.
   *
   * A count rather than the rows: MOB-22's entry card shows `2 photos` as a tag
   * beside the delivery and incident tags, and nothing on that card opens them.
   * `JournalPhotoSchema.noteId` already carries the join, so this is a count of
   * it — added 2026-08-20 with the product owner's approval, because the
   * alternative was a client running one photo request per visible day to
   * render a number.
   */
  photoCount: z12.number().int().nonnegative(),
  /** Null means **not recorded**, and never 0 — a 0 is a claim about the site. */
  headcount: z12.number().int().nullable(),
  weather: WeatherSchema.nullable(),
  /** Whether this note is the day's record. At most one per date. */
  isDailyEntry: z12.boolean(),
  createdBy: z12.string().uuid(),
  /** The author's name, for the feed. Null when they never set one — a fallback
   *  would put an invented label under somebody's words. */
  authorName: z12.string().nullable(),
  /** The dictation this note was composed from, when it was. The audio is
   *  retained as evidence (`architecture/0019` §5), and this is the join. */
  dictationId: z12.string().uuid().nullable(),
  deliveries: z12.array(ExtractedDeliverySchema),
  incidents: z12.array(ExtractedIncidentSchema),
  extractionStatus: NoteExtractionStatusSchema,
  /** When a person last edited this note after filing (MOB-63), or null for
   *  the unedited majority. What renders as the "Edited" marker. Distinct
   *  from `updatedAt`, which system writes also move. */
  editedAt: z12.string().nullable(),
  createdAt: z12.string(),
  updatedAt: z12.string()
});
var JournalNoteFieldsSchema = z12.object({
  /**
   * The note's id, minted by the client.
   *
   * Required, and the whole reason a note can be queued on a phone. The outbox
   * retries — that is its purpose — and a reply lost over site signal is
   * ordinary rather than exceptional. Without an id the server cannot tell a
   * retry from a second note, so it files both; and `journal_notes` has no
   * delete route on purpose, so the correction is a *third* note explaining the
   * second.
   *
   * Reserve-then-commit, which is how photographs get the same guarantee, is
   * not available here: it needs signal at the moment of capture, and under
   * MOB-44 that is exactly the moment there is none. Minting the uuid on the
   * device costs nothing and works with the radio off.
   *
   * `data-model/0006` already made every primary key a uuid, so what changes is
   * only *who* mints it.
   *
   * @see project_intelligence/architecture/0009-the-outbox-holds-more-than-photographs.md §3
   */
  id: z12.string().uuid(),
  date: IsoDateSchema,
  /**
   * What is being filed. Defaults to an ordinary note, so every existing caller
   * is unchanged.
   *
   * A `no-work` note is never composed. It is one tap on MOB-23's gap card, for
   * one named date — `data-model/0010` §5: *"The moment it can be applied to a
   * range it stops being a claim about a day and becomes a claim about a
   * calendar, and nobody read the calendar."*
   */
  kind: JournalNoteKindSchema.default("note"),
  /**
   * Capped to match `journal_entries.officeNotes`.
   *
   * Optional here and refined below: **required exactly when the kind is
   * `note`**. MOB-18's filing guard is the neighbouring rule and is untouched —
   * a no-work declaration is not an exception to it, it is a different act
   * reached by a different control that never passes through the composer.
   */
  body: z12.string().trim().min(1).max(2e4).optional(),
  headcount: z12.number().int().min(0).max(2e3).nullish(),
  weather: WeatherSchema.nullish(),
  /** Flagging writes the day's `journal_entries` row — see §4.4. */
  isDailyEntry: z12.boolean().default(false),
  /**
   * What the user agreed to, not what the model said.
   *
   * The composer calls `extractFromDraft` while the note is being written,
   * shows each suggestion as an editable card, and sends back whatever
   * survives — corrected, deleted or added to by hand. Items keep their
   * `sourceQuote`, so which of them the model produced is still recoverable
   * afterwards.
   *
   * Default `[]` rather than required: a note with no deliveries in it is the
   * ordinary case, and so is a client that never reached the extract call.
   */
  /**
   * Photographs filed with this note, by id.
   *
   * The console uploads them before the note exists, so they are on the day
   * already and are claimed here. The phone may file the note **first**: it
   * mints the photographs' ids itself and queues note and photographs
   * together, so with no signal either can land before the other. The note
   * keeps this list (`named_photo_ids`), and a photograph that arrives later is
   * claimed when its upload completes — whichever arrives second makes the
   * link (`data-model/0007`, amended 2026-10-03).
   *
   * A photo already claimed by another note is left alone rather than
   * reassigned; see `JournalNotesService.claimPhotos`.
   */
  photoIds: z12.array(z12.string().uuid()).max(50).default([]),
  deliveries: z12.array(ExtractedDeliverySchema).max(50).default([]),
  incidents: z12.array(ExtractedIncidentSchema).max(50).default([]),
  /**
   * The raw suggestion, echoed back so `extracted_raw` keeps what the model
   * actually said even where the user overrode it — `document-ai/0002` §"keeps
   * what the model said" is explicit that this is the only way to find out
   * later whether the reading is worth what it costs.
   *
   * Diagnostic only. Nothing reads it back into a column that is acted on, so
   * a client that lies here corrupts its own audit trail and nothing else.
   */
  extractedRaw: NoteExtractionSuggestionSchema.nullish(),
  /**
   * The dictation the composer was opened from, if any — must name a stored
   * dictation on the same project or the note is refused, because a dangling
   * evidence join is worse than none. Absent on a typed note, which stays the
   * ordinary case.
   */
  dictationId: z12.string().uuid().nullish()
});
function noteKindRule(input, ctx) {
  if (input.kind === "note" && (input.body === void 0 || input.body === "")) {
    ctx.addIssue({
      code: z12.ZodIssueCode.custom,
      path: ["body"],
      message: "A note needs words. Only a no-work day is filed without them."
    });
  }
  if (input.kind === "no-work" && input.isDailyEntry) {
    ctx.addIssue({
      code: z12.ZodIssueCode.custom,
      path: ["isDailyEntry"],
      message: "A no-work day has no record to sign off."
    });
  }
  if (input.kind === "no-work" && input.body !== void 0) {
    ctx.addIssue({
      code: z12.ZodIssueCode.custom,
      path: ["body"],
      message: "A no-work day carries no words. File a note instead."
    });
  }
  for (const [index, delivery] of (input.deliveries ?? []).entries()) {
    const saysSomething = delivery.supplier !== null || delivery.item !== null || delivery.qty !== null;
    if (saysSomething && (delivery.item === null || delivery.qty === null)) {
      ctx.addIssue({
        code: z12.ZodIssueCode.custom,
        path: ["deliveries", index],
        message: "A delivery needs an item and a quantity."
      });
    }
  }
}
var JournalNoteInputSchema = JournalNoteFieldsSchema.superRefine(noteKindRule);
var JournalNotesResponseSchema = z12.object({ notes: z12.array(JournalNoteSchema) });
var JournalNoteCreatedSchema = z12.object({
  note: JournalNoteSchema,
  /**
   * The day's record this note wrote, or null for an unflagged note — so the
   * client never has to guess whether to refetch the day.
   */
  entry: JournalEntrySchema.nullable(),
  /**
   * Whether this note took the daily-record flag off another one.
   *
   * On the wire so the feed can show the day's sign-off as cleared without a
   * second round trip. Replacing a day's record clears its sign-off (§4.5): a
   * sign-off is somebody putting their name to specific content, and keeping it
   * attached to content that has since been replaced is the failure
   * `data-model/0004` §1 exists to prevent, arriving from the other direction.
   *
   * **Reports what this call did, so it is always `false` on the 200.** A
   * request that found the note already filed replaced nothing; whether the
   * original one did is not recoverable from the row, and inventing an answer
   * would tell a feed to clear a sign-off that nobody has just replaced.
   */
  replacedDailyEntry: z12.boolean()
});
var JournalNoteUpdateSchema = z12.object({
  /** Re-dating a note. Refused server-side for a daily record and for a
   *  note with claimed photographs — both are anchored to their day. */
  date: IsoDateSchema.optional(),
  body: z12.string().trim().min(1).max(2e4).optional(),
  headcount: z12.number().int().min(0).max(2e3).nullish(),
  weather: WeatherSchema.nullish(),
  isDailyEntry: z12.literal(true).optional(),
  deliveries: z12.array(ExtractedDeliverySchema).max(50).optional(),
  incidents: z12.array(ExtractedIncidentSchema).max(50).optional(),
  /**
   * Photographs added to a filed note (owner, 2026-10-03). The ids are the
   * phone's own, so a photograph still queued can be named before it lands;
   * it is linked when it does, whichever reaches the server first. Audited
   * like every other edit, and a no-op when every id is already named.
   */
  addPhotoIds: z12.array(z12.string().uuid()).min(1).max(50).optional()
}).superRefine((input, ctx) => {
  for (const [index, delivery] of (input.deliveries ?? []).entries()) {
    const saysSomething = delivery.supplier !== null || delivery.item !== null || delivery.qty !== null;
    if (saysSomething && (delivery.item === null || delivery.qty === null)) {
      ctx.addIssue({
        code: z12.ZodIssueCode.custom,
        path: ["deliveries", index],
        message: "A delivery needs an item and a quantity."
      });
    }
  }
}).refine((value) => Object.keys(value).length > 0, "No fields to update");
var JournalNoteUpdatedSchema = JournalNoteCreatedSchema;
var JournalDaySummarySchema = z12.object({
  date: IsoDateSchema,
  noteCount: z12.number().int().nonnegative(),
  /**
   * The decomposed feed-row count: one Daily log row per note, plus one
   * Delivery row per extracted delivery. What the range header's "9 entries"
   * counts, and what a day header shows.
   */
  entryCount: z12.number().int().nonnegative(),
  hasDailyEntry: z12.boolean(),
  signedOff: z12.boolean(),
  photoCount: z12.number().int().nonnegative(),
  /**
   * People with an open stint at this project on this date.
   *
   * **Advisory.** A stint is a membership period, not a day's attendance
   * (`data-model/0003`), so this is a suggestion for the composer's stepper and
   * never a headcount to write anywhere.
   */
  crewCount: z12.number().int().nonnegative(),
  /**
   * Somebody has stated that nothing happened on this date.
   *
   * **Computed from what the notes are, never from how many there are**, and
   * that distinction is the whole reason the field exists. A no-work note makes
   * `noteCount` 1, so the date stops being a gap on its own — accidentally
   * correct, and wrong at the very next question: MOB-19's pill counts *"any gap
   * or partial day"*, and the only reading of *partial* consistent with this
   * system's states is a day holding notes with no daily log. A no-work Saturday
   * matches that exactly, so a count-derived answer renders it solid `--warn` and
   * counts it in the pill — the app accusing somebody of a backlog one tap after
   * they cleared it.
   *
   * `noteCount` and `entryCount` still count the no-work note, because MOB-22
   * renders it as an entry card. The header's *wording* comes from here; the
   * number comes from the count. They answer different questions and are
   * allowed to differ.
   *
   * @see project_intelligence/data-model/0010-a-day-can-be-recorded-as-no-work.md §4
   */
  noWork: z12.boolean()
});
var JournalDaysResponseSchema = z12.object({
  days: z12.array(JournalDaySummarySchema),
  /**
   * The project's start date, or null.
   *
   * It is optional at setup and nothing enforces it, so null is ordinary. The
   * calendar falls back to the earliest recorded date and then to today — never
   * to an arbitrary lookback, because an orange *Not logged* outline on a day
   * before the site existed is a gap that was never a gap.
   */
  windowStart: IsoDateSchema.nullable()
});
var PhotoDescriptionStatusSchema = ModelPassStatusSchema;
var PhotoKindSchema = z12.enum(["photo", "screenshot"]);
var JournalPhotoSchema = z12.object({
  id: z12.string().uuid(),
  entryId: z12.string().uuid(),
  /**
   * The note this photograph is evidence for, or null when it belongs to the
   * day and nothing narrower.
   *
   * The day keeps the whole set — `entryId` is still required — so a client
   * showing the day does not have to assemble it from notes. This is what lets
   * a note show only its own pictures, which is the distinction a note covering
   * a delivery *and* an incident needs.
   */
  noteId: z12.string().uuid().nullable(),
  filename: z12.string(),
  mimeType: z12.string(),
  sizeBytes: z12.number().int().nonnegative(),
  /** The user's own words. A model never writes here. */
  caption: z12.string().nullable(),
  widthPx: z12.number().int().positive().nullable(),
  heightPx: z12.number().int().positive().nullable(),
  kind: PhotoKindSchema,
  /** What the model saw. Null until described, or when none was available. */
  description: z12.string().nullable(),
  /** Text the model read *inside* the image — a screenshot's whole value. */
  visibleText: z12.string().nullable(),
  descriptionStatus: PhotoDescriptionStatusSchema,
  /** Whether a downscale exists, so the grid knows which URL to ask for. */
  hasThumbnail: z12.boolean(),
  position: z12.number().int().nonnegative(),
  createdAt: z12.string()
});
var JournalPhotosResponseSchema = z12.object({
  photos: z12.array(JournalPhotoSchema)
});
var JournalNoteDetailSchema = z12.object({
  note: JournalNoteSchema,
  photos: z12.array(JournalPhotoSchema)
});
var JournalPhotoResponseSchema = z12.object({ photo: JournalPhotoSchema });
var PhotoUploadUrlInputSchema = z12.object({
  /**
   * The photograph's id, minted by the client. Optional: the console's
   * uploader leaves it to the server.
   *
   * The phone mints it so a note can name its photographs before either has
   * reached the server — with no signal, the note and its pictures are
   * queued together and land in either order (`data-model/0007`, amended
   * 2026-10-03). It is also the collision a retry needs: a reserve whose
   * reply was lost, sent again with the same id, answers with the same row
   * rather than reserving a second photograph (`architecture/0009` §3).
   */
  id: z12.string().uuid().optional(),
  filename: z12.string().trim(),
  mimeType: z12.string().trim().min(1).max(255),
  sizeBytes: z12.number().int(),
  caption: z12.string().trim().max(2e3).optional(),
  /** Read off the image by the browser before upload, for grid layout. */
  widthPx: z12.number().int().positive().max(1e5).optional(),
  heightPx: z12.number().int().positive().max(1e5).optional(),
  /**
   * Whether the browser managed to produce a downscale it will PUT separately.
   *
   * Client-generated because the API has no image library and deliberately does
   * not gain one (`document-ai/0001` §2.10 rejected `sharp` for Alpine). It is
   * a display convenience only — the original is what is stored, described and
   * downloaded — so a browser that cannot make one simply says false.
   */
  withThumbnail: z12.boolean().optional()
}).superRefine((file, ctx) => {
  const refused = fileRefusal(file);
  if (refused) refuse(ctx, refused.reason);
});
var PhotoUploadUrlResponseSchema = z12.object({
  photoId: z12.string().uuid(),
  uploadUrl: z12.string().url(),
  /** Present when `withThumbnail` was asked for. */
  thumbnailUploadUrl: z12.string().url().nullable(),
  /** Seconds. */
  expiresIn: z12.number().int().positive()
});
var PhotoPatchSchema = z12.object({ caption: z12.string().trim().max(2e3).nullable() }).refine((v) => Object.keys(v).length > 0, "No fields to update");
var MULTIPART_THRESHOLD_BYTES = 8 * 1024 * 1024;
var MULTIPART_PART_BYTES = 5 * 1024 * 1024;
function planUpload(sizeBytes) {
  if (sizeBytes <= MULTIPART_THRESHOLD_BYTES) return { mode: "single" };
  return {
    mode: "multipart",
    partSize: MULTIPART_PART_BYTES,
    partCount: Math.ceil(sizeBytes / MULTIPART_PART_BYTES)
  };
}
function partRange(partNumber, partSize, totalBytes) {
  const start = (partNumber - 1) * partSize;
  return { start, end: Math.min(start + partSize, totalBytes) };
}
var PhotoMultipartUrlResponseSchema = z12.object({
  photoId: z12.string().uuid(),
  uploadId: z12.string(),
  partSize: z12.number().int().positive(),
  partCount: z12.number().int().positive(),
  /** Present when `withThumbnail` was asked for. Always a single `PUT` — a
   *  480px downscale is tens of kilobytes and splitting it is absurd. */
  thumbnailUploadUrl: z12.string().url().nullable(),
  expiresIn: z12.number().int().positive()
});
var PhotoUploadPartsInputSchema = z12.object({
  uploadId: z12.string().min(1).max(512),
  partNumbers: z12.array(z12.number().int().min(1).max(1e4)).min(1).max(100)
});
var PhotoUploadPartsResponseSchema = z12.object({
  parts: z12.array(
    z12.object({ partNumber: z12.number().int().min(1), url: z12.string().url() })
  ),
  expiresIn: z12.number().int().positive()
});
var CompletePhotoSchema = z12.object({
  multipart: z12.object({
    uploadId: z12.string().min(1).max(512),
    parts: z12.array(
      z12.object({
        partNumber: z12.number().int().min(1).max(1e4),
        etag: z12.string().min(1).max(256)
      })
    ).min(1).max(1e4)
  }).optional()
});
var DICTATION_AUDIO_TYPES = {
  "audio/mp4": ["m4a", "mp4"],
  "audio/x-m4a": ["m4a"],
  "audio/aac": ["aac"],
  "audio/mpeg": ["mp3"],
  "audio/wav": ["wav"],
  "audio/x-wav": ["wav"],
  "audio/webm": ["webm"],
  "audio/ogg": ["ogg"],
  "audio/flac": ["flac"]
};
var DictationStorageStatusSchema = DocumentStorageStatusSchema;
var ClarificationAnswerSchema = z12.object({
  chosenIndex: z12.number().int().min(0).max(3).nullable(),
  typedText: z12.string().trim().min(1).max(300).nullable(),
  answeredAt: z12.string()
});
var DictationClarificationSchema = z12.object({
  id: z12.string().uuid(),
  /** Plain site-manager language: "18 or 80 people on site?" */
  question: z12.string().trim().min(1).max(300),
  /** The transcript sentence it is about, verbatim — `sourceQuote`'s rule:
   *  a model that cannot point at the sentence says null, never a
   *  paraphrase. */
  quote: z12.string().trim().min(1).max(500).nullable(),
  /** Candidate readings, the heard one included. The composer always adds
   *  type-it-yourself; these are the one-tap answers. */
  options: z12.array(z12.string().trim().min(1).max(120)).min(2).max(4),
  answer: ClarificationAnswerSchema.nullable()
});
var MAX_DICTATION_CLARIFICATIONS = 3;
var AnswerClarificationSchema = z12.object({
  chosenIndex: z12.number().int().min(0).max(3).nullish(),
  typedText: z12.string().trim().min(1).max(300).nullish()
}).superRefine((value, ctx) => {
  const chosen = value.chosenIndex !== null && value.chosenIndex !== void 0;
  const typed = value.typedText !== null && value.typedText !== void 0;
  if (chosen === typed) {
    ctx.addIssue({
      code: z12.ZodIssueCode.custom,
      message: "Answer with exactly one of chosenIndex or typedText"
    });
  }
});
var JournalDictationSchema = z12.object({
  id: z12.string().uuid(),
  projectId: z12.string().uuid(),
  /** The day the dictation is about, client-stated: the phone knows which
   *  day's record it was capturing for, and "this morning" resolves there. */
  date: IsoDateSchema,
  filename: z12.string(),
  mimeType: z12.string(),
  sizeBytes: z12.number().int().positive(),
  /** Client-measured; display and eval only. Null when the client did not say. */
  durationSeconds: z12.number().int().min(0).max(3600).nullable(),
  storageStatus: DictationStorageStatusSchema,
  /** Stage 1, audio → text. `skipped` means no transcription model is bound,
   *  which is a supported configuration and not a failure. */
  transcriptionStatus: ModelPassStatusSchema,
  /** Stage 2, transcript → suggestion. `skipped` when no extractor is bound —
   *  or when stage 1 produced nothing for it to read. */
  extractionStatus: ModelPassStatusSchema,
  /**
   * Stage 1's output, verbatim. This is what the composer pours into the
   * work-done field — the speaker's own words as recognised, never a model's
   * rewriting of them — and the user edits from there.
   */
  transcript: z12.string().nullable(),
  /** Stage 2's reading of the transcript, re-validated against the contract
   *  before it was stored. Null until extraction has run. */
  suggestion: NoteExtractionSuggestionSchema.nullable(),
  /** The question pass (`architecture/0025`): `skipped` = no model bound or
   *  nothing to read; `done` with an empty list = the dictation was clear,
   *  which is a good answer. */
  clarificationStatus: ModelPassStatusSchema,
  /** The questions worth asking, with whatever answers exist. */
  clarifications: z12.array(DictationClarificationSchema).max(MAX_DICTATION_CLARIFICATIONS),
  createdAt: z12.string(),
  updatedAt: z12.string()
});
function isDictationSettled(dictation) {
  return dictation.transcriptionStatus !== "pending" && dictation.extractionStatus !== "pending" && dictation.clarificationStatus !== "pending";
}
function recordingClock(elapsedSeconds) {
  const whole = Math.max(0, Math.floor(elapsedSeconds));
  const minutes = Math.floor(whole / 60);
  const seconds = whole % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
function capCountdown(elapsedSeconds) {
  const remaining = MAX_DICTATION_SECONDS - Math.floor(elapsedSeconds);
  if (remaining > 60) return null;
  const cap = `${MAX_DICTATION_SECONDS / 60} minutes`;
  if (remaining <= 0) return `Recording stopped \u2014 a note is at most ${cap}.`;
  return `Recording stops in ${remaining}s \u2014 a note is at most ${cap}.`;
}
function atRecordingCap(elapsedSeconds) {
  return elapsedSeconds >= MAX_DICTATION_SECONDS;
}
var JournalDictationResponseSchema = z12.object({
  dictation: JournalDictationSchema
});
var DictationUploadInputSchema = z12.object({
  /**
   * **Minted by the client**, like a note's id — the phone's outbox retries
   * against loss, and the id is what makes a retried reserve find its own row
   * instead of minting a second recording. `JournalNoteFieldsSchema.id`
   * carries the full argument.
   */
  id: z12.string().uuid(),
  date: IsoDateSchema,
  filename: z12.string().trim(),
  mimeType: z12.string().trim().min(1).max(255),
  sizeBytes: z12.number().int(),
  durationSeconds: z12.number().int().min(0).optional(),
  /**
   * What this recording is for (MOB-84).
   *
   * `note` runs the full two-stage pipeline — transcription, then extraction
   * and the clarifying questions. `question` is speech into Ask Brain:
   * transcription only, because the transcript goes into the question box for
   * confirmation and nothing structured is being filed. Encoded at reserve
   * time as the extraction stage being born `skipped`, so the pipeline needs
   * no second code path.
   */
  purpose: z12.enum(["note", "question"]).default("note")
}).superRefine((recording, ctx) => {
  if (recording.sizeBytes > MAX_DICTATION_BYTES || (recording.durationSeconds ?? 0) > MAX_DICTATION_SECONDS) {
    refuse(ctx, DICTATION_TOO_LONG);
    return;
  }
  const refused = fileRefusal(recording);
  if (refused) refuse(ctx, refused.reason);
});
var DictationUploadResponseSchema = z12.object({
  dictationId: z12.string().uuid(),
  uploadUrl: z12.string().url(),
  /**
   * The exact `Content-Type` the PUT must send. It is signed into the URL,
   * and it is the *normalised* type, not the one the client asked with —
   * MediaRecorder's `audio/webm;codecs=opus` would fail the signature it
   * helped create. Echoed so the uploader never has to re-derive it.
   */
  contentType: z12.string(),
  /** Seconds. */
  expiresIn: z12.number().int().positive()
});
var WEATHER_LABELS = {
  clear: "Clear",
  "partial-sun": "Partial sun",
  rain: "Rain",
  "high-wind": "High wind",
  fog: "Fog"
};
var WEATHER_LABELS_SHORT = {
  clear: "Clear",
  "partial-sun": "Cloudy",
  rain: "Rain",
  "high-wind": "Wind",
  fog: "Fog"
};
var DELIVERY_CONDITION_LABELS = {
  ok: "OK",
  damaged: "Damaged",
  "wrong-spec": "Wrong spec",
  partial: "Partial",
  rejected: "Rejected"
};
var EXTRACTED_DELIVERY_CONDITION_LABELS = {
  ok: DELIVERY_CONDITION_LABELS.ok,
  damaged: DELIVERY_CONDITION_LABELS.damaged,
  "wrong-spec": DELIVERY_CONDITION_LABELS["wrong-spec"],
  unknown: "Not stated"
};
var EXTRACTED_DELIVERY_CONDITION_LABELS_STANDALONE = {
  ...EXTRACTED_DELIVERY_CONDITION_LABELS,
  ok: "Condition OK",
  unknown: "Condition not recorded"
};
var INCIDENT_SEVERITY_LABELS = {
  none: "None",
  minor: "Minor",
  reportable: "Reportable"
};
var INCIDENT_SEVERITY_LABELS_CAPTURE = {
  minor: "Minor",
  reportable: "Serious",
  none: "Near-miss"
};
var EXTRACTED_INCIDENT_SEVERITY_LABELS = {
  ...INCIDENT_SEVERITY_LABELS,
  unknown: "Severity not recorded"
};
var EXTRACTED_INCIDENT_SEVERITY_LABELS_CAPTURE = {
  ...INCIDENT_SEVERITY_LABELS_CAPTURE,
  unknown: "Not stated"
};
var INSPECTION_RESULT_LABELS = {
  pass: "Pass",
  "pass-with-notes": "Pass with notes",
  fail: "Fail"
};
function formatJournalPhotoCitation(input) {
  const what = input.kind === "screenshot" ? "Screenshot" : "Site photo";
  const filename = input.filename?.trim();
  return filename ? `${what}, ${input.date} \u2014 ${filename}` : `${what}, ${input.date}`;
}
var SEVERITY_ORDER = ["none", "minor", "reportable"];
function worstIncidentSeverity(incidents) {
  return incidents.reduce(
    (worst, i) => SEVERITY_ORDER.indexOf(i.severity) > SEVERITY_ORDER.indexOf(worst) ? i.severity : worst,
    "none"
  );
}
var journalsContract = c8.router({
  list: {
    method: "GET",
    path: "/projects/:id/journal",
    responses: {
      200: JournalEntriesResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "The site's journal, newest day first"
  },
  // ---------------- Notes ----------------
  //
  // Declared **before** `get`, and the order is load-bearing: `/journal/notes`
  // and `/journal/days` would otherwise be matched by `/journal/:entryId` and
  // answer 404 for an entry id that is the word "notes". Nest registers routes
  // in handler-declaration order, so `JournalsController` keeps the same order.
  //
  // These are the contract's first `query` blocks. `from`/`to` arrive as
  // strings, and `IsoDateSchema` already validates strings, so no coercion is
  // needed here — unlike `brain.list`'s numeric `limit`/`offset`.
  listNotes: {
    method: "GET",
    path: "/projects/:id/journal/notes",
    query: z12.object({
      /** Inclusive. Omit both for everything, matching `list`. */
      from: IsoDateSchema.optional(),
      to: IsoDateSchema.optional(),
      /**
       * Substring, case-insensitive. Browse and filter, not retrieval — the
       * field says "Search", not "Ask".
       *
       * **Spans what the card shows, not only the body.** MOB-21: *"Filters the
       * entry list on the entry text, the metadata line (which carries the
       * author's name, headcount and weather) and the kind badge."* So the
       * columns are `body`, the author's name, the weather value and the kind —
       * typing a crew member's name, or `rain`, or `no work`, finds days. It was
       * `body` alone until 2026-08-20, which made the placeholder *"Search
       * entries, crew, kit…"* a promise the query did not keep.
       *
       * Headcount is deliberately absent: it is a number, and matching `18`
       * against a substring would also match every note mentioning 18 rails.
       *
       * Matching happens against the **stored** weather value, not its label —
       * `WEATHER_LABELS_SHORT.partial-sun` is *Cloudy* on the phone and
       * *Partial sun* on the console, and a search that agreed with one surface
       * would disagree with the other.
       */
      q: z12.string().trim().max(200).optional()
    }),
    responses: {
      200: JournalNotesResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Notes filed against this site's dates, newest first"
  },
  createNote: {
    method: "POST",
    path: "/projects/:id/journal/notes",
    body: JournalNoteInputSchema,
    responses: {
      201: JournalNoteCreatedSchema,
      // **This note is already filed** — the caller sent an `id` that exists.
      // A retry after a lost reply, which on site signal is the ordinary case
      // rather than the exceptional one, so it is a success and not a 409.
      //
      // Same body as the 201, read back from the row, so a client that never
      // saw the first answer still gets one. Distinguished by status because
      // "filed" and "filed again just now" are different things to say to
      // somebody, and because a client that finds itself here every time has a
      // bug worth seeing.
      200: JournalNoteCreatedSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      // **Not** the "one entry per day" 409 that `create` carries. Several
      // notes against one date is the point, and flagging a second daily record
      // *replaces* the first rather than colliding with it. This is the narrow
      // case where two people do that in the same instant: the partial unique
      // index refuses the loser, nothing is half-written, and telling them
      // somebody got there first beats a 500 that says nothing.
      409: ErrorSchema
    },
    summary: "File a note, and optionally make it the day's record"
  },
  getNote: {
    method: "GET",
    path: "/projects/:id/journal/notes/:noteId",
    responses: {
      200: JournalNoteDetailSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    // MOB-62: the card in the feed is a summary, this is the screen behind it.
    summary: "One note, with the photographs filed against it"
  },
  updateNote: {
    method: "PATCH",
    path: "/projects/:id/journal/notes/:noteId",
    body: JournalNoteUpdateSchema,
    responses: {
      200: JournalNoteUpdatedSchema,
      // Refusals with a sentence: a no-work declaration has nothing to edit,
      // a daily record cannot move to another day, a note with claimed
      // photographs is anchored to the day its evidence is filed on.
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      // Two different notes claimed the same date's record in the same
      // instant — `create`'s own race, reachable here through promotion.
      409: ErrorSchema
    },
    // MOB-63/64: every edit writes `journal_note_edits` (who, when, before,
    // after — prior versions retained, history not surfaced), and setting
    // `isDailyEntry` promotes the note to the day's record, replacing and
    // demoting the standing one exactly as filing with the flag does.
    summary: "Amend a filed note, audited; may promote it to the day's record"
  },
  extractFromDraft: {
    method: "POST",
    path: "/projects/:id/journal/notes/extract",
    body: NoteExtractionInputSchema,
    responses: {
      200: NoteExtractionSuggestionSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    // Reads a draft and suggests; writes nothing. It is called while the note
    // is still being composed, because the suggestion has to be on screen in
    // time for somebody to correct it — extraction that lands after the save
    // has nobody left to check it, and lands in a record that gets signed off.
    //
    // 200 rather than 201: nothing was created.
    summary: "Suggest deliveries and incidents from a draft \u2014 a suggestion, not a record"
  },
  days: {
    method: "GET",
    path: "/projects/:id/journal/days",
    query: z12.object({
      from: IsoDateSchema.optional(),
      to: IsoDateSchema.optional()
    }),
    responses: {
      200: JournalDaysResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Per-date facts for the calendar, the queue and the feed's headers"
  },
  // ---------------- Dictations ----------------
  //
  // Declared before `get` for the reason notes are: `/journal/dictations`
  // must not be matched by `/journal/:entryId`. Same presign → PUT → complete
  // shape as photographs; what differs is what happens after complete — the
  // two-stage pipeline runs out of band, the two status columns report it, and
  // `getDictation` is the poll that collects it.
  dictationUploadUrl: {
    method: "POST",
    path: "/projects/:id/journal/dictations",
    body: DictationUploadInputSchema,
    responses: {
      201: DictationUploadResponseSchema,
      // The reserve already exists and its bytes have not landed — a retry
      // after a lost reply, `createNote`'s rule. A fresh URL for the same row.
      200: DictationUploadResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      // The id exists and its bytes already landed: not a retried reserve but
      // a second recording wearing an old id — a client bug worth refusing
      // loudly rather than silently overwriting evidence.
      409: ErrorSchema,
      413: ErrorSchema
    },
    summary: "Reserve a dictation row and presign the audio PUT"
  },
  completeDictation: {
    method: "POST",
    path: "/projects/:id/journal/dictations/:dictationId/complete",
    body: z12.object({}),
    responses: {
      200: JournalDictationResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema,
      // The reserve's size was a claim; HeadObject is the measurement, so
      // oversize can only be caught here — the object is deleted and the row
      // marked failed, and the same id may then be re-reserved.
      413: ErrorSchema
    },
    // Answers as soon as the bytes are verified, exactly as `completePhoto`
    // does: transcription is seconds, extraction more, and no request holds
    // that open. Idempotent on retry, like every complete.
    summary: "Verify the audio landed, then transcribe and extract out of band"
  },
  answerClarification: {
    method: "POST",
    path: "/projects/:id/journal/dictations/:dictationId/clarifications/:clarificationId",
    body: AnswerClarificationSchema,
    responses: {
      200: JournalDictationResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    // Answering is idempotent-by-overwrite: the latest answer stands, and
    // who answered and when is recorded — the corpus's whole point.
    summary: "Answer one clarifying question, by choice or in your own words"
  },
  getDictation: {
    method: "GET",
    path: "/projects/:id/journal/dictations/:dictationId",
    responses: {
      200: JournalDictationResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    // The poll the composer sits on after `complete`. Also where a pipeline
    // orphaned by a restart gets picked back up — the photo listing's sweep,
    // applied to the one row being watched.
    summary: "One dictation, with its transcript and suggestion when ready"
  },
  get: {
    method: "GET",
    path: "/projects/:id/journal/:entryId",
    responses: {
      200: JournalEntryResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "One day's record"
  },
  create: {
    method: "POST",
    path: "/projects/:id/journal",
    body: JournalEntryInputSchema,
    responses: {
      201: JournalEntryResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      // A real uniqueness collision — one entry per site per day is enforced by
      // the database. Unlike the 409 that `POST /projects` used to carry, which
      // was a workaround for a resolution rule rather than a fact about the data.
      409: ErrorSchema
    },
    summary: "Open a day. Only `date` is required. 409 if that day already has one"
  },
  /**
   * Open a day if it is not open already, and answer with it either way.
   *
   * Idempotent by design, and the reason it exists rather than the client
   * catching `create`'s 409: dropping photographs onto a date must not depend
   * on whether somebody has written that day up yet. "Give me this day" is one
   * intent; expressing it as create-then-recover-from-conflict puts a race
   * between two people photographing the same morning.
   */
  openDay: {
    method: "PUT",
    path: "/projects/:id/journal/days/:date",
    body: z12.object({}),
    responses: {
      200: JournalEntryResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Get or create the day's record. Idempotent"
  },
  patch: {
    method: "PATCH",
    path: "/projects/:id/journal/:entryId",
    body: JournalEntryPatchSchema,
    responses: {
      200: JournalEntryResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Amend a day's record. The date itself is not editable"
  },
  approve: {
    method: "POST",
    path: "/projects/:id/journal/:entryId/approve",
    body: JournalApproveSchema,
    responses: {
      200: JournalEntryResponseSchema,
      // Now load-bearing: an incomplete day is refused here, which is what
      // keeps `headcount`, `weather` and `workCompleted` being nullable from
      // meaning a record can be signed off with holes in it. See
      // `missingForSignOff`.
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    // Its own route rather than `approved: true` through `patch`: sign-off is a
    // distinct intent with audit meaning, and folding it into the general edit
    // path would make it just another field a stray form submit could flip.
    summary: "Sign off a *complete* day's record and record who it went to"
  },
  remove: {
    method: "DELETE",
    path: "/projects/:id/journal/:entryId",
    body: c8.noBody(),
    responses: {
      204: c8.noBody(),
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Delete a day's record, and every photograph on it"
  },
  // ---------------- Photographs ----------------
  //
  // Same three-call upload as project documents and crew certificates —
  // presign, direct PUT to S3, complete — for the same reason: the bytes never
  // pass through the API. What differs is the second URL, since the browser
  // also PUTs the downscale it made.
  listPhotos: {
    method: "GET",
    path: "/projects/:id/journal/:entryId/photos",
    responses: {
      200: JournalPhotosResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    // Also where a description orphaned by a restart gets picked back up — the
    // list is the one call a client makes repeatedly while photos are landing,
    // exactly as the document listing is for parse jobs.
    summary: "The day's photographs, in display order"
  },
  photoUploadUrl: {
    method: "POST",
    path: "/projects/:id/journal/:entryId/photos/upload-url",
    body: PhotoUploadUrlInputSchema,
    responses: {
      201: PhotoUploadUrlResponseSchema,
      // **This photograph is already reserved** — the client sent an `id` the
      // day already holds, a retry after a lost reply. Fresh URLs for the same
      // row, `createNote`'s rule applied to a reserve.
      200: PhotoUploadUrlResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      413: ErrorSchema
    },
    summary: "Reserve a photo row and presign the PUTs"
  },
  photoMultipartUrl: {
    method: "POST",
    path: "/projects/:id/journal/:entryId/photos/multipart",
    body: PhotoUploadUrlInputSchema,
    responses: {
      201: PhotoMultipartUrlResponseSchema,
      // Already reserved under this `id`: a new multipart upload for the same
      // row. Parts sent to the old one are reaped by the bucket's lifecycle rule.
      200: PhotoMultipartUrlResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      413: ErrorSchema
    },
    // Its own route rather than a discriminated union on `photoUploadUrl`'s
    // response: that would change the shape the dashboard already reads, to
    // express something only the phone asks for. The client knows the file size
    // before it calls, so `planUpload` picks the route with no round trip.
    summary: "Reserve a photo row and begin a resumable multipart upload"
  },
  photoUploadParts: {
    method: "POST",
    path: "/projects/:id/journal/:entryId/photos/:photoId/parts",
    body: PhotoUploadPartsInputSchema,
    responses: {
      200: PhotoUploadPartsResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema
    },
    summary: "Fresh URLs for the parts still outstanding. Callable repeatedly"
  },
  completePhoto: {
    method: "POST",
    path: "/projects/:id/journal/:entryId/photos/:photoId/complete",
    // Optional `multipart`, so the single-`PUT` path still sends `{}` and is
    // entirely unchanged. Both transports finish here.
    body: CompletePhotoSchema,
    responses: {
      200: JournalPhotoResponseSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema
    },
    // Answers as soon as the bytes are verified. Description happens afterwards
    // and `descriptionStatus` is what reports it — a vision call is seconds and
    // a grid of twelve would be minutes, which no request can hold open.
    summary: "Verify the bytes landed, then describe the photo out of band"
  },
  patchPhoto: {
    method: "PATCH",
    path: "/projects/:id/journal/:entryId/photos/:photoId",
    body: PhotoPatchSchema,
    responses: {
      200: JournalPhotoResponseSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema
    },
    summary: "Caption a photo. The model's own fields are not writable"
  },
  removePhoto: {
    method: "DELETE",
    path: "/projects/:id/journal/:entryId/photos/:photoId",
    // See `deleteDocument` — a bodyless DELETE must not be a 400.
    body: c8.noBody(),
    responses: { 204: c8.noBody(), 401: ErrorSchema, 404: ErrorSchema },
    summary: "Delete a photo, its objects and its Brain entry"
  }
});

// src/debug.ts
import { initContract as initContract9 } from "@ts-rest/core";
import { z as z13 } from "zod";
var c9 = initContract9();
var LlmRunPictureSchema = z13.object({
  mimeType: z13.string(),
  bytes: z13.number().int(),
  widthPx: z13.number().int().nullable(),
  heightPx: z13.number().int().nullable()
});
var LlmRunImageSchema = z13.object({
  action: z13.enum(["unchanged", "re-encoded", "resized", "not-sent"]),
  before: LlmRunPictureSchema,
  after: LlmRunPictureSchema.nullable()
});
var DebugRunSummarySchema = z13.object({
  id: z13.string().uuid(),
  touchpoint: z13.string(),
  provider: z13.string(),
  model: z13.string(),
  /** `ok`, `refusal`, `max_tokens`, `schema_miss`, `error` or `skipped`. */
  status: z13.string(),
  /** 0 means the code default ran. */
  promptVersion: z13.number().int(),
  /** Which table served the prompt: `tenant`, `platform`, or null for the code default. */
  promptScope: z13.string().nullable(),
  subject: z13.object({ kind: z13.string(), id: z13.string() }).nullable(),
  /** Tokens, as the provider counted them. Nothing here is priced. */
  inputTokens: z13.number().int().nullable(),
  outputTokens: z13.number().int().nullable(),
  cacheReadTokens: z13.number().int().nullable(),
  cacheWriteTokens: z13.number().int().nullable(),
  latencyMs: z13.number().int(),
  hasThinking: z13.boolean(),
  /**
   * Class and HTTP status of a failed call, such as "BadRequestError (HTTP 400,
   * invalid_request_error)". Null when the call did not fail. In the list, so
   * a failure can be read without opening the call.
   */
  errorClass: z13.string().nullable(),
  createdAt: z13.string()
});
var DebugRunSchema = DebugRunSummarySchema.extend({
  /** The prompt as sent, capped when it was recorded. */
  promptText: z13.string().nullable(),
  /**
   * The system prompt as sent, capped when it was recorded. Kept for every call
   * since 2026-09-25 (`architecture/0029` §3). Null for a call recorded before
   * that, other than the retired source selector's (gone 2026-09-27).
   */
  systemText: z13.string().nullable(),
  /** The parsed output, or its stats for a streamed answer. */
  output: z13.record(z13.unknown()).nullable(),
  /** The model's summarised thinking, where it returned any. */
  thinking: z13.string().nullable(),
  /**
   * The provider's own message when the call failed, with its request id:
   * capped at 2,000 characters and redacted when it was recorded
   * (`architecture/0030`). Never in a log line. Null for a call recorded
   * before 2026-09-25.
   */
  errorMessage: z13.string().nullable(),
  /** Pictures sent. 0 is a call with none; null is a call recorded before it was counted. */
  imageCount: z13.number().int().nullable(),
  /** Each picture as stored and as sent. Null for a call with none, or recorded before 2026-09-25. */
  images: z13.array(LlmRunImageSchema).nullable()
});
var DebugStepSchema = z13.object({
  id: z13.string().uuid(),
  runId: z13.string().uuid(),
  attempt: z13.number().int(),
  step: z13.string(),
  outcome: z13.string(),
  reason: z13.string().nullable(),
  detail: z13.record(z13.unknown()).nullable(),
  documentId: z13.string().uuid().nullable(),
  importId: z13.string().uuid().nullable(),
  startedAt: z13.string(),
  finishedAt: z13.string()
});
var DebugChangeSchema = z13.object({
  id: z13.string().uuid(),
  kind: z13.string(),
  summary: z13.string(),
  madeBy: MadeBySchema,
  importId: z13.string().uuid().nullable(),
  documentId: z13.string().uuid().nullable(),
  undoesId: z13.string().uuid().nullable(),
  before: z13.record(z13.unknown()).nullable(),
  after: z13.record(z13.unknown()).nullable(),
  createdAt: z13.string()
});
var DebugReferenceSchema = z13.object({
  value: z13.string(),
  normalised: z13.string(),
  own: z13.boolean(),
  kind: z13.string()
});
var DebugDocumentSchema = z13.object({
  file: z13.object({
    id: z13.string().uuid(),
    filename: z13.string(),
    mimeType: z13.string(),
    sizeBytes: z13.number(),
    checksumSha256: z13.string().nullable(),
    source: z13.string(),
    sourceRef: z13.string().nullable(),
    sourcePath: z13.string().nullable(),
    parentDocumentId: z13.string().uuid().nullable(),
    importId: z13.string().uuid().nullable(),
    storageStatus: z13.string(),
    createdAt: z13.string()
  }),
  /** The card as stored, including what the person's view leaves out. */
  card: z13.object({
    title: z13.string().nullable(),
    docType: z13.string().nullable(),
    language: z13.string().nullable(),
    documentDate: z13.string().nullable(),
    revision: z13.string().nullable(),
    howToRead: z13.string().nullable(),
    summary: z13.string().nullable(),
    /** Each as "name (role as written)" (2026-09-26, LIN-16). */
    parties: z13.array(z13.string()).nullable(),
    /**
     * Each signature line, stamp or signing record the card read, signed or
     * empty, with its organisation (2026-09-26, CON-06, the lighter approach).
     */
    signatures: z13.array(CardSignatureSchema).nullable(),
    aboutProject: z13.string().nullable(),
    /** The place of the site, as the file writes it (2026-09-26, LIN-24). */
    site: z13.string().nullable(),
    siteCountry: z13.string().nullable(),
    projectNames: z13.array(z13.string()).nullable(),
    /**
     * The categories as stored, keys as written (`document-ai/0032`): the
     * main one, who set it, and the others. A key no longer in the tenant's
     * list shows here as it is.
     */
    mainCategory: z13.string().nullable(),
    categoryMadeBy: z13.string().nullable(),
    otherCategories: z13.array(z13.string()),
    madeBy: z13.string().nullable(),
    cardedAt: z13.string().nullable()
  }),
  references: z13.array(DebugReferenceSchema),
  /** Why it sits where it does. */
  placement: z13.object({
    family: z13.object({
      id: z13.string().uuid().nullable(),
      name: z13.string().nullable(),
      series: z13.boolean(),
      role: z13.string(),
      related: DocumentRefSchema.nullable(),
      madeBy: z13.string().nullable(),
      reason: z13.string().nullable(),
      placedAt: z13.string().nullable(),
      question: PlacementQuestionSchema.nullable()
    }),
    status: z13.object({
      status: z13.string().nullable(),
      reason: z13.string().nullable(),
      madeBy: z13.string().nullable(),
      checkedAt: z13.string().nullable(),
      /** The check's question, as stored: the file ids, not their names (2026-09-26). */
      question: StatusQuestionSchema.nullable()
    }),
    /** When the links step last read the file. Null means it has not. */
    linkedAt: z13.string().nullable()
  }),
  /** Every link, in and out, in every state. */
  links: z13.array(DocumentLinkSchema),
  steps: z13.array(DebugStepSchema),
  changes: z13.array(DebugChangeSchema),
  /** The file's own model calls, then its import's. */
  runs: z13.array(DebugRunSummarySchema)
});
var DebugImportSchema = z13.object({
  steps: z13.array(DebugStepSchema),
  runs: z13.array(DebugRunSummarySchema)
});
var DebugThreadSchema = z13.object({ runs: z13.array(DebugRunSchema) });
var debugContract = c9.router({
  document: {
    method: "GET",
    path: "/projects/:id/debug/documents/:docId",
    responses: { 200: DebugDocumentSchema, 401: ErrorSchema, 404: ErrorSchema },
    summary: "Everything recorded about one file. 404 without the debug flag"
  },
  import: {
    method: "GET",
    path: "/projects/:id/debug/imports/:importId",
    responses: { 200: DebugImportSchema, 401: ErrorSchema, 404: ErrorSchema },
    summary: "One import's steps and model calls. 404 without the debug flag"
  },
  thread: {
    method: "GET",
    path: "/projects/:id/debug/threads/:threadId",
    responses: { 200: DebugThreadSchema, 401: ErrorSchema, 404: ErrorSchema },
    summary: "The model calls behind one answer. 404 without the debug flag"
  },
  run: {
    method: "GET",
    path: "/projects/:id/debug/runs/:runId",
    responses: { 200: z13.object({ run: DebugRunSchema }), 401: ErrorSchema, 404: ErrorSchema },
    summary: "One model call, whole: prompt, output, thinking, tokens. 404 without the debug flag"
  }
});

// src/sharepoint.ts
import { initContract as initContract10 } from "@ts-rest/core";
import { z as z14 } from "zod";
var c10 = initContract10();
var SHAREPOINT_REFUSALS = {
  /** The text is not an address of a SharePoint site at all. */
  notSharepoint: "That isn\u2019t a SharePoint address. Open the folder in SharePoint and copy the address from your browser.",
  /** A personal OneDrive, which the sync does not read (question 32). */
  personalOnedrive: "That\u2019s a personal OneDrive. Link a folder in one of your organisation\u2019s SharePoint sites instead.",
  /** A SharePoint address, but of a file, a page or a whole site. */
  notFolder: "That address isn\u2019t a folder. Open the folder itself in SharePoint and copy its address.",
  /** SharePoint answered that there is nothing there, or nothing GroundBrain may read. */
  notReachable: "GroundBrain can\u2019t open that folder. Check the address, or ask your Microsoft 365 admin to check GroundBrain\u2019s access to SharePoint.",
  /**
   * The folder does not open for the person linking it (2026-10-19): it does
   * not exist, or they cannot open it in SharePoint. One sentence for both,
   * so a guessed address tells nothing.
   */
  notYours: "That folder doesn\u2019t open for you in SharePoint. Check the address, and that you can open the folder yourself.",
  /** The person signed in with an account of another organisation. */
  otherOrganisation: "That Microsoft account isn\u2019t in your organisation\u2019s Microsoft 365. Sign in with your work account.",
  /** The check's sign-in did not match its start, or took too long. */
  checkFailed: "The check with Microsoft didn\u2019t finish. Find the folder again.",
  /** A link asked for without a check of this folder by this person, or after it lapsed. */
  checkMissing: "Find the folder again: GroundBrain checks with Microsoft that you can open it before linking it.",
  /** The deployment has no Microsoft app credentials. */
  notConfigured: "SharePoint isn\u2019t set up for GroundBrain yet.",
  /** The tenant's Microsoft organisation has not been connected. */
  notConnected: "Your organisation\u2019s Microsoft 365 isn\u2019t connected to GroundBrain yet. Ask GroundBrain to send your admin the link.",
  /**
   * A file whose fetch spent its tries (2026-10-17): SharePoint failed for
   * longer than the retries wait. "Sync now" tries it again.
   */
  fetchGaveUp: "GroundBrain couldn\u2019t fetch this file from SharePoint. \u201CSync now\u201D tries again."
};
var SharepointHealthSchema = z14.enum(["waiting", "ok", "failing"]);
var SharepointNotTakenSchema = z14.object({
  name: z14.string(),
  /** Where it is below the linked folder, or null at its top. */
  folder: z14.string().nullable(),
  reason: z14.string()
});
var SharepointLinkSchema = z14.object({
  folderName: z14.string(),
  /** The folder's own address in SharePoint, for "Open in SharePoint". */
  folderUrl: z14.string().url(),
  /** The site and the folder's path in it, as SharePoint names them. */
  location: z14.string(),
  linkedAt: z14.string().datetime(),
  /** Who linked it: a name, or an email address when the account has no name. */
  linkedBy: z14.string().nullable(),
  health: SharepointHealthSchema,
  /** The last poll that reached SharePoint. */
  lastCheckedAt: z14.string().datetime().nullable(),
  failingSince: z14.string().datetime().nullable(),
  /** Why the polls fail, in one sentence. Null unless `failing`. */
  failure: z14.string().nullable(),
  /** Files of the folder the project holds now. */
  files: z14.number().int().nonnegative(),
  /**
   * Files that changed or arrived in the folder and are not fetched yet: each
   * waits until it has been unchanged for a few minutes (question 10), then
   * for its fetch.
   */
  waiting: z14.number().int().nonnegative(),
  /** Files of the folder the sync does not take, each with its reason. */
  notTaken: z14.array(SharepointNotTakenSchema)
});
var SharepointStatusSchema = z14.object({
  /** The deployment has GroundBrain's Microsoft app credentials. */
  configured: z14.boolean(),
  /** The tenant's Microsoft organisation has been connected (question 26). */
  connected: z14.boolean(),
  /** The project's folder, or null when it is not linked. */
  link: SharepointLinkSchema.nullable(),
  /**
   * A poll of the folder is queued or running. The dashboard reads the status
   * again soon and says it is checking (2026-10-18, QA finding 6).
   */
  checking: z14.boolean()
});
var SharepointFolderInputSchema = z14.object({
  url: z14.string().trim().min(1, SHAREPOINT_REFUSALS.notSharepoint).max(4096)
});
var SharepointCheckStartSchema = z14.object({ url: z14.string().url() });
var SharepointPreviewInputSchema = SharepointFolderInputSchema.extend({
  code: z14.string().min(1).max(4096),
  state: z14.string().min(1).max(4096)
});
var SharepointFolderPreviewSchema = z14.object({
  folderName: z14.string(),
  folderUrl: z14.string().url(),
  location: z14.string(),
  /** Files in the folder and its subfolders, at any depth, as far as they were counted. */
  files: z14.number().int().nonnegative(),
  /** False when counting stopped early: there are at least `files`. */
  complete: z14.boolean(),
  /**
   * That this person could open this folder, signed by the API, for 15
   * minutes. The link sends it back. It opens nothing in Microsoft.
   */
  proof: z14.string()
});
var SharepointLinkInputSchema = SharepointFolderInputSchema.extend({
  proof: z14.string().min(1).max(4096)
});
var sharepointContract = c10.router({
  status: {
    method: "GET",
    path: "/projects/:id/sharepoint",
    responses: { 200: SharepointStatusSchema, 401: ErrorSchema, 404: ErrorSchema },
    summary: "The project's SharePoint folder, and how its sync stands"
  },
  checkStart: {
    method: "POST",
    path: "/projects/:id/sharepoint/check",
    body: SharepointFolderInputSchema,
    responses: { 200: SharepointCheckStartSchema, 401: ErrorSchema, 404: ErrorSchema, 503: ErrorSchema },
    summary: "Where to sign in with Microsoft, so GroundBrain can check you can open the folder"
  },
  preview: {
    method: "POST",
    path: "/projects/:id/sharepoint/preview",
    body: SharepointPreviewInputSchema,
    responses: {
      200: SharepointFolderPreviewSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      // The folder overlaps another project's: the same folder, or one inside the other.
      409: ErrorSchema,
      503: ErrorSchema
    },
    summary: "Check, as the person, that the folder opens for them; then count its files. Links nothing"
  },
  link: {
    method: "PUT",
    path: "/projects/:id/sharepoint",
    body: SharepointLinkInputSchema,
    responses: {
      200: SharepointStatusSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema,
      503: ErrorSchema
    },
    summary: "Point the project to this folder. A project already linked is relinked"
  },
  unlink: {
    method: "DELETE",
    path: "/projects/:id/sharepoint",
    // See `deleteDocument`: a bodyless DELETE must not be a 400.
    body: c10.noBody(),
    responses: { 204: c10.noBody(), 401: ErrorSchema, 404: ErrorSchema },
    summary: "Stop syncing. The files stay (question 4)"
  },
  syncNow: {
    method: "POST",
    path: "/projects/:id/sharepoint/sync",
    body: z14.object({}),
    responses: { 202: SharepointStatusSchema, 401: ErrorSchema, 404: ErrorSchema, 409: ErrorSchema },
    summary: "Look for changes now, rather than at the next minute"
  }
});

// src/outlook.ts
import { initContract as initContract11 } from "@ts-rest/core";
import { z as z15 } from "zod";
var c11 = initContract11();
var OUTLOOK_REFUSALS = {
  /** The deployment has no Microsoft app credentials. */
  notConfigured: "Outlook isn\u2019t set up for GroundBrain yet.",
  /** The tenant's Microsoft organisation has not been connected. */
  notConnected: "Your organisation\u2019s Microsoft 365 isn\u2019t connected to GroundBrain yet. Ask GroundBrain to send your admin the link.",
  /** The person has not confirmed their Microsoft account (question 3). */
  notConfirmed: "Confirm your Microsoft account first, so GroundBrain knows which mailbox is yours.",
  /** The account signed in to belongs to another organisation. */
  otherOrganisation: "That Microsoft account isn\u2019t in your organisation\u2019s Microsoft 365. Sign in with your work account.",
  /** Another GroundBrain user confirmed the same Microsoft account first. */
  accountTaken: "That Microsoft account is already confirmed by someone else in GroundBrain.",
  /** The account has no mailbox GroundBrain can read: a guest, or no licence. */
  noMailbox: "GroundBrain can\u2019t find a mailbox for your Microsoft account.",
  /**
   * A folder that holds every project at once, or that is not mail a person
   * filed (question 11). Inbox, Sent Items and Archive as a whole; their
   * subfolders can be chosen.
   */
  wholeMailboxFolder: "This folder holds all your email, not one project\u2019s. Choose a folder you file this project\u2019s email in.",
  systemFolder: "Drafts, Outbox, Deleted Items, Junk Email and other system folders can\u2019t be synced.",
  /** A folder the picker no longer finds in the mailbox. */
  folderGone: "That folder isn\u2019t in your mailbox any more.",
  /**
   * An email whose fetch spent its tries (2026-10-17): Outlook failed for
   * longer than the retries wait. "Sync now" tries it again.
   */
  fetchGaveUp: "GroundBrain couldn\u2019t fetch this email from Outlook. \u201CSync now\u201D tries again.",
  /**
   * The email's content is encrypted or rights-protected (S/MIME, Microsoft
   * Purview): it is kept, and GroundBrain cannot read it (question 17).
   */
  encrypted: "This email is encrypted, so GroundBrain can\u2019t read what it says."
};
function outlookLinkedAttachment(name) {
  return `\u201C${name}\u201D is a link to a file in OneDrive or SharePoint, not an attachment. GroundBrain doesn\u2019t follow links.`;
}
function outlookFolderTaken(projectName) {
  return `That folder already syncs into \u201C${projectName}\u201D. A folder can belong to one project only.`;
}
var MicrosoftAccountSchema = z15.object({
  name: z15.string().nullable(),
  /** The account's sign-in address, as Microsoft gives it. */
  address: z15.string().nullable()
});
var MicrosoftSignInStatusSchema = z15.object({
  /** The deployment has GroundBrain's Microsoft app credentials. */
  configured: z15.boolean(),
  /** The tenant's Microsoft organisation has been connected. */
  connected: z15.boolean(),
  /** The viewer's confirmed Microsoft account, or null when they have not confirmed one. */
  account: MicrosoftAccountSchema.nullable()
});
var MicrosoftAuthUrlSchema = z15.object({ url: z15.string().url() });
var MicrosoftCallbackInputSchema = z15.object({
  code: z15.string().min(1).max(8192),
  state: z15.string().min(1).max(2048)
});
var MailFolderSchema = z15.object({
  id: z15.string(),
  /** The folder it is in, or null at the top of the mailbox. */
  parentId: z15.string().nullable(),
  name: z15.string(),
  /** Emails in the folder itself, not counting its subfolders. */
  emails: z15.number().int().nonnegative(),
  /** Why it cannot be chosen, in one sentence, or null when it can. */
  refusal: z15.string().nullable(),
  /** The project it, or a folder above it, already syncs into. Null when none. */
  syncsInto: z15.object({ projectId: z15.string().uuid(), projectName: z15.string() }).nullable()
});
var MailFoldersSchema = z15.object({
  account: MicrosoftAccountSchema,
  folders: z15.array(MailFolderSchema)
});
var OutlookHealthSchema = z15.enum(["waiting", "ok", "failing"]);
var OutlookNotTakenSchema = z15.object({
  subject: z15.string().nullable(),
  /** Where it is: the chosen folder, then the folders below it. */
  folder: z15.string(),
  reason: z15.string()
});
var OutlookFolderLinkSchema = z15.object({
  id: z15.string().uuid(),
  folderName: z15.string(),
  /** Where it is in its mailbox, from the top: "Inbox / Projekt Lipová". */
  path: z15.string(),
  /**
   * Whose mailbox, in GroundBrain: the person's name, or their email address.
   * Null when that account is gone.
   */
  owner: z15.string().nullable(),
  /**
   * Whose mailbox, as Microsoft names the account the owner confirmed for it:
   * its name, or its address. Null when there is none. Shown beside `owner`
   * (the owner, 2026-10-18, QA finding 5): a GroundBrain name is free text,
   * and this is the identity Microsoft confirmed.
   */
  ownerAccount: z15.string().nullable(),
  /** The viewer's own mailbox. */
  mine: z15.boolean(),
  linkedAt: z15.string().datetime(),
  health: OutlookHealthSchema,
  /** The last poll that reached Outlook. */
  lastCheckedAt: z15.string().datetime().nullable(),
  failingSince: z15.string().datetime().nullable(),
  /** Why the polls fail, in one sentence. Null unless `failing`. */
  failure: z15.string().nullable(),
  /** Emails of the folder and its subfolders that the project holds now. */
  emails: z15.number().int().nonnegative(),
  /** Emails that arrived and are not fetched yet. */
  waiting: z15.number().int().nonnegative(),
  /** Emails of the folder the sync does not take, each with its reason. */
  notTaken: z15.array(OutlookNotTakenSchema)
});
var OutlookStatusSchema = z15.object({
  configured: z15.boolean(),
  connected: z15.boolean(),
  /** The viewer's confirmed Microsoft account, or null. */
  account: MicrosoftAccountSchema.nullable(),
  /** Every folder that syncs into the project, whoever's mailbox it is in. */
  folders: z15.array(OutlookFolderLinkSchema),
  /**
   * A poll of the project's folders is queued or running. The dashboard reads
   * the status again soon and says it is checking, so a folder that "Try now"
   * fixed turns green at once (2026-10-18, QA finding 6).
   */
  checking: z15.boolean()
});
var OutlookChooseInputSchema = z15.object({
  folderIds: z15.array(z15.string().min(1).max(512)).min(1).max(50)
});
var OutlookPreviewSchema = z15.object({
  /** The chosen folders, each with where it is in the mailbox. */
  folders: z15.array(z15.object({ id: z15.string(), name: z15.string(), path: z15.string() })),
  /** Emails in them and in their subfolders, at any depth. */
  emails: z15.number().int().nonnegative(),
  /** Their subfolders, which sync with them. */
  subfolders: z15.number().int().nonnegative()
});
var MySyncedFolderSchema = OutlookFolderLinkSchema.extend({
  projectId: z15.string().uuid(),
  projectName: z15.string()
});
var outlookContract = c11.router({
  // ---- The person's Microsoft account ------------------------------------
  microsoftStatus: {
    method: "GET",
    path: "/connectors/microsoft",
    responses: { 200: MicrosoftSignInStatusSchema, 401: ErrorSchema },
    summary: "Whether the viewer has confirmed their Microsoft account"
  },
  microsoftAuthUrl: {
    method: "GET",
    path: "/connectors/microsoft/auth-url",
    responses: { 200: MicrosoftAuthUrlSchema, 401: ErrorSchema, 503: ErrorSchema },
    summary: "Where to send the browser to confirm the viewer's Microsoft account"
  },
  microsoftCallback: {
    method: "POST",
    path: "/connectors/microsoft/callback",
    body: MicrosoftCallbackInputSchema,
    responses: {
      200: MicrosoftSignInStatusSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      // Another GroundBrain user confirmed this Microsoft account.
      409: ErrorSchema,
      503: ErrorSchema
    },
    summary: "Finish confirming the viewer's Microsoft account. Keeps who they are, no token"
  },
  microsoftForget: {
    method: "DELETE",
    path: "/connectors/microsoft",
    body: c11.noBody(),
    responses: { 204: c11.noBody(), 401: ErrorSchema },
    summary: "Forget the viewer's Microsoft account. Their folders stop syncing; the emails stay"
  },
  mailFolders: {
    method: "GET",
    path: "/connectors/microsoft/mail-folders",
    responses: { 200: MailFoldersSchema, 401: ErrorSchema, 409: ErrorSchema, 503: ErrorSchema },
    summary: "The folders of the viewer's own mailbox, for the picker"
  },
  mySyncedFolders: {
    method: "GET",
    path: "/connectors/microsoft/synced-folders",
    responses: { 200: z15.object({ folders: z15.array(MySyncedFolderSchema) }), 401: ErrorSchema },
    summary: "Every folder of the viewer's mailbox that syncs into a project"
  },
  // ---- A project's folders ------------------------------------------------
  status: {
    method: "GET",
    path: "/projects/:id/outlook",
    responses: { 200: OutlookStatusSchema, 401: ErrorSchema, 404: ErrorSchema },
    summary: "The Outlook folders that sync into the project, and how each stands"
  },
  preview: {
    method: "POST",
    path: "/projects/:id/outlook/preview",
    body: OutlookChooseInputSchema,
    responses: {
      200: OutlookPreviewSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      // Not confirmed, or a folder that syncs into another project.
      409: ErrorSchema,
      503: ErrorSchema
    },
    summary: "Check the chosen folders and count their emails. Syncs nothing"
  },
  choose: {
    method: "PUT",
    path: "/projects/:id/outlook",
    body: OutlookChooseInputSchema,
    responses: {
      200: OutlookStatusSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema,
      503: ErrorSchema
    },
    summary: "Sync these folders of the viewer's mailbox into the project, beside any it has"
  },
  stop: {
    method: "DELETE",
    path: "/projects/:id/outlook/:linkId",
    body: c11.noBody(),
    responses: { 204: c11.noBody(), 401: ErrorSchema, 404: ErrorSchema },
    summary: "Stop syncing one folder. Its emails stay"
  },
  syncNow: {
    method: "POST",
    path: "/projects/:id/outlook/sync",
    body: z15.object({}),
    responses: { 202: OutlookStatusSchema, 401: ErrorSchema, 404: ErrorSchema, 409: ErrorSchema },
    summary: "Look for new email now, rather than at the next minute"
  }
});

// src/paths.ts
import { z as z16 } from "zod";
var PresignedUrlSchema = z16.object({ url: z16.string().url() });
function asJson(path) {
  return `${path}?as=json`;
}
var OFF_CONTRACT_ROUTES = {
  ask: "projects/:id/chat/threads/:threadId/ask",
  documentDownload: "projects/:id/documents/:docId/download",
  documentPreview: "projects/:id/documents/:docId/preview",
  documentAssetContent: "projects/:id/documents/:docId/assets/:assetId/content",
  // A page's stored render, for the region a look read (2026-09-26, Phase 5).
  documentPagePicture: "projects/:id/documents/:docId/pages/:page/picture",
  crewDocumentDownload: "crew/:personId/documents/:docId/download",
  journalPhotoContent: "projects/:id/journal/:entryId/photos/:photoId/content",
  journalPhotoThumbnail: "projects/:id/journal/:entryId/photos/:photoId/thumbnail"
};
var seg = (value) => encodeURIComponent(value);
function askPath(projectId, threadId) {
  return `/projects/${seg(projectId)}/chat/threads/${seg(threadId)}/ask`;
}
function documentDownloadPath(projectId, documentId) {
  return `/projects/${seg(projectId)}/documents/${seg(documentId)}/download`;
}
function documentPreviewPath(projectId, documentId) {
  return `/projects/${seg(projectId)}/documents/${seg(documentId)}/preview`;
}
function documentAssetContentPath(projectId, documentId, assetId) {
  const document = `/projects/${seg(projectId)}/documents/${seg(documentId)}`;
  return `${document}/assets/${seg(assetId)}/content`;
}
function documentPagePicturePath(projectId, documentId, page) {
  const document = `/projects/${seg(projectId)}/documents/${seg(documentId)}`;
  return `${document}/pages/${seg(String(page))}/picture`;
}
function crewDocumentDownloadPath(personId, documentId) {
  return `/crew/${seg(personId)}/documents/${seg(documentId)}/download`;
}
function journalPhotoContentPath(projectId, entryId, photoId) {
  return `/projects/${seg(projectId)}/journal/${seg(entryId)}/photos/${seg(photoId)}/content`;
}
function journalPhotoThumbnailPath(projectId, entryId, photoId) {
  return `/projects/${seg(projectId)}/journal/${seg(entryId)}/photos/${seg(photoId)}/thumbnail`;
}

// src/ask-stream.ts
function upsertStep(steps, step) {
  const next = steps.filter((existing) => existing.seq !== step.seq);
  next.push(step);
  return next.sort((a, b) => a.seq - b.seq);
}
function appendText(texts, turn, text) {
  const last = texts[texts.length - 1];
  if (last && last.turn === turn) return [...texts.slice(0, -1), { turn, text: last.text + text }];
  return [...texts, { turn, text }];
}
function streamedAnswer(texts, steps) {
  const noted = new Set(
    steps.filter((step) => step.kind === "note" && step.note?.form === "text").map((step) => step.turn)
  );
  return texts.filter((run) => run.turn === null || !noted.has(run.turn)).map((run) => run.text).join("");
}
function shownSteps(steps) {
  return steps.filter((step) => step.kind !== "turn" && !(step.kind === "note" && step.note?.shown === false));
}
async function* readAskEvents(body) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    for (; ; ) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const parsed = parseAskFrame(frame);
        if (parsed) yield parsed;
        boundary = buffer.indexOf("\n\n");
      }
    }
    buffer += decoder.decode();
    const last = parseAskFrame(buffer);
    if (last) yield last;
  } finally {
    reader.releaseLock();
  }
}
function parseAskFrame(frame) {
  const line = frame.split("\n").find((candidate) => candidate.startsWith("data:"));
  if (!line) return null;
  try {
    const parsed = AskEventSchema.safeParse(JSON.parse(line.slice(5).trim()));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

// src/markdown.ts
var FENCE = /^\s{0,3}(```+|~~~+)\s*([^\s`]*)\s*$/;
var ATX = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/;
var RULE = /^\s{0,3}(?:-{3,}|\*{3,}|_{3,})\s*$/;
var QUOTE = /^\s{0,3}>\s?/;
var LIST_ITEM = /^(\s*)(?:([-*+])|(\d{1,9})[.)])\s+(.*)$/;
var TASK = /^\[([ xX])\]\s+(.*)$/;
var MARKER = /^\[\[([a-z][a-z0-9_-]*):([^\]\s]*)\]\]/i;
var MAX_INLINE_SPAN = 2e3;
function parseMarkdown(source) {
  return parseBlocks(source.replace(/\r\n?/g, "\n").split("\n"));
}
function parseBlocks(lines) {
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (!line.trim()) {
      i++;
      continue;
    }
    const fence = FENCE.exec(line);
    if (fence) {
      const marker = fence[1] ?? "```";
      const body = [];
      i++;
      while (i < lines.length && !isClosingFence(lines[i] ?? "", marker)) {
        body.push(lines[i] ?? "");
        i++;
      }
      if (i < lines.length) i++;
      blocks.push({ type: "code", lang: fence[2] || null, text: body.join("\n") });
      continue;
    }
    if (RULE.test(line)) {
      blocks.push({ type: "rule" });
      i++;
      continue;
    }
    const heading = ATX.exec(line);
    if (heading) {
      blocks.push({
        type: "heading",
        level: (heading[1] ?? "#").length,
        spans: parseSpans(heading[2] ?? "")
      });
      i++;
      continue;
    }
    if (QUOTE.test(line)) {
      const body = [];
      while (i < lines.length && QUOTE.test(lines[i] ?? "")) {
        body.push((lines[i] ?? "").replace(QUOTE, ""));
        i++;
      }
      blocks.push({ type: "quote", blocks: parseBlocks(body) });
      continue;
    }
    if (line.includes("|") && isDelimiterRow(lines[i + 1] ?? "")) {
      const table = parseTable(lines, i);
      if (table) {
        blocks.push(table.block);
        i = table.end;
        continue;
      }
    }
    if (LIST_ITEM.test(line)) {
      const list = parseList(lines, i);
      blocks.push(list.block);
      i = list.end;
      continue;
    }
    const prose = [];
    while (i < lines.length && (lines[i] ?? "").trim() && !startsBlock(lines, i)) {
      prose.push(lines[i] ?? "");
      i++;
    }
    if (prose.length === 0) {
      prose.push(line);
      i++;
    }
    blocks.push({ type: "para", spans: parseSpans(prose.join("\n")) });
  }
  return blocks;
}
function startsBlock(lines, at) {
  const line = lines[at] ?? "";
  return FENCE.test(line) || RULE.test(line) || ATX.test(line) || QUOTE.test(line) || LIST_ITEM.test(line) || line.includes("|") && isDelimiterRow(lines[at + 1] ?? "");
}
function isClosingFence(line, opener) {
  const char = opener[0] ?? "`";
  const match = /^\s{0,3}(```+|~~~+)\s*$/.exec(line);
  return !!match && (match[1] ?? "").startsWith(char) && (match[1] ?? "").length >= opener.length;
}
function parseList(lines, start) {
  const first = LIST_ITEM.exec(lines[start] ?? "");
  const ordered = first?.[3] !== void 0;
  const items = [];
  let i = start;
  while (i < lines.length) {
    const match = LIST_ITEM.exec(lines[i] ?? "");
    if (!match) break;
    if (match[3] !== void 0 !== ordered) break;
    const content = match[4] ?? "";
    const contentColumn = (match[0] ?? "").length - content.length;
    const body = [content];
    i++;
    while (i < lines.length) {
      const next = lines[i] ?? "";
      if (!next.trim()) {
        const after = lines[i + 1] ?? "";
        if (after.trim() && leadingSpaces(after) >= contentColumn) {
          body.push("");
          i++;
          continue;
        }
        break;
      }
      if (leadingSpaces(next) >= contentColumn) {
        body.push(next.slice(contentColumn));
        i++;
        continue;
      }
      if (LIST_ITEM.test(next) || startsBlock(lines, i)) break;
      body.push(next.trim());
      i++;
    }
    let checked = null;
    const task = TASK.exec(body[0] ?? "");
    if (task) {
      checked = (task[1] ?? "").toLowerCase() === "x";
      body[0] = task[2] ?? "";
    }
    items.push({
      marker: ordered ? `${match[3]}.` : "\u2014",
      checked,
      blocks: parseBlocks(body)
    });
  }
  return { block: { type: "list", ordered, items }, end: i };
}
function leadingSpaces(line) {
  return (/^\s*/.exec(line)?.[0] ?? "").length;
}
function isDelimiterRow(line) {
  if (!line.includes("|") || !line.includes("-")) return false;
  const cells = splitRow(line);
  return cells.length > 0 && cells.every((cell) => /^:?-+:?$/.test(cell.trim()));
}
function parseTable(lines, start) {
  const head = splitRow(lines[start] ?? "").map((cell) => cell.trim());
  const align = splitRow(lines[start + 1] ?? "").map((cell) => {
    const text = cell.trim();
    const left = text.startsWith(":");
    const right = text.endsWith(":");
    if (left && right) return "center";
    if (right) return "right";
    if (left) return "left";
    return null;
  });
  if (align.length !== head.length || head.length === 0) return null;
  const rows = [];
  let i = start + 2;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (!line.trim() || !line.includes("|")) break;
    const cells = splitRow(line);
    rows.push(head.map((_, column) => parseSpans((cells[column] ?? "").trim())));
    i++;
  }
  return {
    block: { type: "table", head: head.map((cell) => parseSpans(cell)), rows, align },
    end: i
  };
}
function splitRow(line) {
  let text = line.trim();
  if (text.startsWith("|")) text = text.slice(1);
  if (text.endsWith("|") && !text.endsWith("\\|")) text = text.slice(0, -1);
  const cells = [];
  let cell = "";
  for (let i = 0; i < text.length; i++) {
    if (text[i] === "\\" && text[i + 1] === "|") {
      cell += "|";
      i++;
      continue;
    }
    if (text[i] === "|") {
      cells.push(cell);
      cell = "";
      continue;
    }
    cell += text[i];
  }
  cells.push(cell);
  return cells;
}
var ESCAPABLE = /[\\`*_{}[\]()#+\-.!|~>]/;
function parseSpans(source) {
  const spans = [];
  let text = "";
  let i = 0;
  const flush = () => {
    if (text) spans.push({ type: "text", text });
    text = "";
  };
  while (i < source.length) {
    const char = source[i] ?? "";
    if (char === "\\" && ESCAPABLE.test(source[i + 1] ?? "")) {
      text += source[i + 1];
      i += 2;
      continue;
    }
    if (char === "\n") {
      flush();
      spans.push({ type: "break" });
      i++;
      continue;
    }
    if (char === "`") {
      const run = runLength(source, i, "`");
      const close = findRun(source, i + run, "`", run);
      if (close !== -1) {
        flush();
        spans.push({ type: "code", text: trimCode(source.slice(i + run, close)) });
        i = close + run;
        continue;
      }
    }
    if (char === "[") {
      const marker = MARKER.exec(source.slice(i));
      if (marker) {
        flush();
        spans.push({ type: "marker", name: marker[1] ?? "", arg: marker[2] ?? "" });
        i += (marker[0] ?? "").length;
        continue;
      }
      const link = matchLink(source, i);
      if (link) {
        flush();
        spans.push({ type: "link", href: link.href, spans: parseSpans(link.text) });
        i = link.end;
        continue;
      }
    }
    if (char === "~" && source[i + 1] === "~") {
      const close = source.indexOf("~~", i + 2);
      if (close !== -1 && close - i <= MAX_INLINE_SPAN) {
        flush();
        spans.push({ type: "del", spans: parseSpans(source.slice(i + 2, close)) });
        i = close + 2;
        continue;
      }
    }
    if (char === "*" || char === "_") {
      const emphasis = matchEmphasis(source, i);
      if (emphasis) {
        flush();
        spans.push(emphasis.span);
        i = emphasis.end;
        continue;
      }
    }
    text += char;
    i++;
  }
  flush();
  return spans;
}
function runLength(source, at, char) {
  let n = 0;
  while (source[at + n] === char) n++;
  return n;
}
function findRun(source, from, char, length) {
  for (let i = from; i < source.length && i - from <= MAX_INLINE_SPAN; i++) {
    if (source[i] !== char) continue;
    const run = runLength(source, i, char);
    if (run === length) return i;
    i += run - 1;
  }
  return -1;
}
function trimCode(text) {
  return text.startsWith(" ") && text.endsWith(" ") && text.trim() ? text.slice(1, -1) : text;
}
function matchEmphasis(source, start) {
  const char = source[start] ?? "";
  const run = Math.min(runLength(source, start, char), 2);
  const openEnd = start + run;
  if (openEnd >= source.length) return null;
  if (/\s/.test(source[openEnd] ?? "")) return null;
  if (char === "_" && !isBoundary(source[start - 1])) return null;
  for (let i = openEnd; i < source.length && i - start <= MAX_INLINE_SPAN; i++) {
    if (source[i] === "\\") {
      i++;
      continue;
    }
    if (source[i] !== char) continue;
    if (/\s/.test(source[i - 1] ?? "")) continue;
    const closeRun = runLength(source, i, char);
    if (closeRun < run) {
      i += closeRun - 1;
      continue;
    }
    if (char === "_" && !isBoundary(source[i + run])) {
      i += closeRun - 1;
      continue;
    }
    const inner = source.slice(openEnd, i);
    if (!inner) return null;
    return {
      span: { type: run === 2 ? "strong" : "em", spans: parseSpans(inner) },
      end: i + run
    };
  }
  return null;
}
function isBoundary(char) {
  return char === void 0 || !/[\p{L}\p{N}]/u.test(char);
}
function matchLink(source, start) {
  let depth = 0;
  let i = start;
  for (; i < source.length && i - start <= MAX_INLINE_SPAN; i++) {
    if (source[i] === "\\") {
      i++;
      continue;
    }
    if (source[i] === "[") depth++;
    else if (source[i] === "]") {
      depth--;
      if (depth === 0) break;
    }
  }
  if (depth !== 0 || i >= source.length || source[i + 1] !== "(") return null;
  const text = source.slice(start + 1, i);
  let parens = 0;
  let j = i + 1;
  for (; j < source.length && j - i <= MAX_INLINE_SPAN; j++) {
    if (source[j] === "\\") {
      j++;
      continue;
    }
    if (source[j] === "(") parens++;
    else if (source[j] === ")") {
      parens--;
      if (parens === 0) break;
    }
  }
  if (parens !== 0 || j >= source.length) return null;
  return { text, href: source.slice(i + 2, j).trim(), end: j + 1 };
}
var LINKABLE = /* @__PURE__ */ new Set(["http:", "https:", "mailto:"]);
function safeHref(raw) {
  const href = raw.trim();
  if (!/^[a-z][a-z0-9+.-]*:/i.test(href)) return null;
  try {
    return LINKABLE.has(new URL(href).protocol) ? href : null;
  } catch {
    return null;
  }
}
function spansToText(spans) {
  return spans.map((span) => {
    switch (span.type) {
      case "text":
        return span.text;
      case "code":
        return span.text;
      case "break":
        return "\n";
      case "marker":
        return "";
      default:
        return spansToText(span.spans);
    }
  }).join("");
}

// src/llm.ts
import { z as z17 } from "zod";
var LlmTouchpointSlugSchema = z17.enum([
  "chat.answer",
  // The answering agent's look at a region of a page, a picture or a journal
  // photo, with the question (Phase 5, 2026-09-26, `document-ai/0026` §12). A
  // vision call, so Claude Sonnet 5.5 (`architecture/0033` §6).
  "chat.look",
  // `chat.select-sources`, the old path's selector, went with it (2026-09-27,
  // Phase 5 slice X): the agent chooses what to read with its tools. Its run
  // rows stay readable, because a run's touchpoint is plain text.
  "chat.review-answer",
  // The question's language, named once before the agent's first turn, so
  // code can write the reply language by name (2026-09-27,
  // `document-ai/0021` §13.6). A model reads it; no code does
  // (`architecture/0032`).
  "chat.question-language",
  "chat.draft-decision",
  "documents.card",
  // The pages read whole from their image (2026-09-26, `document-ai/0020` §12):
  // a drawing or a figure page read with its text-layer words, the model gate
  // that picks the figure pages, and the transcription of a page with no
  // usable text layer, by its one reader (`architecture/0033`).
  "documents.read-page",
  "documents.page-gate",
  "documents.transcribe-page",
  "projects.brief",
  // `projects.outline` and `projects.place-areas`, the outline's two calls,
  // went on 2026-10-03 (`document-ai/0032`) and left the vocabulary on
  // 2026-10-18 (drift D-10): no customer's rows carried them (product/0001).
  "projects.families",
  "projects.project-check",
  // Which party names are one organisation, and which is this company, for
  // the check's guard (2026-09-26, LANG-04, `architecture/0032`).
  "projects.party-groups",
  "projects.links",
  // Which files the families and the links steps compare each new file with:
  // a model reads a catalogue of every visible file (2026-09-26, LANG-01,
  // `architecture/0032`). It replaced a choice by words of one language.
  "projects.related-files",
  "documents.plan.identify-structure",
  "documents.plan.read-basics",
  "documents.plan.parse-phases",
  "documents.plan.read-outline",
  "vision.transcribe-page",
  "vision.describe-image",
  "journals.transcribe-dictation",
  "journals.extract-note",
  "journals.clarify-dictation",
  "crew.extract-certificate"
]);
var LlmProviderSchema = z17.enum(["anthropic", "gemini", "elevenlabs"]);
var LlmEffortSchema = z17.enum(["low", "medium", "high", "xhigh", "max"]);
var LlmRunStatusSchema = z17.enum([
  "ok",
  "refusal",
  "max_tokens",
  "schema_miss",
  "error",
  "skipped"
]);
var LLM_MAX_TOKENS_CEILING = 128e3;
var LlmParamsSchema = z17.object({
  model: z17.string().min(1).max(200).optional(),
  maxTokens: z17.number().int().min(1).max(LLM_MAX_TOKENS_CEILING).optional(),
  effort: LlmEffortSchema.optional(),
  temperature: z17.number().min(0).max(1).optional()
}).strict();

// src/index.ts
var c12 = initContract12();
var contract = c12.router(
  {
    auth: authContract,
    health: healthContract,
    projects: projectsContract,
    journals: journalsContract,
    brain: brainContract,
    chat: chatContract,
    decisions: decisionsContract,
    crew: crewContract,
    jurisdictions: jurisdictionsContract,
    compliance: complianceContract,
    connectors: connectorsContract,
    // A project's SharePoint folder and its sync (`plans/sharepoint-sync.md`).
    sharepoint: sharepointContract,
    // The Outlook folders people choose for a project, and their sync
    // (`plans/outlook-sync.md`).
    outlook: outlookContract,
    debug: debugContract
  },
  {
    strictStatusCodes: true
  }
);
export {
  AGENT_TOOL_NAMES,
  ALLOWED_UPLOAD_EXTENSIONS,
  ALLOWED_UPLOAD_TYPES,
  ANSWERING_FAMILY_ROLES,
  ANSWER_CONFIDENCE_LABELS,
  ANSWER_CONFIDENCE_TONES,
  AddCrewMemberInputSchema,
  AdminUserStatusSchema,
  AgentToolNameSchema,
  AnswerClarificationSchema,
  AnswerConfidenceSchema,
  AnswerCoverageSchema,
  AnswerEndedBySchema,
  AnswerNoteSchema,
  AnswerStepKindSchema,
  AnswerStepOutcomeSchema,
  AnswerStepSchema,
  AnswerTrailCountsSchema,
  AnswerTrailSchema,
  AnswerUnfinishedSchema,
  AskEventSchema,
  AskEventSchemaAsInstalled,
  AskInputSchema,
  AskStageSchema,
  AssetRoleSchema,
  AuthResponseSchema,
  AuthTokensSchema,
  BBoxSchema,
  BrainCategoryShelfSchema,
  BrainDocumentItemSchema,
  BrainEntryItemSchema,
  BrainItemSchema,
  BrainPhotoRefSchema,
  BriefPartySchema,
  BriefSupplierSchema,
  CHAT_SOURCE_LABELS,
  CITE_MARKER_PATTERN,
  COMPLIANCE_FIELD_TYPE_LABELS,
  COMPLIANCE_STATUS_LABELS,
  COMPLIANCE_STATUS_LABELS_STANDALONE,
  CREW_ROLE_LABELS,
  CardPartySchema,
  CardSignatureSchema,
  CertificateSuggestionSchema,
  ChangesResponseSchema,
  ChatMessageSchema,
  ChatRoleSchema,
  ChatSourceKindSchema,
  ChatSourceLinkSchema,
  ChatSourceSchema,
  ChatThreadInputSchema,
  ChatThreadSchema,
  ChatThreadSummarySchema,
  CitationLocatorSchema,
  ClarificationAnswerSchema,
  ClockTimeSchema,
  CodeExecutionDetailSchema,
  CompletePhotoSchema,
  ComplianceFieldTypeSchema,
  ComplianceRequirementSchema,
  ComplianceStatusSchema,
  ConfirmCertificateInputSchema,
  ConnectorAuthUrlSchema,
  ConnectorCallbackInputSchema,
  ConnectorStatusSchema,
  ContainedFileSchema,
  ContainmentSchema,
  ContextDetailSchema,
  CountryCodeSchema,
  CrewBreakdownSchema,
  CrewCertificateResponseSchema,
  CrewCertificateSchema,
  CrewCountOnDateSchema,
  CrewDetailSchema,
  CrewDocStatusSchema,
  CrewMemberResponseSchema,
  CrewMemberSchema,
  CrewPeriodSchema,
  CrewRoleSchema,
  CrewRosterSchema,
  CrewUploadUrlInputSchema,
  CrewUploadUrlResponseSchema,
  DECISION_SOURCE_LABELS,
  DECISION_STATE_LABELS,
  DEFAULT_DOCUMENT_CATEGORIES,
  DELIVERY_CONDITION_LABELS,
  DICTATION_AUDIO_TYPES,
  DICTATION_TOO_LONG,
  DOCUMENT_CATEGORY_SHAPE,
  DOCUMENT_SOURCE_LABELS,
  DOC_TYPE_LABELS,
  DebugChangeSchema,
  DebugDocumentSchema,
  DebugImportSchema,
  DebugReferenceSchema,
  DebugRunSchema,
  DebugRunSummarySchema,
  DebugStepSchema,
  DebugThreadSchema,
  DecisionElevateInputSchema,
  DecisionSchema,
  DecisionSourceSchema,
  DecisionStateSchema,
  DeliveryConditionSchema,
  DictationClarificationSchema,
  DictationStorageStatusSchema,
  DictationUploadInputSchema,
  DictationUploadResponseSchema,
  DocTypeSchema,
  DocumentAssetSchema,
  DocumentAssetsResponseSchema,
  DocumentCardStateSchema,
  DocumentCategoryKeySchema,
  DocumentCategoryRefSchema,
  DocumentCategorySchema,
  DocumentChunkSchema,
  DocumentFamilySchema,
  DocumentLinkSchema,
  DocumentMainCategorySchema,
  DocumentQuestionSchema,
  DocumentRefSchema,
  DocumentResponseSchema,
  DocumentSegmentSchema,
  DocumentSegmentsResponseSchema,
  DocumentSourceSchema,
  DocumentStatusDoubtSchema,
  DocumentStatusQuestionSchema,
  DocumentStatusSchema,
  DocumentStorageStatusSchema,
  DocumentsResponseSchema,
  DriveImportFailureSchema,
  DriveImportInputSchema,
  DriveImportResponseSchema,
  EXTRACTED_DELIVERY_CONDITION_LABELS,
  EXTRACTED_DELIVERY_CONDITION_LABELS_STANDALONE,
  EXTRACTED_INCIDENT_SEVERITY_LABELS,
  EXTRACTED_INCIDENT_SEVERITY_LABELS_CAPTURE,
  ErrorSchema,
  ExtractedDeliveryConditionSchema,
  ExtractedDeliverySchema,
  ExtractedIncidentSchema,
  ExtractedIncidentSeveritySchema,
  FAMILY_ROLE_LABELS,
  FILE_STATUS_COUNT_WORDS,
  FILE_STATUS_LABELS,
  FILE_STATUS_PHRASES,
  FamilyMemberSchema,
  FamilyRoleSchema,
  FileStatusSchema,
  FindingsDetailSchema,
  HEIC_REJECTION,
  HEIC_TYPES,
  HIDDEN_FILE_STATUSES,
  HOW_TO_READ_LABELS,
  HiddenFileStatusSchema,
  HowToReadSchema,
  IDENTITY_VERSION_RULE,
  IMAGE_SUBJECT_LABELS,
  IMG_MARKER_PATTERN,
  IMPORT_STATUS_LABELS,
  INCIDENT_SEVERITY_LABELS,
  INCIDENT_SEVERITY_LABELS_CAPTURE,
  INLINE_VIEWABLE_TYPES,
  INSPECTION_RESULT_LABELS,
  IdentityKindSchema,
  ImageSubjectSchema,
  ImportResponseSchema,
  ImportStatusSchema,
  ImportsResponseSchema,
  IncidentSeveritySchema,
  InspectionResultSchema,
  IsoDateSchema,
  JobStatusSchema,
  JournalApproveSchema,
  JournalDaySummarySchema,
  JournalDaysResponseSchema,
  JournalDeliverySchema,
  JournalDictationResponseSchema,
  JournalDictationSchema,
  JournalEntriesResponseSchema,
  JournalEntryInputSchema,
  JournalEntryPatchSchema,
  JournalEntryResponseSchema,
  JournalEntrySchema,
  JournalIncidentSchema,
  JournalInspectionSchema,
  JournalNoteCreatedSchema,
  JournalNoteDetailSchema,
  JournalNoteFieldsSchema,
  JournalNoteInputSchema,
  JournalNoteKindSchema,
  JournalNoteSchema,
  JournalNoteUpdateSchema,
  JournalNoteUpdatedSchema,
  JournalNotesResponseSchema,
  JournalPhotoResponseSchema,
  JournalPhotoSchema,
  JournalPhotosResponseSchema,
  JournalVisitorSchema,
  JurisdictionSchema,
  KNOWLEDGE_SOURCE_LABELS,
  KeptLooksDetailSchema,
  KnowledgeEntrySchema,
  KnowledgeNoteInputSchema,
  KnowledgeSourceTypeSchema,
  KnowledgeSummarySchema,
  LINKING_REFERENCE_KINDS,
  LINK_FOUND_BY_LABELS,
  LINK_KIND_LABELS,
  LINK_KIND_PHRASES,
  LINK_STATE_LABELS,
  LLM_MAX_TOKENS_CEILING,
  LinkEvidenceSchema,
  LinkFoundBySchema,
  LinkKindSchema,
  LinkResponseSchema,
  LinkStateSchema,
  LinkedFileSchema,
  LinksDetailSchema,
  LinksResponseSchema,
  ListFilesDetailSchema,
  LlmEffortSchema,
  LlmParamsSchema,
  LlmProviderSchema,
  LlmRunImageSchema,
  LlmRunPictureSchema,
  LlmRunStatusSchema,
  LlmTouchpointSlugSchema,
  LoginInputSchema,
  LookDetailSchema,
  LookTargetKindSchema,
  LookTargetSchema,
  MADE_BY_LABELS,
  MAX_CAPTION_CHARS,
  MAX_DECISIONS,
  MAX_DICTATION_BYTES,
  MAX_DICTATION_CLARIFICATIONS,
  MAX_DICTATION_SECONDS,
  MAX_DOCUMENT_CATEGORIES,
  MAX_DRIVE_PICK,
  MAX_FILENAME_LENGTH,
  MAX_SOURCE_PATH_CHARS,
  MAX_SOURCE_PATH_SEGMENT_CHARS,
  MAX_STATUS_GROUP,
  MAX_UPLOAD_BYTES,
  METADATA_SOURCE_LABELS,
  MULTIPART_PART_BYTES,
  MULTIPART_THRESHOLD_BYTES,
  MadeBySchema,
  MailFolderSchema,
  MailFoldersSchema,
  MeResponseSchema,
  MetadataDetailSchema,
  MetadataSourceSchema,
  MicrosoftAccountSchema,
  MicrosoftAuthUrlSchema,
  MicrosoftCallbackInputSchema,
  MicrosoftSignInStatusSchema,
  ModelPassStatusSchema,
  MySyncedFolderSchema,
  NOTE_KIND_LABELS,
  NUMBER_REFERENCE_KINDS,
  NoteExtractionInputSchema,
  NoteExtractionStatusSchema,
  NoteExtractionSuggestionSchema,
  OFF_CONTRACT_ROUTES,
  ORGANISE_GAP_LABELS,
  OUTLOOK_REFUSALS,
  OpenFileDetailSchema,
  OpenImportInputSchema,
  OrganiseGapSchema,
  OutlookChooseInputSchema,
  OutlookFolderLinkSchema,
  OutlookHealthSchema,
  OutlookNotTakenSchema,
  OutlookPreviewSchema,
  OutlookStatusSchema,
  PAGE_READING_KIND_LABELS,
  PAGE_READING_STATUS_LABELS,
  PARSED_METADATA_SOURCES,
  PARTY_ROLES,
  PARTY_ROLE_DESCRIPTIONS,
  PARTY_ROLE_LABELS,
  PLACEMENT_RELATION_LABELS,
  PROCESSING_JOB_KIND_LABELS,
  PROCESSING_STEP_LABELS,
  PROCESSING_STEP_OUTCOME_LABELS,
  PROJECT_ALIAS_KIND_LABELS,
  PROJECT_CHANGE_KIND_LABELS,
  PROJECT_ORGANISE_STATE_LABELS,
  PROJECT_STATUS_LABELS,
  PageQuestionSchema,
  PageReadingKindSchema,
  PageReadingStatusSchema,
  ParsedPhaseSchema,
  PartyRoleSchema,
  PersonStatusSchema,
  PhaseInputSchema,
  PhaseLevelSchema,
  PhaseOriginSchema,
  PhaseSchema,
  PhasesInputSchema,
  PhasesResponseSchema,
  PhotoDescriptionStatusSchema,
  PhotoKindSchema,
  PhotoMultipartUrlResponseSchema,
  PhotoPatchSchema,
  PhotoUploadPartsInputSchema,
  PhotoUploadPartsResponseSchema,
  PhotoUploadUrlInputSchema,
  PhotoUploadUrlResponseSchema,
  PlaceDocumentInputSchema,
  PlacementQuestionSchema,
  PlacementRelationSchema,
  PlanParseInputSchema,
  PlanParseJobSchema,
  PlanVsActualSchema,
  PresignedUrlSchema,
  ProcessingJobKindSchema,
  ProcessingStepOutcomeSchema,
  ProcessingStepSchema,
  ProjectAliasKindSchema,
  ProjectBasicsPatchSchema,
  ProjectBasicsSchema,
  ProjectBriefResponseSchema,
  ProjectBriefSchema,
  ProjectChangeKindSchema,
  ProjectChangeSchema,
  ProjectDocumentSchema,
  ProjectImportSchema,
  ProjectOrganiseResponseSchema,
  ProjectOrganiseSchema,
  ProjectOrganiseStateSchema,
  ProjectResponseSchema,
  ProjectSchema,
  ProjectStatusSchema,
  ProjectSuggestionSchema,
  ProjectsResponseSchema,
  QuestionFactsSchema,
  QuestionMemberSchema,
  QuestionSummarySchema,
  QuestionTopicSchema,
  READING_ITEM_KIND_LABELS,
  READING_SOURCE_LABELS,
  READ_LINK_KINDS,
  REFERENCE_KIND_LABELS,
  ReadingItemKindSchema,
  ReadingSourceSchema,
  ReferenceKindSchema,
  RefreshInputSchema,
  RegisterInputSchema,
  RelationToFileSchema,
  RequirementInputSchema,
  RequirementsInputSchema,
  RequirementsResponseSchema,
  RowsForCodeDetailSchema,
  SEGMENT_TEXT_SOURCE_LABELS,
  SETUP_STEP_COUNT,
  SHAREPOINT_REFUSALS,
  STATUS_DOUBT_LABELS,
  SearchDetailSchema,
  SegmentKindSchema,
  SegmentTextSourceSchema,
  SemanticToneSchema,
  SetDocumentCategoryInputSchema,
  SetDocumentStatusInputSchema,
  SetDocumentStatusesInputSchema,
  SetDocumentStatusesResponseSchema,
  SetLinkStateInputSchema,
  SetupStepSchema,
  SharepointCheckStartSchema,
  SharepointFolderInputSchema,
  SharepointFolderPreviewSchema,
  SharepointHealthSchema,
  SharepointLinkInputSchema,
  SharepointLinkSchema,
  SharepointNotTakenSchema,
  SharepointPreviewInputSchema,
  SharepointStatusSchema,
  SourceBoxSchema,
  StatusDoubtKindSchema,
  StatusDoubtSchema,
  StatusQuestionSchema,
  StepDetailSchema,
  TOPIC_LABELS,
  TokensResponseSchema,
  ToolboxTalkSchema,
  UNDOABLE_CHANGE_KINDS,
  UNFINISHED_UPLOAD_REASON,
  UPLOAD_REFUSALS,
  UndoResponseSchema,
  UploadUrlInputSchema,
  UploadUrlResponseSchema,
  UserSchema,
  VersionsDetailSchema,
  WEATHER_LABELS,
  WEATHER_LABELS_SHORT,
  WeatherSchema,
  appendText,
  asJson,
  asOutline,
  askPath,
  atRecordingCap,
  authContract,
  brainContract,
  canIndent,
  canMove,
  canOutdent,
  capCountdown,
  cardPartiesOf,
  cardPartyText,
  cardSignaturesOf,
  chatContract,
  complianceContract,
  connectorsContract,
  contract,
  countryCodeOf,
  countryCodes,
  countryLabel,
  crewContract,
  crewDocumentDownloadPath,
  crewRoleLabel,
  daysBetween,
  debugContract,
  decisionState,
  decisionsContract,
  describeStep,
  docTypeLabel,
  documentAssetContentPath,
  documentDisplayName,
  documentDownloadPath,
  documentPagePicturePath,
  documentPreviewPath,
  documentUploadRefusal,
  duplicateCategoryKeys,
  durationInWeeks,
  errorMessage,
  extensionOf,
  fileRefusal,
  formatBytes,
  formatCitation,
  formatCiteMarker,
  formatCoverage,
  formatImageMarker,
  formatJournalPhotoCitation,
  formatMissingForSignOff,
  formatUnfinished,
  hasChildren,
  healthContract,
  isAuthCall,
  isCountryCode,
  isDictationSettled,
  isInlineViewable,
  isSystemFile,
  journalPhotoContentPath,
  journalPhotoThumbnailPath,
  journalsContract,
  jurisdictionsContract,
  missingForSignOff,
  moveRow,
  normaliseCiteMarkers,
  normaliseFileTime,
  normaliseSourcePath,
  noteKindRule,
  outlineNumbers,
  outlineRefusal,
  outlookContract,
  outlookFolderTaken,
  outlookLinkedAttachment,
  parseAskFrame,
  parseCiteMarkers,
  parseImageMarkers,
  parseMarkdown,
  parseSpans,
  partRange,
  planUpload,
  progressToHandover,
  projectsContract,
  readAskEvents,
  recordingClock,
  refuse,
  removeRow,
  roleAnswers,
  safeHref,
  sanitiseCaption,
  sharepointContract,
  shiftRow,
  shownSteps,
  spansToText,
  statusHides,
  streamedAnswer,
  stripCiteMarkers,
  stripImageMarkers,
  subPhaseSpan,
  subtreeEnd,
  summariseSteps,
  trailFromSteps,
  trimLineEnds,
  upsertStep,
  worstIncidentSeverity
};
//# sourceMappingURL=index.js.map